import {
  logAcabado,
  logAcabadoApi,
  logAcabadoTexture,
  logGola,
  logGolaApi,
  logGolaItemTexturesSnapshot,
  logGolaLinkedChildrenSnapshot,
  logItemTexturesSnapshot,
  logLinkedChildrenSnapshot,
} from '@context/utils/linked/linkedAcabadoChangeLogger'
import { isLinkedVisualBatchActive } from '@context/utils/linked/runLinkedVisualBatch'
import {
  parseLinkedCatalogParts,
  resolveCatalogIdAfterGolaChange,
  sceneGLBLookupKey,
} from '@context/utils/linked/buildLinkedCatalogId'
import {
  applyIdCambioFromApi,
  isGolaEffective,
  normalizeLinkedCatalogBase,
} from '@context/utils/linked/linkedUnitVariants'
import { syncLinkedItemZonesFromTextures } from '@context/utils/linked/syncLinkedItemZonesFromTextures'
import { invalidateLinkedTextureCaches } from '@context/utils/linked/invalidateLinkedTextureCaches'
import {
  applyLinkedUnitOptionsPatch,
  extractLinkedOptionsBucket,
  fetchLinkedUnitOptions,
  fetchLinkedUnitOptionsRaw,
  mapLinkedUnitOptionsResponse,
} from '@utils/sceneObjects/linked/fetchLinkedUnitOptions'
import {
  reconcileExistingTextureWithUnitAcabado,
} from '@utils/sceneObjects/linked/reconcileTexturesAfterUnitChange'
import {
  collectValidZonesFromUnitAcabados,
  isTextureValidForUnitAcabados,
  rebuildLinkedItemZonesFromUnit,
} from '@utils/sceneObjects/linked/rebuildLinkedItemZonesFromUnit'
import {
  resolveZoneKeyForTarget,
  textureBelongsToZone,
  textureZoneAlreadyProcessed,
} from '@utils/sceneObjects/linked/linkedZoneTextureMapping'
import {
  applyMaterialToTextureEntry,
  applyMaterialToZoneMaterial,
  bumpLinkedItemMaterialEpoch,
  findZocaloCatalogOption,
  getPuertaInheritanceZoneDisplayName,
  isLateralOrCostadoLinkedCatalog,
  isRegletaPuertaLinkedCatalog,
  matchesLateralCostadoForModeEditor,
  pickTextureEntryFields,
  preserveZoneBrightness,
  replaceTextureEntryFromInheritedSource,
  replaceZoneMaterialFromInheritedSource,
  replaceZoneMaterialFromTexture,
  shouldPreserveZoneBrightnessForLinkedTarget,
} from '@utils/sceneObjects/linked/linkedTextureFields'
import { getProxyUrl } from '@proxies/index'
import { useAppStore } from '@store'
import {
  AnyID,
  BackGLBInfo,
  BackLinkedData,
  Getter,
  Item,
  ItemWithOptions,
  LinkedItem,
  LinkedItemID,
  // LinkedItem,
  LinkedItemSon,
  LinkedItemWithMorphs,
  LinkedItemsID,
  Material,
  Options,
  SceneObjects,
  Setter,
  Store,
  TiradorWithPositions,
} from '@types'
import { mapMorphsFromDTO } from '@utils/mapMorphsFromDTO'
import { API } from '@utils/queryBuilder'
import isLinked from '@utils/sceneObjects/assertions/isLinked'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isLinkedSon from '@utils/sceneObjects/assertions/isLinkedSon'
import {
  getExistingTexturesFromScene,
  mapExistingTexturesToNewItemByDisplayName,
  syncZonesWithMappedTextures,
} from '@utils/sceneObjects/getExistingTexturesFromScene'
import getId from '@utils/sceneObjects/getId'
import { getZocaloMaterialFieldsFromOption } from '@utils/sceneObjects/getZocaloMaterialFromOption'
import { adjustChildrenPositionsAfterInheritance } from '@utils/sceneObjects/items/adjustChildrenToEncimera'
import { getGLB } from '@utils/sceneObjects/items/getGLB'
import { getCatalogGlbAabbMm } from '@utils/sceneObjects/items/getGLBSize'
import { newGetDataFromGLB } from '@utils/sceneObjects/items/newGetDataFromGLB'
import { depthVisualOrderSorter } from '@utils/sceneObjects/linked/depthVisualOrderSorter'
import fetchLinkedChildrenInfo from '@utils/sceneObjects/linked/fetchLinkedChildrenInfo'
import {
  applyEncimeraThicknessMm,
  applyLinkedApplianceLocalTransformFromApi,
  freeDimensionUsesLb8060StyleAltoOffset,
  getFreeDimensionEncimeraPositionYMm,
  getFreeDimensionParentLayoutAltoMm,
  getFreeDimensionVisualBodyTopYMm,
  isCountertopApplianceLinkedSon,
  isEncimeraLinkedSonItem,
  isIndependentZocaloLinkedSon,
  isRbFreeDimensionRegleta,
  itemUsesFreeDimensionAltoApiMorph,
  linkedSonKeepsApiLocalTransform,
  linkedSonScalesWithParentFurniture,
  logFreeDimensionAncho,
  placeLinkedEncimeraY,
  placeLinkedIndependentZocalo,
  resolveEncimeraAcabadoAjusteYMm,
  revertIndependentZocaloFloorOffsetIfNeeded,
  sealLinkedApplianceApiFloorCoordsFromParentCatalog,
  syncFreeDimensionEncimeraAnchoToParent,
  syncFreeDimensionEncimeraFondoToParent,
  syncIndependentZocaloToParent,
  usesFreeDimensionScaling,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import {
  findSiblingEncimera,
  isPlacaLinkedSon,
} from '@utils/debug/placaSwapDiagnostic'
import type { GolaUpdatePrefetch } from '@context/utils/linked/batchLinkedGolaChange'
import {
  applyLinkedItemGlbVariantInStore,
  finalizeLinkedItemGlbVariantSideEffects,
} from '@utils/sceneObjects/linked/applyLinkedItemGlbVariant'
import { prefetchLinkedVariantCatalogs } from '@utils/sceneObjects/linked/prefetchLinkedVariantCatalogs'
import {
  rebaseKbrFamilyDimsAfterUnitChange,
  refreshKbrFamilyGlbBases,
  type KbrFamilyRebaseResult,
} from '@utils/sceneObjects/linked/rebaseKbrFamilyDimsAfterUnitChange'
import { loadGlbCached } from '@utils/sceneObjects/glbLoaderCache'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import { runWithConcurrency } from '@utils/runWithConcurrency'
import { Euler, Matrix4 } from 'three'
import { calculateTiradorPosition } from '../utils/calculateTiradorPosition'
import getLinkedChildrenInfo from './getLinkedChildrenInfo'

/** Cajón/drawer index: option identifica el cajón; nodo del API suele ser el tipo de elemento (2). */
function getTiradorDrawerNode(child: LinkedItemSon): number | undefined {
  return child.option ?? child.nodo
}

function resolveTiradorIdElementosEnlazados(
  tiradorOptions: ItemWithOptions[] | undefined,
  child: LinkedItemSon,
  clickedIdElementosEnlazados: number,
): number | null {
  const drawerNode = getTiradorDrawerNode(child)
  if (!tiradorOptions?.length || drawerNode === undefined) {
    return clickedIdElementosEnlazados
  }

  const newTiradorOption = tiradorOptions.find(
    (opt) => opt.idElementosEnlazados === clickedIdElementosEnlazados,
  )
  if (!newTiradorOption?.idUnidadEnlazada) {
    return clickedIdElementosEnlazados
  }

  const matchingOption = tiradorOptions.find(
    (opt) => opt.nodo === drawerNode && opt.idUnidadEnlazada === newTiradorOption.idUnidadEnlazada,
  )
  return matchingOption?.idElementosEnlazados ?? null
}

function resolveTiradorIdUnidadEnlazada(
  tiradorOptions: ItemWithOptions[] | undefined,
  idElementosEnlazados: number,
): number | undefined {
  const id = tiradorOptions?.find((opt) => opt.idElementosEnlazados === idElementosEnlazados)
    ?.idUnidadEnlazada
  return id && id > 0 ? id : undefined
}

function resolveLinkedElementIdAnt(item: {
  linkedID?: number | null
  idElementosEnlazados?: number | null
}): number | undefined {
  const fromLinked = Number(item.linkedID)
  if (Number.isFinite(fromLinked) && fromLinked > 0) return fromLinked
  const fromEl = Number(item.idElementosEnlazados)
  if (Number.isFinite(fromEl) && fromEl > 0) return fromEl
  return undefined
}

function isApplianceLinkedSonType(item: { typeOfSon?: string; description?: string } | null | undefined) {
  const t = `${item?.typeOfSon ?? ''} ${item?.description ?? ''}`.toLowerCase()
  return /fregadero|placa|grifo|tpcgrifo|horno|micro/.test(t)
}

const updateLinkedGlbInFlight = new Map<string, Promise<unknown>>()

function setSceneGlbForCatalog(
  sceneGLB: Map<string | number, { glb: unknown; backGLBInfo: unknown }>,
  codigo: string | number | null | undefined,
  payload: { glb: unknown; backGLBInfo: unknown },
  idUnidades?: number | null,
) {
  if (codigo === undefined || codigo === null || codigo === '') return
  const raw = String(codigo)
  const lookup = sceneGLBLookupKey(raw)
  sceneGLB.set(raw, payload)
  if (lookup && lookup !== raw) sceneGLB.set(lookup, payload)
  if (idUnidades != null) sceneGLB.set(idUnidades, payload)
}

function schedulePlacaAccordingToEncimera(get: Getter, linkedID: string) {
  setTimeout(() => {
    const positionFregaderoGrifoPlaca = get().positionFregaderoGrifoPlacaAccordingToEncimera
    if (positionFregaderoGrifoPlaca) {
      positionFregaderoGrifoPlaca(linkedID)
    }
    get().reseatSmallAppliancesOnLinkedSon(linkedID)
  }, 0)
}

function scheduleAppliancesAccordingToEncimera(get: Getter, encimeraID: string) {
  setTimeout(() => {
    const encimera = get().items.get(encimeraID as AnyID)
    const parentId =
      encimera && 'firstParentID' in encimera ? (encimera as { firstParentID?: string }).firstParentID : undefined
    if (!parentId) return
    const parent = get().items.get(parentId as AnyID)
    const childs = parent && 'childs' in parent ? parent.childs : undefined
    if (!childs) return
    childs.forEach((childID: string) => {
      const child = get().items.get(childID as AnyID)
      if (!child || child.type !== 'linkedSon') return
      if (!isCountertopApplianceLinkedSon(child as never)) return
      get().positionFregaderoGrifoPlacaAccordingToEncimera(childID)
    })
    get().reseatSmallAppliancesOnHost(parentId)
  }, 0)
}

/** Grifo/fregadero usan posicionX/Y/Z; tiradores usan bcPosicion. No ocultar si hay posición válida. */
function resolveLinkedSonVisibility(
  responseData: Record<string, unknown>,
  prefix: 'a' | 'b',
  fallback?: { x?: number; y?: number; z?: number },
  options?: { isGolaUnit?: boolean; isTirador?: boolean },
): boolean {
  if (options?.isGolaUnit && options?.isTirador) return false

  const cx = responseData[`${prefix}cPosicionX`] as number | null | undefined
  const cy = responseData[`${prefix}cPosicionY`] as number | null | undefined
  const cz = responseData[`${prefix}cPosicionZ`] as number | null | undefined
  const x = cx ?? (responseData.posicionX as number | undefined) ?? fallback?.x ?? 0
  const y = cy ?? (responseData.posicionY as number | undefined) ?? fallback?.y ?? 0
  const z = cz ?? (responseData.posicionZ as number | undefined) ?? fallback?.z ?? 0

  return !(x === 0 && y === 0 && z === 0)
}

const buildTexturesFromBackData = (item: LinkedItem, _newUnidadData: any) => {
  const currentTextures = Array.isArray(item.textures) ? item.textures : []
  return {
    textures: currentTextures.map((texture: any) => ({ ...texture })),
  }
}

export type LinkedItemsActions = ReturnType<typeof createLinkedItemsActions>

export const createLinkedItemsActions = (set: Setter, get: Getter) => ({
  editLinkedTexture: (linkedID: LinkedItemsID, zoneName: string, material: Material) => {
    let newTexture
    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinked(item)) return
      const zone = item.zones[zoneName]
      if (!material || !zone) return
      item.zones[zoneName].material = applyMaterialToZoneMaterial(
        zone.material as Record<string, unknown>,
        material as Record<string, unknown>,
      )
      const texture = item.textures.find((el) => el.zoneName === zoneName)
      if (!texture) return
      applyMaterialToTextureEntry(texture as Record<string, unknown>, material as Record<string, unknown>)
      newTexture = { ...texture }
    })
    return newTexture
  },

  editLinkedTexture2: (
    linkedID: LinkedItemsID,
    zoneName: string,
    material: Material,
    modeEditor: 'Single' | 'All' | 'Selected' | 'MBJ' | 'MAL' | 'COL',
    selectedIds?: Set<LinkedItemsID>,
  ) => {
    let newTexture
    const pendingIndependentZocaloUpdates: Array<{
      sonId: AnyID
      complementoName: string
      linkedTextureID: number
      idUnidades?: number
    }> = []
    let puertaComplementoName = ''
    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinked(item)) {
        logAcabado('EDIT_TEXTURE_ABORT', { modeEditor, linkedID, reason: 'not_linked' })
        return
      }
      logAcabado('EDIT_TEXTURE_START', {
        modeEditor,
        linkedID,
        zoneName,
        itemTipo: (item as { tipoNormalizado?: string }).tipoNormalizado,
        catalogID: item.catalogID,
        selectedCount: selectedIds?.size ?? 0,
        materialName: (material as { name?: string })?.name,
        materialUrl: (material as { url?: string })?.url,
        materialColor: (material as { color?: string })?.color,
        materialSearchID: (material as { searchID?: unknown })?.searchID,
      })

      const hasZocalo = (targetItem: any): boolean => {
        if (!isLinked(targetItem)) return false
        return (
          targetItem.textures?.some(
            (t: any) =>
              t.zoneDisplayName === 'Zocalo' ||
              t.zone?.toLowerCase().includes('zocalo') ||
              t.zoneName?.toLowerCase().includes('zocalo'),
          ) || false
        )
      }

      const getZocaloTextures = (targetItem: any): any[] => {
        if (!isLinked(targetItem)) return []
        return (
          targetItem.textures?.filter(
            (t: any) =>
              t.zoneDisplayName === 'Zocalo' ||
              t.zone?.toLowerCase().includes('zocalo') ||
              t.zoneName?.toLowerCase().includes('zocalo'),
          ) || []
        )
      }

      const getItemModel = (targetItem: any): string | null => {
        if (!targetItem || !isLinked(targetItem) || !targetItem.textures) return null
        const textureWithModel = targetItem.textures.find((t: any) => t.modelo && t.modelo !== '')
        return textureWithModel?.modelo || null
      }

      let referenceModel = getItemModel(item)

      const isCompatibleModel = (targetItem: SceneObjects, zoneDisplayName?: string): boolean => {
        const targetModel = getItemModel(targetItem)
        if (!targetModel || !referenceModel) return false

        if (zoneDisplayName === 'Gola') return true

        if (
          zoneDisplayName &&
          isLinked(targetItem) &&
          !targetItem.textures?.some((t) => textureBelongsToZone(t, zoneDisplayName))
        ) {
          return false
        }

        if (
          (zoneDisplayName === 'Puerta' || zoneDisplayName === 'Costado') &&
          referenceModel === 'AcabadoModelo' &&
          targetModel === 'AcabadoModelo'
        ) {
          return true
        }

        const isVitrina = referenceModel.toLowerCase().includes('vitrina')
        if (isVitrina) {
          return targetModel === referenceModel
        }

        if (referenceModel === 'AcabadoModelo') {
          return targetModel === 'AcabadoModelo'
        }

        return targetModel.split('_')[0] === referenceModel.split('_')[0]
      }

      const touchedItemIds = new Set<string>()

      const texturesMatchingZone = (
        targetItem: SceneObjects,
        zoneDisplayName: string | undefined,
      ) => {
        if (!isLinked(targetItem)) return []
        return targetItem.textures.filter((el) => textureBelongsToZone(el, zoneDisplayName))
      }

      const zone = item.zones[zoneName]
      if (!material || !zone) return

      item.zones[zoneName].material = applyMaterialToZoneMaterial(
        zone.material as Record<string, unknown>,
        material as Record<string, unknown>,
      )

      const texture = item.textures.find((el) => el.zoneName === zoneName)
      if (!texture) return

      const changedZoneDisplayName = texture.zoneDisplayName
      referenceModel =
        texture.modelo || referenceModel || String((material as Material).modelo ?? '').trim() || null
      if (!referenceModel) return

      applyMaterialToTextureEntry(texture as Record<string, unknown>, material as Record<string, unknown>)
      newTexture = { ...texture }

      const applyMaterialToZoneByKey = (
        targetItem: SceneObjects,
        targetTexture: { zoneName: string; zoneDisplayName?: string; zone?: string },
        zoneMaterial: Material,
        options?: { includeBrightness?: boolean; replace?: boolean },
      ) => {
        const targetZoneKey = resolveZoneKeyForTarget(
          targetItem.zones as Record<string, { name?: string; material?: { zoneDisplayName?: string; zone?: string } }>,
          targetTexture,
        )
        if (!targetZoneKey || !targetItem.zones[targetZoneKey]?.material) return

        const existing = targetItem.zones[targetZoneKey].material as Record<string, unknown>

        if (options?.replace) {
          replaceTextureEntryFromInheritedSource(
            targetTexture as Record<string, unknown>,
            zoneMaterial as Record<string, unknown>,
          )
          targetItem.zones[targetZoneKey].material = replaceZoneMaterialFromInheritedSource(
            targetTexture as Record<string, unknown>,
            existing,
            zoneMaterial as Record<string, unknown>,
          ) as Material
          return
        }

        const fullMaterial = applyMaterialToZoneMaterial(
          existing,
          zoneMaterial as Record<string, unknown>,
        )

        if (options?.includeBrightness === false) {
          targetItem.zones[targetZoneKey].material = preserveZoneBrightness(
            existing,
            fullMaterial,
          ) as Material
          return
        }

        targetItem.zones[targetZoneKey].material = fullMaterial as Material
      }

      const applyMaterialToLinkedTargets = (
        targetTexture: Record<string, unknown> & { zoneName: string },
        targetItem: SceneObjects,
        zoneMaterial: Material,
        options?: { includeBrightness?: boolean; replace?: boolean },
      ) => {
        if (options?.replace) {
          applyMaterialToZoneByKey(targetItem, targetTexture, zoneMaterial, { replace: true })
          return
        }
        applyMaterialToTextureEntry(targetTexture, zoneMaterial as Record<string, unknown>)
        applyMaterialToZoneByKey(targetItem, targetTexture, zoneMaterial, options)
      }

      const propagatePuertaMaterialToLateralCostados = (
        doorMaterial: Material,
        isChangingFromParent: boolean,
      ) => {
        if (changedZoneDisplayName !== 'Puerta') return

        s.items.forEach((targetItem) => {
          if (!isLinked(targetItem)) return
          if (!matchesLateralCostadoForModeEditor(targetItem, modeEditor)) return
          if (isChangingFromParent && targetItem.type === 'linkedSon') return

          const inheritedZoneDisplayName = getPuertaInheritanceZoneDisplayName(targetItem)
          if (!inheritedZoneDisplayName) return

          targetItem.textures
            .filter(
              (t) =>
                t.zoneDisplayName === inheritedZoneDisplayName && t.modelo === 'AcabadoModelo',
            )
            .forEach((targetTexture) => {
              applyMaterialToLinkedTargets(
                targetTexture as Record<string, unknown> & { zoneName: string },
                targetItem,
                doorMaterial,
                { replace: true },
              )
            })
          touchedItemIds.add(targetItem.id)
        })
      }

      const updateZocaloTexture = (targetItem: SceneObjects) => {
        if (!isLinked(targetItem)) return

        const complementoName = String((material as Material).complemento ?? '').trim()
        if (!complementoName) return

        const applyComplementoToZocaloCarrier = (
          carrier: SceneObjects,
          optionsSource: LinkedItem | null,
        ) => {
          if (!isLinked(carrier)) return false
          const textures =
            getZocaloTextures(carrier).length > 0
              ? getZocaloTextures(carrier)
              : isIndependentZocaloLinkedSon(carrier) && Array.isArray(carrier.textures)
                ? carrier.textures
                : []
          if (textures.length === 0) return false

          const options =
            (optionsSource?.zocaloOptions as Array<Record<string, unknown>> | undefined) ||
            ((carrier as LinkedItem).zocaloOptions as Array<Record<string, unknown>> | undefined)

          let appliedWithCatalog = false

          textures.forEach((zocaloTexture) => {
            const targetZoneKey = resolveZoneKeyForTarget(
              carrier.zones as Record<
                string,
                { name?: string; material?: { zoneDisplayName?: string; zone?: string } }
              >,
              zocaloTexture,
            )
            if (!targetZoneKey || !carrier.zones[targetZoneKey]?.material) {
              // Hijo Zocalo 4000: a veces la zona no se llama Zocalo; aplicar a textures[0] + primera zona.
              if (isIndependentZocaloLinkedSon(carrier)) {
                const matchingTexture =
                  options && options.length > 0
                    ? findZocaloCatalogOption(options, complementoName)
                    : undefined
                if (matchingTexture) {
                  const matchedZocaloMaterial = getZocaloMaterialFieldsFromOption(
                    matchingTexture,
                    complementoName,
                  )
                  applyMaterialToTextureEntry(
                    zocaloTexture as Record<string, unknown>,
                    matchedZocaloMaterial as Record<string, unknown>,
                  )
                  const zoneKeys = Object.keys(carrier.zones || {})
                  if (zoneKeys[0] && carrier.zones[zoneKeys[0]]?.material) {
                    const existingZoneMaterial = carrier.zones[zoneKeys[0]].material as Record<
                      string,
                      unknown
                    >
                    carrier.zones[zoneKeys[0]].material = preserveZoneBrightness(
                      existingZoneMaterial,
                      applyMaterialToZoneMaterial(
                        existingZoneMaterial,
                        matchedZocaloMaterial as Record<string, unknown>,
                      ),
                    ) as Material
                  }
                  appliedWithCatalog = true
                }
              }
              return
            }

            const zoneDisplayName = carrier.zones[targetZoneKey].material?.zoneDisplayName
            if (
              zoneDisplayName &&
              zoneDisplayName !== 'Zocalo' &&
              !isIndependentZocaloLinkedSon(carrier)
            ) {
              return
            }

            const existingZoneMaterial = carrier.zones[targetZoneKey].material as Record<
              string,
              unknown
            >

            if (options && options.length > 0) {
              const matchingTexture = findZocaloCatalogOption(options, complementoName)
              if (matchingTexture) {
                const matchedZocaloMaterial = getZocaloMaterialFieldsFromOption(
                  matchingTexture,
                  complementoName,
                )
                applyMaterialToTextureEntry(
                  zocaloTexture as Record<string, unknown>,
                  matchedZocaloMaterial as Record<string, unknown>,
                )
                carrier.zones[targetZoneKey].material = preserveZoneBrightness(
                  existingZoneMaterial,
                  applyMaterialToZoneMaterial(
                    existingZoneMaterial,
                    matchedZocaloMaterial as Record<string, unknown>,
                  ),
                ) as Material
                appliedWithCatalog = true
                return
              }
            }

            carrier.zones[targetZoneKey].material = preserveZoneBrightness(
              existingZoneMaterial,
              applyMaterialToZoneMaterial(existingZoneMaterial, {
                complemento: complementoName,
                description: material.description,
              }),
            ) as Material
          })

          touchedItemIds.add(carrier.id)
          return appliedWithCatalog
        }

        // 1) Zócalo en el propio ítem (legacy / zócalo standalone del menú).
        if (hasZocalo(targetItem)) {
          applyComplementoToZocaloCarrier(
            targetItem,
            targetItem.type === 'linkedItem' ? (targetItem as LinkedItem) : null,
          )
        }

        // 2) Zócalo hijo independiente (Zocalo 4000) de MBJ/COL.
        if (targetItem.type === 'linkedItem') {
          const parent = targetItem as LinkedItem
          const zocaloSonIds: string[] = []
          let zocaloSonId =
            typeof parent.independentZocaloSonId === 'string' && parent.independentZocaloSonId
              ? parent.independentZocaloSonId
              : null
          if (!zocaloSonId && parent.childs?.size) {
            for (const cid of parent.childs) {
              const child = s.items.get(cid as AnyID)
              if (
                child &&
                isIndependentZocaloLinkedSon(child as any) &&
                !(child as { isIndependentZocaloReturn?: boolean }).isIndependentZocaloReturn
              ) {
                zocaloSonId = String(cid)
                break
              }
            }
          }
          if (zocaloSonId) zocaloSonIds.push(zocaloSonId)
          const returnId =
            typeof parent.independentZocaloReturnSonId === 'string' &&
            parent.independentZocaloReturnSonId
              ? parent.independentZocaloReturnSonId
              : null
          if (returnId && !zocaloSonIds.includes(returnId)) zocaloSonIds.push(returnId)
          else if (parent.childs?.size) {
            for (const cid of parent.childs) {
              const child = s.items.get(cid as AnyID)
              if (
                (child as { isIndependentZocaloReturn?: boolean } | undefined)
                  ?.isIndependentZocaloReturn &&
                !zocaloSonIds.includes(String(cid))
              ) {
                zocaloSonIds.push(String(cid))
              }
            }
          }
          for (const sonId of zocaloSonIds) {
            const zocaloSon = s.items.get(sonId as AnyID)
            if (zocaloSon && isLinked(zocaloSon)) {
              // Intento sync (si hay zocaloOptions); el visual fiable va por API abajo.
              applyComplementoToZocaloCarrier(zocaloSon, parent)
              const sonTex =
                zocaloSon.textures?.find(
                  (t) =>
                    t.zoneDisplayName === 'Zocalo' ||
                    String(t.zone ?? '')
                      .toLowerCase()
                      .includes('zocalo') ||
                    String(t.zoneName ?? '')
                      .toLowerCase()
                      .includes('zocalo'),
                ) || zocaloSon.textures?.[0]
              const linkedTextureID = Number(sonTex?.linkedTextureID ?? 0)
              // Siempre encolar API: sin zocaloOptions el sync no cambia la URL del mesh.
              pendingIndependentZocaloUpdates.push({
                sonId: sonId as AnyID,
                complementoName,
                linkedTextureID: linkedTextureID > 0 ? linkedTextureID : 0,
                idUnidades:
                  typeof (zocaloSon as any).idUnidades === 'number'
                    ? (zocaloSon as any).idUnidades
                    : undefined,
              })
            }
          }
        }
      }

      const syncRelatedZones = (targetItem: SceneObjects, zoneDisplayName: string) => {
        if (!isLinked(targetItem) || !isCompatibleModel(targetItem, zoneDisplayName)) return

        let relatedZoneDisplayName: string | null = null

        if (zoneDisplayName === 'Puerta') {
          relatedZoneDisplayName = 'Costado'
        } else if (zoneDisplayName === 'Costado') {
          relatedZoneDisplayName = 'Puerta'
        }

        if (!relatedZoneDisplayName) return

        const relatedTextures = targetItem.textures.filter(
          (el) => el.zoneDisplayName === relatedZoneDisplayName,
        )

        relatedTextures.forEach((relatedTexture) => {
          if (!relatedTexture) return
          const useReplace = relatedZoneDisplayName === 'Costado'
          applyMaterialToLinkedTargets(
            relatedTexture as Record<string, unknown> & { zoneName: string },
            targetItem,
            material,
            useReplace ? { replace: true } : undefined,
          )
        })
        touchedItemIds.add(targetItem.id)
      }

      touchedItemIds.add(item.id)

      if (modeEditor === 'Single') {
        texturesMatchingZone(item, changedZoneDisplayName).forEach((targetTexture) => {
          if (!targetTexture || targetTexture.zoneName === texture.zoneName) return
          applyMaterialToLinkedTargets(
            targetTexture as Record<string, unknown> & { zoneName: string },
            item,
            material,
          )
        })
        if (
          changedZoneDisplayName === 'Puerta' &&
          !isLateralOrCostadoLinkedCatalog(item)
        ) {
          syncRelatedZones(item, changedZoneDisplayName)
        }
        if (changedZoneDisplayName === 'Puerta') {
          puertaComplementoName = String((material as Material).complemento ?? '').trim()
          updateZocaloTexture(item)
        }
      } else if (modeEditor === 'All') {
        // Determinar si el cambio se está haciendo desde un padre (linkedItem) o desde un hijo (linkedSon)
        const isChangingFromParent = !linkedID.startsWith('linkedSon_')

        s.items.forEach((targetItem) => {
          if (!isLinked(targetItem)) return

          if (isChangingFromParent && targetItem.type === 'linkedSon') {
            return
          }

          // Para la zona "Gola", no verificar compatibilidad de modelo ya que puede tener modelos diferentes
          // (MuebleGola vs AcabadoModelo) pero aún así debe aplicarse a todos
          const isGolaZone = changedZoneDisplayName === 'Gola'
          if (!isGolaZone && !isCompatibleModel(targetItem, changedZoneDisplayName)) return

          const targetTextures = texturesMatchingZone(targetItem, changedZoneDisplayName)

          targetTextures.forEach((targetTexture) => {
            if (!targetTexture) return

            applyMaterialToTextureEntry(
              targetTexture as Record<string, unknown>,
              material as Record<string, unknown>,
            )

            const targetZoneKey = resolveZoneKeyForTarget(
              targetItem.zones as Record<string, { name?: string; material?: { zoneDisplayName?: string; zone?: string } }>,
              targetTexture,
            )

            if (targetZoneKey && targetItem.zones[targetZoneKey]?.material) {
              const existing = targetItem.zones[targetZoneKey].material as Record<string, unknown>
              const zoneMaterial = applyMaterialToZoneMaterial(
                existing,
                material as Record<string, unknown>,
              )

              targetItem.zones[targetZoneKey].material = (
                shouldPreserveZoneBrightnessForLinkedTarget(targetItem, item)
                  ? preserveZoneBrightness(existing, zoneMaterial)
                  : zoneMaterial
              ) as Material
            }
          })

          syncRelatedZones(targetItem, changedZoneDisplayName)
          touchedItemIds.add(targetItem.id)
        })

        if (changedZoneDisplayName === 'Puerta') {
          puertaComplementoName = String((material as Material).complemento ?? '').trim()
          propagatePuertaMaterialToLateralCostados(material, isChangingFromParent)
          s.items.forEach((targetItem) => {
            if (!isLinked(targetItem)) return
            if (isChangingFromParent && targetItem.type === 'linkedSon') {
              return
            }
            updateZocaloTexture(targetItem)
          })
        }
      } else if (modeEditor === 'MBJ' || modeEditor === 'MAL' || modeEditor === 'COL') {
        const targetTipo = modeEditor
        const isChangingFromParent = !linkedID.startsWith('linkedSon_')

        s.items.forEach((targetItem) => {
          if (
            !isLinked(targetItem) ||
            targetItem.tipoNormalizado !== targetTipo ||
            !isCompatibleModel(targetItem, changedZoneDisplayName)
          )
            return

          if (isChangingFromParent && targetItem.type === 'linkedSon') {
            return
          }

          const targetTextures = texturesMatchingZone(targetItem, changedZoneDisplayName)

          targetTextures.forEach((targetTexture) => {
            if (!targetTexture) return

            applyMaterialToTextureEntry(
              targetTexture as Record<string, unknown>,
              material as Record<string, unknown>,
            )

            const targetZoneKey = resolveZoneKeyForTarget(
              targetItem.zones as Record<string, { name?: string; material?: { zoneDisplayName?: string; zone?: string } }>,
              targetTexture,
            )

            if (targetZoneKey && targetItem.zones[targetZoneKey]?.material) {
              const existing = targetItem.zones[targetZoneKey].material as Record<string, unknown>
              const zoneMaterial = applyMaterialToZoneMaterial(
                existing,
                material as Record<string, unknown>,
              )

              targetItem.zones[targetZoneKey].material = (
                shouldPreserveZoneBrightnessForLinkedTarget(targetItem, item)
                  ? preserveZoneBrightness(existing, zoneMaterial)
                  : zoneMaterial
              ) as Material
            }
          })

          syncRelatedZones(targetItem, changedZoneDisplayName)

          if (changedZoneDisplayName === 'Puerta') {
            puertaComplementoName = String((material as Material).complemento ?? '').trim()
            updateZocaloTexture(targetItem)
          }

          touchedItemIds.add(targetItem.id)
        })

        if (changedZoneDisplayName === 'Puerta') {
          puertaComplementoName = String((material as Material).complemento ?? '').trim()
          propagatePuertaMaterialToLateralCostados(material, isChangingFromParent)
        }
      } else if (modeEditor === 'Selected' && selectedIds) {
        // Determinar si el cambio se está haciendo desde un padre (linkedItem) o desde un hijo (linkedSon)
        const isChangingFromParent = !linkedID.startsWith('linkedSon_')

        selectedIds.forEach((selectedId) => {
          const selectedItem = s.items.get(selectedId)
          if (
            !selectedItem ||
            !isLinked(selectedItem) ||
            !isCompatibleModel(selectedItem, changedZoneDisplayName)
          )
            return

          // NO aplicar cambios a linkedSon cuando se cambia desde el padre
          if (isChangingFromParent && selectedItem.type === 'linkedSon') {
            return
          }

          const targetTextures = texturesMatchingZone(selectedItem, changedZoneDisplayName)

          targetTextures.forEach((targetTexture) => {
            if (!targetTexture) return

            applyMaterialToTextureEntry(
              targetTexture as Record<string, unknown>,
              material as Record<string, unknown>,
            )

            const targetZoneKey = resolveZoneKeyForTarget(
              selectedItem.zones as Record<string, { name?: string; material?: { zoneDisplayName?: string; zone?: string } }>,
              targetTexture,
            )

            if (targetZoneKey && selectedItem.zones[targetZoneKey]?.material) {
              const existing = selectedItem.zones[targetZoneKey].material as Record<string, unknown>
              const zoneMaterial = applyMaterialToZoneMaterial(
                existing,
                material as Record<string, unknown>,
              )

              selectedItem.zones[targetZoneKey].material = (
                shouldPreserveZoneBrightnessForLinkedTarget(selectedItem, item)
                  ? preserveZoneBrightness(existing, zoneMaterial)
                  : zoneMaterial
              ) as Material
            }
          })
          syncRelatedZones(selectedItem, changedZoneDisplayName)

          if (changedZoneDisplayName === 'Puerta') {
            puertaComplementoName = String((material as Material).complemento ?? '').trim()
            updateZocaloTexture(selectedItem)
          }

          touchedItemIds.add(selectedId)
        })

        if (changedZoneDisplayName === 'Puerta') {
          puertaComplementoName = String((material as Material).complemento ?? '').trim()
          propagatePuertaMaterialToLateralCostados(material, isChangingFromParent)
        }
      }

      for (const touchedId of touchedItemIds) {
        const touchedItem = s.items.get(touchedId as AnyID)
        if (!touchedItem || !isLinked(touchedItem)) continue
        syncLinkedItemZonesFromTextures(touchedItem, {
          itemId: touchedId,
          catalogID: String(touchedItem.catalogID ?? ''),
          source: `editLinkedTexture2:${modeEditor}`,
        })
        bumpLinkedItemMaterialEpoch(touchedItem)
      }
      logAcabado('EDIT_TEXTURE_TOUCHED', {
        modeEditor,
        linkedID,
        zoneName,
        touched: Array.from(touchedItemIds),
        materialEpoch: (s.items.get(linkedID) as { materialEpoch?: number } | undefined)
          ?.materialEpoch,
        contadorModificaciones: (s.contadorModificaciones || 0) + 1,
      })

      s.shouldUpdateCamera = true
      s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
    })

    // Barrido extra: si la puerta trae complemento, asegurar update API de TODOS los
    // zócalos hijos independientes afectados por el modeEditor (por si faltó encolar).
    if (puertaComplementoName) {
      const storeItems = get().items
      const shouldUpdateParent = (parent: SceneObjects): boolean => {
        if (!isLinked(parent) || parent.type !== 'linkedItem') return false
        if (modeEditor === 'Single') {
          return parent.id === linkedID || parent.id === String(linkedID).split('!')[0]
        }
        if (modeEditor === 'All') return true
        if (modeEditor === 'MBJ' || modeEditor === 'MAL' || modeEditor === 'COL') {
          return parent.tipoNormalizado === modeEditor
        }
        if (modeEditor === 'Selected' && selectedIds) {
          return selectedIds.has(parent.id as LinkedItemsID)
        }
        return false
      }

      storeItems.forEach((candidate) => {
        if (!isLinked(candidate)) return
        if (candidate.type === 'linkedItem' && shouldUpdateParent(candidate)) {
          const parent = candidate as LinkedItem
          const zocaloSonIds: string[] = []
          let zocaloSonId =
            typeof parent.independentZocaloSonId === 'string' && parent.independentZocaloSonId
              ? parent.independentZocaloSonId
              : null
          if (!zocaloSonId && parent.childs) {
            for (const cid of Array.from(parent.childs as Set<string> | string[])) {
              const child = storeItems.get(cid as AnyID)
              if (
                child &&
                isIndependentZocaloLinkedSon(child as any) &&
                !(child as { isIndependentZocaloReturn?: boolean }).isIndependentZocaloReturn
              ) {
                zocaloSonId = String(cid)
                break
              }
            }
          }
          if (zocaloSonId) zocaloSonIds.push(zocaloSonId)
          if (
            typeof parent.independentZocaloReturnSonId === 'string' &&
            parent.independentZocaloReturnSonId &&
            !zocaloSonIds.includes(parent.independentZocaloReturnSonId)
          ) {
            zocaloSonIds.push(parent.independentZocaloReturnSonId)
          }
          for (const sonId of zocaloSonIds) {
            const son = storeItems.get(sonId as AnyID)
            if (!son || !isLinked(son)) continue
            const sonTex =
              son.textures?.find(
                (t) =>
                  t.zoneDisplayName === 'Zocalo' ||
                  String(t.zone ?? '')
                    .toLowerCase()
                    .includes('zocalo') ||
                  String(t.zoneName ?? '')
                    .toLowerCase()
                    .includes('zocalo'),
              ) || son.textures?.[0]
            pendingIndependentZocaloUpdates.push({
              sonId: sonId as AnyID,
              complementoName: puertaComplementoName,
              linkedTextureID: Number(sonTex?.linkedTextureID ?? 0),
              idUnidades:
                typeof (son as any).idUnidades === 'number' ? (son as any).idUnidades : undefined,
            })
          }
        }
      })
    }

    if (pendingIndependentZocaloUpdates.length > 0) {
      const unique = new Map<
        string,
        {
          sonId: AnyID
          complementoName: string
          linkedTextureID: number
          idUnidades?: number
        }
      >()
      for (const u of pendingIndependentZocaloUpdates) {
        unique.set(String(u.sonId), u)
      }
      // console.warn('[Zocalo-Complemento] updating independent sons', {
        // complemento: puertaComplementoName || null,
        // modeEditor,
        // count: unique.size,
        // sons: Array.from(unique.values()).map((u) => ({
          // sonId: u.sonId,
          // linkedTextureID: u.linkedTextureID,
          // idUnidades: u.idUnidades,
        // })),
      // })
      for (const u of unique.values()) {
        void get().updateZocaloWithCompleteTexture(
          u.sonId,
          u.complementoName,
          u.linkedTextureID,
          u.idUnidades,
        )
      }
    }

    if (newTexture) {
      logAcabadoTexture('editLinkedTexture2_applied', newTexture as Record<string, unknown>, {
        linkedID,
        zoneName,
        modeEditor,
        materialAcabado: material.acabado,
        materialName: material.name,
        materialComplemento: (material as Material).complemento,
        pendingZocaloSons: pendingIndependentZocaloUpdates.length,
      })
    } else {
      logAcabado('editLinkedTexture2_no_texture', { linkedID, zoneName, modeEditor })
    }

    return newTexture
  },

  updateLinkedMorph: () => {
    // This has yet to be commented together
  },

  removeLinkedFromParent: (linkedID: string) => {
    // This will make it independent from their parent
  },

  updateLinkedHierarchy: (parent: string) => {
    // This will update the 1.1, 1.2 and such when you delete/move an item
  },

  updateLinkedGLB: async (linkedID: string, idElementosEnlazados: number, info: Options) => {
    const existing = updateLinkedGlbInFlight.get(linkedID)
    if (existing) {
      await existing
      const current = get().items.get(linkedID as AnyID)
      if (
        isLinkedSon(current) &&
        Number(current.idElementosEnlazados ?? current.linkedID) === Number(idElementosEnlazados)
      ) {
        return
      }
    }

    const run = (async () => {
    const sceneGLB = get().sceneGLB
    const prevItem = get().items.get(linkedID as AnyID)
    const items = get().items

    logAcabado('updateLinkedGLB_START', {
      linkedID,
      idElementosEnlazados,
      description: prevItem?.description,
      catalogID: prevItem?.catalogID,
      firstParentID: (prevItem as LinkedItemSon)?.firstParentID,
    })

    if (!prevItem) {
      logAcabado('updateLinkedGLB_ABORT', { linkedID, reason: 'no_item' })
      return
    }
    if (!isLinkedSon(prevItem)) {
      logAcabado('updateLinkedGLB_ABORT', { linkedID, reason: 'not_linkedSon' })
      return
    }

    let prevHand: 'Izquierda' | 'Centro' | 'Derecha' | 'Normal' = 'Derecha'
    if (prevItem.description === 'Tirador' && prevItem.handPosition) {
      prevHand = prevItem.handPosition
    }

    const parentID = prevItem.firstParentID
    const parentItem = items.get(parentID as AnyID)

    if (!parentItem) {
      return
    }
    if (!isLinkedItem(parentItem)) {
      return
    }
    if (!parentItem.idUnidades) {
      return
    }
    const parentIdUnidades = parentItem.idUnidades

    let targetIdElementosEnlazados = idElementosEnlazados
    if (prevItem.description === 'Tirador') {
      const resolved = resolveTiradorIdElementosEnlazados(
        parentItem.tiradorOptions,
        prevItem,
        idElementosEnlazados,
      )
      if (resolved === null) return
      targetIdElementosEnlazados = resolved
    }
    const tiradorOptionIdUnidades =
      prevItem.description === 'Tirador'
        ? resolveTiradorIdUnidadEnlazada(parentItem.tiradorOptions, targetIdElementosEnlazados)
        : undefined

    let newLinkedSonInfo
    try {
      const prevLinkedElId = resolveLinkedElementIdAnt(prevItem)
      const useLinkedElementAnt =
        prevItem.description === 'Tirador' || isApplianceLinkedSonType(prevItem)
      const idAnt = useLinkedElementAnt
        ? prevLinkedElId ||
          (prevItem.description === 'Tirador' ? targetIdElementosEnlazados : parentIdUnidades)
        : parentIdUnidades
      logAcabadoApi(
        'acabadosGetElementosEnlazadosUnidadAcabados2',
        { idAnt, targetIdElementosEnlazados },
      )
      const response = await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
        idAnt,
        targetIdElementosEnlazados,
      )
      logAcabadoApi(
        'acabadosGetElementosEnlazadosUnidadAcabados2',
        { idAnt, targetIdElementosEnlazados },
        response,
      )

      if (!response) {
        logAcabado('updateLinkedGLB_ABORT', { linkedID, reason: 'no_response' })
        return
      }
      if (!response.data) {
        logAcabado('updateLinkedGLB_ABORT', { linkedID, reason: 'no_response_data' })
        return
      }
      newLinkedSonInfo = response.data
    } catch (error) {
      logAcabado('updateLinkedGLB_ERROR', { linkedID, error: String(error) })
      return
    }
    const parent = items.get(prevItem.firstParentID as AnyID)
    if (!isLinkedItem(parent)) {
      return
    }
    const loader = await setGLTFLoader()

    if (newLinkedSonInfo && newLinkedSonInfo.unidad) {
      if (!newLinkedSonInfo.unidad) {
        return
      }
      const unidad = newLinkedSonInfo.unidad
      if (!unidad) {
        return
      }
      const glbRef = unidad.referenciaBlender
      if (!glbRef) {
        return
      }

      const glb = await loadGlbCached(glbRef)

      if (!unidad.idUnidades) {
        return
      }
      const extraInfo = await API().api.acabadosGetUnidadAcabadosInfo(unidad.idUnidades)

      const backGLBInfo: BackGLBInfo = {
        ...unidad,
        linkedID: (unidad as any).linkedID ?? unidad.idUnidades ?? 0,
        idUnidades: unidad.idUnidades ?? 0,
        codigo: unidad.codigo ?? '',
        active: true,
      }
      const processedInfo = newGetDataFromGLB({
        glbData: glb,
        backGLBInfo,
        id: '',
        type: 'linkedSon',
        catalogID: unidad.codigo ?? '',
        extraInfo: {
          zonas: Array.isArray(extraInfo.data?.zonas)
            ? extraInfo.data.zonas.map((z) => ({
                idAcabadoUnidad: z.idAcabadoUnidad,
                zona: z.zona ?? '',
              }))
            : [],
        },
        itemInfo: {},
      })

      const backLinkedData = {
        ...newLinkedSonInfo,
        cantidad: newLinkedSonInfo.cantidad ?? 1,
      } as BackLinkedData

      const childrenInfo = getLinkedChildrenInfo(
        backLinkedData,
        info as unknown as Record<string, Record<string, ItemWithOptions[]>>,
        targetIdElementosEnlazados,
      )
      const newChildrenOrder = [...parent.childrenOrder]
      set((s) => {
        const currElement = s.items.get(linkedID as AnyID)
        if (!isLinkedSon(currElement)) return
        const prevPosition = { x: currElement.x, y: currElement.y, z: currElement.z }
        const prevSize = {
          width: currElement.width,
          height: currElement.height,
          thickness: currElement.thickness,
        }
        const prevMorphs = currElement.morphs

        let newX = prevPosition.x
        let newY = prevPosition.y
        let newZ = prevPosition.z

        if (parent) {
          const relativeX = prevPosition.x - parent.x
          const relativeY = prevPosition.y - parent.y
          const relativeZ = prevPosition.z - parent.z

          newX = parent.x + relativeX
          newY = parent.y + relativeY
          newZ = parent.z + relativeZ

          if (currElement.description !== 'Tirador') {
            if (
              Math.abs(relativeX) < 1000 &&
              Math.abs(relativeY) < 1000 &&
              Math.abs(relativeZ) < 1000
            ) {
              currElement.x = newX
              currElement.y = newY
              currElement.z = newZ
            }
          }
        }

        currElement.linkedID = targetIdElementosEnlazados
        currElement.idElementosEnlazados = targetIdElementosEnlazados
        currElement.catalogID = unidad.codigo!
        currElement.idUnidades = tiradorOptionIdUnidades ?? unidad.idUnidades ?? 0
        currElement.textures = processedInfo.textures
        currElement.zones = processedInfo.zones

        if (currElement.description === 'Tirador') {
          if (newLinkedSonInfo) {
            if (typeof newLinkedSonInfo.posicionX === 'number') {
              currElement.x = newLinkedSonInfo.posicionX
            } else {
              currElement.x = newX
            }
            if (typeof newLinkedSonInfo.posicionY === 'number') {
              currElement.y = newLinkedSonInfo.posicionY
            } else {
              currElement.y = newY
            }
            if (typeof newLinkedSonInfo.posicionZ === 'number') {
              currElement.z = newLinkedSonInfo.posicionZ
            } else {
              currElement.z = newZ
            }
          } else {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          }
        } else if (currElement.typeOfSon === 'Fregadero' && newLinkedSonInfo) {
          const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
          const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
          const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

          if (isNaN(posicionBackendX) || isNaN(posicionBackendY) || isNaN(posicionBackendZ)) {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          } else {
            // Coords API = catálogo; no dejar y escalada previa del fregadero anterior.
            ;(currElement as any).baseY = posicionBackendY
            ;(currElement as any).posicionX = posicionBackendX
            ;(currElement as any).posicionY = posicionBackendY
            ;(currElement as any).posicionZ = posicionBackendZ
            currElement.x = posicionBackendX
            currElement.y = posicionBackendY
            currElement.z = posicionBackendZ
            // Reescalar X/Y/Z al alto/ancho/fondo actual del padre (p. ej. tras morph Alto).
            if (isLinkedItem(parent)) {
              applyLinkedApplianceLocalTransformFromApi(currElement, parent)
            }
          }

          setTimeout(() => {
            const positionFregaderoGrifoPlaca = get().positionFregaderoGrifoPlacaAccordingToEncimera
            if (positionFregaderoGrifoPlaca) {
              positionFregaderoGrifoPlaca(linkedID)
            }
          }, 0)
        } else if (isPlacaLinkedSon(currElement) && newLinkedSonInfo) {
          currElement.typeOfSon = 'Placa'
          const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
          const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
          const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

          if (isNaN(posicionBackendX) || isNaN(posicionBackendY) || isNaN(posicionBackendZ)) {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          } else {
            ;(currElement as any).baseY = posicionBackendY
            ;(currElement as any).posicionX = posicionBackendX
            ;(currElement as any).posicionY = posicionBackendY
            ;(currElement as any).posicionZ = posicionBackendZ
            currElement.x = posicionBackendX
            currElement.y = posicionBackendY
            currElement.z = posicionBackendZ
            const encimeraForAjuste = findSiblingEncimera(s.items, currElement.firstParentID)
            ;(currElement as { encimeraAcabadoAjusteY?: number }).encimeraAcabadoAjusteY =
              resolveEncimeraAcabadoAjusteYMm(encimeraForAjuste as never)
            if (isLinkedItem(parent)) {
              const encimeraAabb = getCatalogGlbAabbMm(
                s.sceneGLB as never,
                (encimeraForAjuste as { catalogID?: string | number } | null)?.catalogID,
                [
                  sceneGLBLookupKey(
                    String(
                      (encimeraForAjuste as { catalogID?: string | number } | null)?.catalogID ??
                        '',
                    ),
                  ),
                  (encimeraForAjuste as { idUnidades?: number } | null)?.idUnidades,
                ],
              )
              applyLinkedApplianceLocalTransformFromApi(
                currElement,
                parent,
                encimeraForAjuste as never,
                { encimeraAabb },
              )
            }
          }
          schedulePlacaAccordingToEncimera(get, linkedID)
        } else if (isPlacaLinkedSon(currElement)) {
          currElement.x = newX
          currElement.y = newY
          currElement.z = newZ
          schedulePlacaAccordingToEncimera(get, linkedID)
        } else if (
          (currElement.typeOfSon === 'Grifo' || currElement.typeOfSon === 'TPCGrifo') &&
          newLinkedSonInfo
        ) {
          const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
          const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
          const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

          if (isNaN(posicionBackendX) || isNaN(posicionBackendY) || isNaN(posicionBackendZ)) {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          } else {
            ;(currElement as any).baseY = posicionBackendY
            ;(currElement as any).posicionX = posicionBackendX
            ;(currElement as any).posicionY = posicionBackendY
            ;(currElement as any).posicionZ = posicionBackendZ
            currElement.x = posicionBackendX
            currElement.y = posicionBackendY
            currElement.z = posicionBackendZ
            if (isLinkedItem(parent)) {
              applyLinkedApplianceLocalTransformFromApi(currElement, parent)
            }
          }

          setTimeout(() => {
            const positionFregaderoGrifoPlaca = get().positionFregaderoGrifoPlacaAccordingToEncimera
            if (positionFregaderoGrifoPlaca) {
              positionFregaderoGrifoPlaca(linkedID)
            }
          }, 0)
        } else if (currElement.typeOfSon === 'TPCGrifo' && newLinkedSonInfo) {
          const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
          const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
          const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0

          if (isNaN(posicionBackendX) || isNaN(posicionBackendY) || isNaN(posicionBackendZ)) {
            currElement.x = newX
            currElement.y = newY
            currElement.z = newZ
          } else {
            ;(currElement as any).baseY = posicionBackendY
            ;(currElement as any).posicionX = posicionBackendX
            ;(currElement as any).posicionY = posicionBackendY
            ;(currElement as any).posicionZ = posicionBackendZ
            currElement.x = posicionBackendX
            currElement.y = posicionBackendY
            currElement.z = posicionBackendZ
            if (isLinkedItem(parent)) {
              applyLinkedApplianceLocalTransformFromApi(currElement, parent)
            }
          }
        } else {
          currElement.x = newX
          currElement.y = newY
          currElement.z = newZ
        }

        if (isPlacaLinkedSon(currElement)) {
          const ancho = Number(unidad.ancho)
          const alto = Number(unidad.alto ?? (unidad as { altura?: number }).altura)
          const fondo = Number(unidad.fondo)
          currElement.width = Number.isFinite(ancho) && ancho > 0 ? ancho : prevSize.width
          currElement.height = Number.isFinite(alto) && alto > 0 ? alto : prevSize.height
          currElement.thickness = Number.isFinite(fondo) && fondo > 0 ? fondo : prevSize.thickness
          currElement.morphs = Array.isArray((processedInfo as { morphs?: unknown }).morphs)
            ? ((processedInfo as { morphs: typeof prevMorphs }).morphs ?? [])
            : []
          if (processedInfo.glbSize) {
            currElement.glbSize = processedInfo.glbSize
          }
        } else {
          currElement.width = prevSize.width
          currElement.height = prevSize.height
          currElement.thickness = prevSize.thickness
          currElement.morphs = prevMorphs
        }
        currElement.referenciaBlender = unidad.referenciaBlender
        currElement.glbPath = String(unidad?.referenciaBlender ?? '')
        setSceneGlbForCatalog(
          s.sceneGLB as never,
          unidad.codigo,
          { glb, backGLBInfo: unidad },
          unidad.idUnidades,
        )

        if (!isLinkedItem(parent)) return
        const parentObj = s.items.get(prevItem.firstParentID as AnyID)
        if (parentObj && isLinkedItem(parentObj)) {
          parentObj.childrenOrder = newChildrenOrder.sort(depthVisualOrderSorter)
        }
        currElement.typeOfSon =
          currElement.typeOfSon ||
          childrenInfo?.typeOfSon ||
          currElement.description ||
          'Desconocido'

        if (currElement.description !== 'Tirador') {
          const x = currElement.x ?? 0
          const y = currElement.y ?? 0
          const z = currElement.z ?? 0
          currElement.visible = !(x === 0 && y === 0 && z === 0)
        }

        if (currElement.description === 'Tirador' && newLinkedSonInfo) {
          const posicionesAlternativas = [
            'biPosicionX',
            'biPosicionY',
            'biPosicionZ',
            'bcPosicionX',
            'bcPosicionY',
            'bcPosicionZ',
            'bdPosicionX',
            'bdPosicionY',
            'bdPosicionZ',
            'aiPosicionX',
            'aiPosicionY',
            'aiPosicionZ',
            'acPosicionX',
            'acPosicionY',
            'acPosicionZ',
            'adPosicionX',
            'adPosicionY',
            'adPosicionZ',
            'biRotacionX',
            'biRotacionY',
            'biRotacionZ',
            'bcRotacionX',
            'bcRotacionY',
            'bcRotacionZ',
            'bdRotacionX',
            'bdRotacionY',
            'bdRotacionZ',
            'aiRotacionX',
            'aiRotacionY',
            'aiRotacionZ',
            'acRotacionX',
            'acRotacionY',
            'acRotacionZ',
            'adRotacionX',
            'adRotacionY',
            'adRotacionZ',
            'b2idRotacionX',
            'b2idRotacionY',
            'b2idRotacionZ',
            'a2idRotacionX',
            'a2idRotacionY',
            'a2idRotacionZ',
          ]

          const drawerNode = getTiradorDrawerNode(currElement)
          const newTirador: LinkedItemSon = {
            ...currElement,
            id: currElement.id,
            nodo: drawerNode,
            option: drawerNode,
            parentOption: currElement.parentOption,
            handPosition: currElement.handPosition || 'Centro',
            catalogID: newLinkedSonInfo.unidad?.codigo ?? currElement.catalogID ?? '',
            linkedID: targetIdElementosEnlazados,
            idElementosEnlazados: targetIdElementosEnlazados,
            morphs: currElement.morphs || newLinkedSonInfo.unidad?.morphs || [],
          }
          posicionesAlternativas.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(newLinkedSonInfo, key)) {
              // @ts-expect-error: Puede que no esté tipado en el DTO, pero lo copiamos si existe
              newTirador[key] = newLinkedSonInfo[key]
            }
          })
          if (newLinkedSonInfo.unidad?.morphs) {
            newTirador.morphs = mapMorphsFromDTO(newLinkedSonInfo.unidad.morphs)
          }
          if (typeof newLinkedSonInfo.posicionX === 'number')
            newTirador.x = newLinkedSonInfo.posicionX
          if (typeof newLinkedSonInfo.posicionY === 'number')
            newTirador.y = newLinkedSonInfo.posicionY
          if (typeof newLinkedSonInfo.posicionZ === 'number')
            newTirador.z = newLinkedSonInfo.posicionZ
          if (newLinkedSonInfo.unidad?.referenciaBlender)
            newTirador.glbPath = String(newLinkedSonInfo.unidad.referenciaBlender)

          const nuevaPos = calculateTiradorPosition(newTirador, parentItem)
          newTirador.x = nuevaPos.x
          newTirador.y = nuevaPos.y
          newTirador.z = nuevaPos.z

          s.items.set(currElement.id, newTirador)
        }
      })
      if (
        prevItem.typeOfSon === 'Fregadero' ||
        prevItem.typeOfSon === 'Grifo' ||
        prevItem.typeOfSon === 'TPCGrifo'
      ) {
        setTimeout(() => get().syncFregaderoAccessories(parentID as string), 0)
      }
      return
    }

    const parentOption = prevItem.parentOption
    const option = prevItem.option
    const optionsArray = info[parentOption]?.[option]
    const foundOption = Object.values(optionsArray ?? {}).find((el) => {
      const opt = el as unknown as ItemWithOptions
      return (
        typeof opt === 'object' &&
        opt !== null &&
        'idUnidadEnlazada' in opt &&
        opt.idUnidadEnlazada === idElementosEnlazados
      )
    }) as (ItemWithOptions & { codigo?: string | number }) | undefined
    const codigo = foundOption?.codigo
    if (!codigo) {
      return
    }

    const glb = await getGLB(sceneGLB, String(codigo))
    const backData = newLinkedSonInfo.unidad

    set((s) => {
      const currElement = s.items.get(linkedID as AnyID)
      if (!isLinkedSon(currElement)) return
      const prevPosition = { x: currElement.x, y: currElement.y, z: currElement.z }
      const prevSize = {
        width: currElement.width,
        height: currElement.height,
        thickness: currElement.thickness,
      }
      const prevMorphs = currElement.morphs
      let newX = prevPosition.x
      let newY = prevPosition.y
      let newZ = prevPosition.z
      if (currElement.description === 'Tirador' && parent) {
        const distToLeft = prevPosition.x - parent.x
        const distToBase = parent.y + parent.height - prevPosition.y
        const distToBack = parent.z + parent.thickness - prevPosition.z
        newX = parent.x + distToLeft
        newY = parent.y + parent.height - distToBase
        newZ = parent.z + parent.thickness - distToBack
      }

      currElement.glbData = glb
      currElement.backGLBInfo = {
        idUnidades: backData?.idUnidades ?? 0,
        linkedID: backData?.idUnidades ?? 0,
        active: true,
        codigo: backData?.codigo ?? '',
        referenciaBlender: backData?.referenciaBlender ?? '',
        elemento: backData?.elemento,
        descripcion: backData?.descripcion,
        descripcionLarga: backData?.descripcionLarga,
        ancho: backData?.ancho,
        fondo: backData?.fondo,
        alto: backData?.alto,
        mano: backData?.mano,
        altura: backData?.altura,
        alturaHaciaAbajo: backData?.alturaHaciaAbajo,
        colision: backData?.colision,
        cota: backData?.cota,
        tipoNormalizado: backData?.tipoNormalizado ?? 'MBJ',
        tipoUnidNorm: backData?.tipoUnidNorm ?? '',
        modificarAncho: backData?.modificarAncho,
        modificarFondo: backData?.modificarFondo,
        modificarAlto: backData?.modificarAlto,
        morphs: backData?.morphs ?? [],
        precio: backData?.precio,
        acabados: backData?.acabados ?? [],
        unidadesLuces: backData?.unidadesLuces ?? [],
        elementosEnlazados: backData?.elementosEnlazados ?? [],
        referenciaJpgpng: backData?.referenciaJpgpng,
        universal: false,
      } as unknown as BackGLBInfo
      currElement.visible = true
      currElement.icono = ''
      currElement.matrix = new Matrix4()
      currElement.linkedID = idElementosEnlazados
      currElement.catalogID = codigo
      currElement.idUnidades =
        tiradorOptionIdUnidades ??
        foundOption?.idUnidadEnlazada ??
        backData?.idUnidades ??
        currElement.idUnidades

      currElement.x = newX
      currElement.y = newY
      currElement.z = newZ
        currElement.width = prevSize.width
        currElement.height = prevSize.height
        currElement.thickness = prevSize.thickness
        currElement.morphs = prevMorphs

        if (currElement.description !== 'Tirador') {
          const x = currElement.x ?? 0
          const y = currElement.y ?? 0
          const z = currElement.z ?? 0
          currElement.visible = !(x === 0 && y === 0 && z === 0)
        }

      if (newLinkedSonInfo.unidad) {
        currElement.glbPath = String(newLinkedSonInfo.unidad.referenciaBlender ?? '')
        if (
          newLinkedSonInfo.unidad.codigo !== undefined &&
          newLinkedSonInfo.unidad.codigo !== null
        ) {
          setSceneGlbForCatalog(
            s.sceneGLB as never,
            newLinkedSonInfo.unidad.codigo,
            {
              glb,
              backGLBInfo: {
                idUnidades: newLinkedSonInfo.unidad?.idUnidades ?? 0,
                linkedID: newLinkedSonInfo.unidad?.idUnidades ?? 0,
                active: true,
                codigo: newLinkedSonInfo.unidad?.codigo ?? '',
                referenciaBlender: newLinkedSonInfo.unidad?.referenciaBlender ?? '',
                elemento: newLinkedSonInfo.unidad?.elemento,
                descripcion: newLinkedSonInfo.unidad?.descripcion,
                descripcionLarga: newLinkedSonInfo.unidad?.descripcionLarga,
                ancho: newLinkedSonInfo.unidad?.ancho,
                fondo: newLinkedSonInfo.unidad?.fondo,
                alto: newLinkedSonInfo.unidad?.alto,
                mano: newLinkedSonInfo.unidad?.mano,
                altura: newLinkedSonInfo.unidad?.altura,
                alturaHaciaAbajo: newLinkedSonInfo.unidad?.alturaHaciaAbajo,
                colision: newLinkedSonInfo.unidad?.colision,
                cota: newLinkedSonInfo.unidad?.cota,
                tipoNormalizado: newLinkedSonInfo.unidad?.tipoNormalizado ?? 'MBJ',
                tipoUnidNorm: newLinkedSonInfo.unidad?.tipoUnidNorm ?? '',
                modificarAncho: newLinkedSonInfo.unidad?.modificarAncho,
                modificarFondo: newLinkedSonInfo.unidad?.modificarFondo,
                modificarAlto: newLinkedSonInfo.unidad?.modificarAlto,
                morphs: newLinkedSonInfo.unidad?.morphs ?? [],
                precio: newLinkedSonInfo.unidad?.precio,
                acabados: newLinkedSonInfo.unidad?.acabados ?? [],
                unidadesLuces: newLinkedSonInfo.unidad?.unidadesLuces ?? [],
                elementosEnlazados: newLinkedSonInfo.unidad?.elementosEnlazados ?? [],
                referenciaJpgpng: newLinkedSonInfo.unidad?.referenciaJpgpng,
                universal: newLinkedSonInfo.unidad?.universal,
              } as unknown as BackGLBInfo,
            },
            newLinkedSonInfo.unidad.idUnidades,
          )
        }
      }

      currElement.typeOfSon =
        currElement.typeOfSon || prevItem.typeOfSon || prevItem.description || 'Desconocido'

      if (currElement.description === 'Tirador') {
        currElement.handPosition = currElement.handPosition || 'Centro'
        const nuevaPos = calculateTiradorPosition(currElement, parentItem)
        currElement.x = nuevaPos.x
        currElement.y = nuevaPos.y
        currElement.z = nuevaPos.z
        get().setLinkedHandPosition(currElement.id as LinkedItemID, currElement.handPosition)
      }
    })

    const updatedParentItem = get().items.get(parentID as AnyID)
    if (
      updatedParentItem &&
      updatedParentItem.type === 'linkedItem' &&
      updatedParentItem.childs &&
      Array.isArray(updatedParentItem.childs) &&
      updatedParentItem.childs.length > 0
    ) {
      adjustChildrenPositionsAfterInheritance(
        updatedParentItem,
        (childId) => get().items.get(childId),
        (childId, updates) => {
          get().editItem(childId, updates)
        },
      )
    }

    if (
      prevItem.typeOfSon === 'Fregadero' ||
      prevItem.typeOfSon === 'Grifo' ||
      prevItem.typeOfSon === 'TPCGrifo'
    ) {
      setTimeout(() => get().syncFregaderoAccessories(parentID as string), 0)
    }
    if (isEncimeraLinkedSonItem(prevItem) || isEncimeraLinkedSonItem(get().items.get(linkedID as AnyID))) {
      scheduleAppliancesAccordingToEncimera(get, linkedID)
    }
    })()

    updateLinkedGlbInFlight.set(linkedID, run)
    try {
      await run
    } finally {
      if (updateLinkedGlbInFlight.get(linkedID) === run) {
        updateLinkedGlbInFlight.delete(linkedID)
      }
    }
  },

  updateLinkedGLBForGroup: async (
    parentIDs: Set<string>,
    idElementosEnlazados: number,
    info: Options,
    childDescription: string = 'Tirador',
    idElementosEnlazadosAnt?: number,
    modeEditor?: 'Single' | 'All' | 'Selected' | 'MBJ' | 'MAL' | 'COL',
    childIDsFilter?: Set<string>,
  ) => {
    logAcabado('updateLinkedGLBForGroup_START', {
      parentIDs: Array.from(parentIDs),
      idElementosEnlazados,
      idElementosEnlazadosAnt,
      childDescription,
      modeEditor,
      childIDsFilter: childIDsFilter ? Array.from(childIDsFilter) : undefined,
    })

    const batchActive = isLinkedVisualBatchActive()
    let originalFrameloop: Store['frameloop'] = 'demand'
    if (!batchActive) {
      set((store) => {
        originalFrameloop = store.frameloop
        store.frameloop = 'always'
      })
    }

    const loader = await setGLTFLoader()
    const state = get()

    try {
      if (childDescription === 'Fregadero' && modeEditor === 'All') {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (childItem.description === 'Fregadero' || childItem.typeOfSon === 'Fregadero') {
              if (linkedItem.fregaderoOptions && linkedItem.fregaderoOptions.length > 0) {
                let selectedDescription = null

                for (const parentKey in info) {
                  if (info[parentKey] && typeof info[parentKey] === 'object') {
                    for (const childKey in info[parentKey]) {
                      const childOptions = info[parentKey][childKey]
                      if (Array.isArray(childOptions)) {
                        const found = childOptions.find(
                          (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                        )
                        if (found && found.descripcion) {
                          selectedDescription = found.descripcion
                          break
                        }
                      }
                    }
                    if (selectedDescription) break
                  }
                }

                if (selectedDescription) {
                  const selectedOption = linkedItem.fregaderoOptions.find(
                    (opt: any) => opt.descripcion === selectedDescription,
                  )

                  if (selectedOption) {
                    if (selectedOption.idElementosEnlazados) {
                      // No pisar linkedID aquí: updateLinkedGLB lo necesita como
                      // idElementoEnlazadoAntiguo en getelementosenlazadosunidadacabados/{ant}/{nuevo}.
                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.idUnidades = selectedOption.idUnidadEnlazada
                        }
                      })

                      await get().updateLinkedGLB(
                        childID,
                        selectedOption.idElementosEnlazados,
                        info,
                      )
                    } else {
                      // console.warn(
                        // `[DEBUG] updateLinkedGLBForGroup - No se pudo obtener idElementosEnlazados para mueble ${linkedItem.id}`,
                      // )
                    }
                  } else {
                    // console.warn(
                      // `[DEBUG] updateLinkedGLBForGroup - No se encontró opción para descripción "${selectedDescription}" en fregaderoOptions del mueble ${linkedItem.id}`,
                    // )
                  }
                } else {
                  // console.warn(
                    // `[DEBUG] updateLinkedGLBForGroup - No se pudo extraer la descripción del elemento seleccionado para mueble ${linkedItem.id}`,
                  // )
                }
              } else {
                // console.warn(
                  // `[DEBUG] updateLinkedGLBForGroup - Mueble ${linkedItem.id} no tiene fregaderoOptions guardadas`,
                // )
              }
            }
          }
        }

        return
      }

      // NOTA: Este bloque está comentado porque estaba causando que todos los grifos
      // obtuvieran el mismo idElementosEnlazados cuando se cambiaba el GLB del padre.
      // Cuando se llama desde LinkedTexturesEditor.tsx (modeEditor undefined), cada grifo
      // debe procesarse individualmente según su nodo en la lógica normal más abajo.
      /*
      if (
        (childDescription === 'Grifo' || childDescription?.toLowerCase().includes('grifo')) &&
        !modeEditor
      ) {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (
              childItem.description?.toLowerCase().includes('grifo') ||
              childItem.typeOfSon?.toLowerCase().includes('grifo')
            ) {
              set((s) => {
                const child = s.items.get(childID as AnyID)
                if (child && isLinkedSon(child)) {
                  child.linkedID = idElementosEnlazados
                }
              })

              try {
                await get().updateLinkedGLB(childID, idElementosEnlazados, info)
              } catch (error) {
                console.error('❌ GRIFO DEBUG - Error en updateLinkedGLB:', error)
              }
            }
          }
        }

        return
      }
      */

      if (childDescription === 'TPCGrifo' && modeEditor === 'All') {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (childItem.description === 'TPCGrifo' || childItem.typeOfSon === 'TPCGrifo') {
              let selectedDescription = null
              for (const parentKey in info) {
                if (info[parentKey] && typeof info[parentKey] === 'object') {
                  for (const childKey in info[parentKey]) {
                    const childOptions = info[parentKey][childKey]
                    if (Array.isArray(childOptions)) {
                      const found = childOptions.find(
                        (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                      )
                      if (found && found.descripcion) {
                        selectedDescription = found.descripcion
                        break
                      }
                    }
                  }
                  if (selectedDescription) break
                }
              }

              let correctIdElementosEnlazados: number | null = null
              let backendPositions: { x: number; y: number; z: number } | null = null

              if (
                selectedDescription &&
                linkedItem.grifoOptions &&
                linkedItem.grifoOptions.length > 0
              ) {
                const matchingOption = linkedItem.grifoOptions.find(
                  (opt: any) => opt.descripcion === selectedDescription,
                )

                if (matchingOption && matchingOption.idElementosEnlazados) {
                  correctIdElementosEnlazados = matchingOption.idElementosEnlazados

                  if (linkedItem.idUnidades) {
                    try {
                      const backendResponse =
                        await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
                          linkedItem.idUnidades,
                          correctIdElementosEnlazados,
                        )
                      if (backendResponse?.data) {
                        const backendData = backendResponse.data
                        backendPositions = {
                          x: backendData.posicionX ?? childItem.x,
                          y: backendData.posicionY ?? childItem.y,
                          z: backendData.posicionZ ?? childItem.z,
                        }
                      }
                    } catch (error) {
                      // console.warn(
                        // `❌ [TPCGrifo All] Error obteniendo posiciones del backend para ${childID}:`,
                        // error,
                      // )
                    }
                  }
                }
              }

              // Usar las posiciones del backend si están disponibles, sino usar las actuales
              const positionsToPreserve = backendPositions || {
                x: childItem.x,
                y: childItem.y,
                z: childItem.z,
              }

              if (selectedDescription) {
                set((s) => {
                  const child = s.items.get(childID as AnyID)
                  if (child && isLinkedSon(child)) {
                    child.linkedID = idElementosEnlazados

                    for (const parentKey in info) {
                      if (info[parentKey] && typeof info[parentKey] === 'object') {
                        for (const childKey in info[parentKey]) {
                          const childOptions = info[parentKey][childKey]
                          if (Array.isArray(childOptions)) {
                            const found = childOptions.find(
                              (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                            )
                            if (found && found.idUnidadEnlazada) {
                              child.idUnidades = found.idUnidadEnlazada
                              break
                            }
                          }
                        }
                      }
                    }
                  }
                })

                try {
                  // Guardar las posiciones a preservar antes de updateLinkedGLB
                  const positionsToRestore = positionsToPreserve ? { ...positionsToPreserve } : null

                  await get().updateLinkedGLB(childID, idElementosEnlazados, info)

                  // Función para restaurar posiciones
                  const restorePositions = () => {
                    if (positionsToRestore) {
                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.x = positionsToRestore.x
                          child.y = positionsToRestore.y
                          child.z = positionsToRestore.z
                          ;(child as any).baseY = positionsToRestore.y
                        }
                      })
                    }
                  }

                  restorePositions()

                  setTimeout(() => {
                    restorePositions()
                  }, 100)

                  // Restaurar después de un delay más largo (por si hay más callbacks)
                  setTimeout(() => {
                    restorePositions()
                    // Ajustar la posición Y según el acabado de la encimera
                    const positionFregaderoGrifoPlaca =
                      get().positionFregaderoGrifoPlacaAccordingToEncimera
                    if (positionFregaderoGrifoPlaca) {
                      positionFregaderoGrifoPlaca(childID)
                    }
                  }, 500)
                } catch (error) {
                  console.error('❌ TPCGRIFO DEBUG - Error en updateLinkedGLB:', error)
                }
              }
            }
          }
        }

        return
      }

      if (
        (childDescription === 'Grifo' || childDescription?.toLowerCase().includes('grifo')) &&
        modeEditor === 'All'
      ) {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (
              childItem.description?.toLowerCase().includes('grifo') ||
              childItem.typeOfSon?.toLowerCase().includes('grifo')
            ) {
              // Guardar el linkedID original y las posiciones del grifo actual
              const originalLinkedID = childItem.linkedID
              let originalPositions: { x: number; y: number; z: number } | null = null

              // Obtener las posiciones originales del backend usando el linkedID del grifo actual
              if (originalLinkedID && linkedItem.idUnidades) {
                try {
                  const originalResponse =
                    await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
                      linkedItem.idUnidades,
                      originalLinkedID,
                    )
                  if (originalResponse?.data) {
                    const originalData = originalResponse.data
                    originalPositions = {
                      x: originalData.posicionX ?? childItem.x,
                      y: originalData.posicionY ?? childItem.y,
                      z: originalData.posicionZ ?? childItem.z,
                    }
                  }
                } catch (error) {
                  // console.warn('❌ Error obteniendo posiciones originales del grifo:', error)
                  // Si falla, usar las posiciones actuales del grifo
                  originalPositions = {
                    x: childItem.x,
                    y: childItem.y,
                    z: childItem.z,
                  }
                }
              } else {
                // Si no hay linkedID original, usar las posiciones actuales
                originalPositions = {
                  x: childItem.x,
                  y: childItem.y,
                  z: childItem.z,
                }
              }

              if (linkedItem.grifoOptions && linkedItem.grifoOptions.length > 0) {
                let selectedDescription = null

                for (const parentKey in info) {
                  if (info[parentKey] && typeof info[parentKey] === 'object') {
                    for (const childKey in info[parentKey]) {
                      const childOptions = info[parentKey][childKey]
                      if (Array.isArray(childOptions)) {
                        const found = childOptions.find(
                          (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                        )
                        if (found && found.descripcion) {
                          selectedDescription = found.descripcion
                          break
                        }
                      }
                    }
                    if (selectedDescription) break
                  }
                }

                if (selectedDescription) {
                  const selectedOption = linkedItem.grifoOptions.find(
                    (opt: any) => opt.descripcion === selectedDescription,
                  )

                  if (selectedOption) {
                    if (selectedOption.idElementosEnlazados) {
                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = selectedOption.idElementosEnlazados
                          child.idUnidades = selectedOption.idUnidadEnlazada
                        }
                      })

                      await get().updateLinkedGLB(
                        childID,
                        selectedOption.idElementosEnlazados,
                        info,
                      )

                      // Restaurar las posiciones originales después de cambiar el modelo
                      if (originalPositions) {
                        set((s) => {
                          const child = s.items.get(childID as AnyID)
                          if (child && isLinkedSon(child)) {
                            child.x = originalPositions!.x
                            child.y = originalPositions!.y
                            child.z = originalPositions!.z
                            ;(child as any).baseY = originalPositions!.y
                          }
                        })

                        // Ajustar la posición Y según el acabado de la encimera después de restaurar
                        setTimeout(() => {
                          const positionFregaderoGrifoPlaca =
                            get().positionFregaderoGrifoPlacaAccordingToEncimera
                          if (positionFregaderoGrifoPlaca) {
                            positionFregaderoGrifoPlaca(childID)
                          }
                        }, 100)
                      }
                    } else {
                      // console.warn(
                        // `[DEBUG] updateLinkedGLBForGroup - No se pudo obtener idElementosEnlazados para mueble ${linkedItem.id}`,
                      // )
                    }
                  } else {
                    // console.warn(
                      // `[DEBUG] updateLinkedGLBForGroup - No se encontró opción para descripción "${selectedDescription}" en grifoOptions del mueble ${linkedItem.id}`,
                    // )
                  }
                } else {
                  // console.warn(
                    // `[DEBUG] updateLinkedGLBForGroup - No se pudo extraer la descripción del elemento seleccionado para mueble ${linkedItem.id}`,
                  // )
                }
              } else {
                // console.warn(
                  // `[DEBUG] updateLinkedGLBForGroup - Mueble ${linkedItem.id} no tiene grifoOptions guardadas`,
                // )
              }
            }
          }
        }

        return
      }

      if (childDescription === 'Placa' && modeEditor === 'All') {
        const allLinkedItems = Array.from(state.items.values()).filter(
          (item) => item.type === 'linkedItem',
        )

        for (const linkedItem of allLinkedItems) {
          if (!isLinkedItem(linkedItem) || !linkedItem.childs || linkedItem.childs.length === 0) {
            continue
          }

          for (const childID of linkedItem.childs) {
            const childItem = state.items.get(childID as AnyID)
            if (!isLinkedSon(childItem)) continue

            if (childItem.description === 'Placa' || childItem.typeOfSon === 'Placa') {
              if (linkedItem.placaOptions && linkedItem.placaOptions.length > 0) {
                let selectedDescription = null

                for (const parentKey in info) {
                  if (info[parentKey] && typeof info[parentKey] === 'object') {
                    for (const childKey in info[parentKey]) {
                      const childOptions = info[parentKey][childKey]
                      if (Array.isArray(childOptions)) {
                        const found = childOptions.find(
                          (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                        )
                        if (found && found.descripcion) {
                          selectedDescription = found.descripcion
                          break
                        }
                      }
                    }
                    if (selectedDescription) break
                  }
                }

                if (selectedDescription) {
                  const selectedOption = linkedItem.placaOptions.find(
                    (opt: any) => opt.descripcion === selectedDescription,
                  )

                  if (selectedOption) {
                    if (selectedOption.idElementosEnlazados) {
                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = selectedOption.idElementosEnlazados
                          child.idUnidades = selectedOption.idUnidadEnlazada
                        }
                      })

                      await get().updateLinkedGLB(
                        childID,
                        selectedOption.idElementosEnlazados,
                        info,
                      )
                    } else {
                      // console.warn(
                        // `[DEBUG] updateLinkedGLBForGroup - No se pudo obtener idElementosEnlazados para mueble ${linkedItem.id}`,
                      // )
                    }
                  } else {
                    // console.warn(
                      // `[DEBUG] updateLinkedGLBForGroup - No se encontró opción para descripción "${selectedDescription}" en placaOptions del mueble ${linkedItem.id}`,
                    // )
                  }
                } else {
                  // console.warn(
                    // `[DEBUG] updateLinkedGLBForGroup - No se pudo extraer la descripción del elemento seleccionado para mueble ${linkedItem.id}`,
                  // )
                }
              } else {
                // console.warn(
                  // `[DEBUG] updateLinkedGLBForGroup - Mueble ${linkedItem.id} no tiene placaOptions guardadas`,
                // )
              }
            }
          }
        }

        return
      }

      const parentIDsArray = Array.from(parentIDs)
      const BATCH_SIZE = 5
      let processedCount = 0

      for (const parentID of parentIDsArray) {
        const parentItem = state.items.get(parentID as AnyID)
        if (!parentItem || !isLinkedItem(parentItem)) {
          continue
        }

        const childrenToProcess = Array.from(parentItem.childrenOrder)

        for (let i = 0; i < childrenToProcess.length; i++) {
          const childKey = childrenToProcess[i]
          const childID = childKey.split('!')[1]
          const childItem = state.items.get(childID as AnyID)
          if (!childItem || !isLinkedSon(childItem)) {
            continue
          }
          // Filtrar hijos por descripción, pero permitir grifos/TPCGrifos cuando childDescription es 'Grifo'
          // También permitir fregaderos cuando childDescription es 'Fregadero'
          const isGrifoMatch =
            (childDescription === 'Grifo' || childDescription === 'TPCGrifo') &&
            (childItem.description?.toLowerCase().includes('grifo') ||
              childItem.typeOfSon === 'Grifo' ||
              childItem.typeOfSon === 'TPCGrifo')

          const isFregaderoMatch =
            childDescription === 'Fregadero' &&
            (childItem.description?.toLowerCase().includes('fregadero') ||
              childItem.typeOfSon === 'Fregadero')

          if (childItem.description !== childDescription && !isGrifoMatch && !isFregaderoMatch) {
            continue
          }

          if (childIDsFilter && !childIDsFilter.has(childID)) {
            continue
          }

          try {
            let targetIdElementosEnlazados = idElementosEnlazados
            let targetIdElementosEnlazadosAnt = idElementosEnlazadosAnt
            let optionIdUnidades: number | undefined

            // Si idElementosEnlazadosAnt no se proporcionó, usar el ID del elemento enlazado actual
            if (targetIdElementosEnlazadosAnt === undefined) {
              targetIdElementosEnlazadosAnt =
                resolveLinkedElementIdAnt(childItem) || idElementosEnlazados
            }

            // Para tipos genéricos (Micro, Horno, HornoMicro, etc.), buscar el idElementosEnlazados correcto
            // basado en el nodo actual y el idUnidadEnlazada de la opción seleccionada
            const safeInfo = info || {}
            if (
              childDescription !== 'Tirador' &&
              childDescription !== 'Placa' &&
              childDescription !== 'Grifo' &&
              childDescription !== 'TPCGrifo' &&
              childDescription !== 'Fregadero' &&
              !childDescription?.toLowerCase().includes('grifo')
            ) {
              const currentNode = childItem.nodo
              const currentParentOption = childItem.parentOption

              // Solo intentar buscar la opción correcta si tenemos toda la información necesaria
              // Si no, usamos el idElementosEnlazados que se pasó directamente
              if (currentNode !== undefined && currentParentOption !== undefined) {
                // Buscar en safeInfo la opción con el idElementosEnlazados que pasamos
                // usando el parentOption del hijo
                const parentOptions = safeInfo[String(currentParentOption)]
                if (parentOptions && typeof parentOptions === 'object') {
                  const childOptions = parentOptions[childDescription]
                  if (Array.isArray(childOptions) && childOptions.length > 0) {
                    // Primero encontrar la opción seleccionada para obtener su idUnidadEnlazada
                    const selectedOption = childOptions.find(
                      (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                    )

                    if (selectedOption) {
                      const newIdUnidadEnlazada = selectedOption.idUnidadEnlazada

                      // Buscar la opción que coincida con el nodo actual y el nuevo idUnidadEnlazada
                      const matchingOption = childOptions.find(
                        (option: any) =>
                          option.nodo === currentNode &&
                          option.idUnidadEnlazada === newIdUnidadEnlazada,
                      )

                      if (matchingOption) {
                        targetIdElementosEnlazados = matchingOption.idElementosEnlazados
                        // Si targetIdElementosEnlazadosAnt era undefined y se estableció como idElementosEnlazados inicial,
                        // actualizarlo al nuevo targetIdElementosEnlazados encontrado
                        if (targetIdElementosEnlazadosAnt === idElementosEnlazados) {
                          targetIdElementosEnlazadosAnt = targetIdElementosEnlazados
                        }
                      } else {
                        // Si no encontramos la opción, usamos el idElementosEnlazados que se pasó
                        // No hacemos continue, simplemente seguimos con el valor original
                        if (process.env.NODE_ENV === 'development') {
                          // console.warn(
                            // `No se encontró opción en ${childDescription} para nodo ${currentNode} con idUnidadEnlazada ${newIdUnidadEnlazada} en parentOption ${currentParentOption}, usando idElementosEnlazados original: ${idElementosEnlazados}`,
                          // )
                        }
                      }
                    } else {
                      // Si no encontramos la opción seleccionada, usamos el idElementosEnlazados que se pasó
                      // No hacemos continue, simplemente seguimos con el valor original
                      if (process.env.NODE_ENV === 'development') {
                        // console.warn(
                          // `No se encontró la opción seleccionada con idElementosEnlazados ${idElementosEnlazados} para ${childDescription} en parentOption ${currentParentOption}, usando idElementosEnlazados original`,
                        // )
                      }
                    }
                  }
                }
              }
              // Si currentNode o currentParentOption son undefined, simplemente continuamos
              // con el idElementosEnlazados que se pasó (no es un error, puede ser válido)
            }

            if (childDescription === 'Tirador') {
              let newIdUnidades = null

              for (const parentKey in info) {
                if (info[parentKey] && typeof info[parentKey] === 'object') {
                  for (const childKey in info[parentKey]) {
                    const childOptions = info[parentKey][childKey]
                    if (Array.isArray(childOptions)) {
                      const found = childOptions.find(
                        (item: any) => item.idElementosEnlazados === idElementosEnlazados,
                      )
                      if (found && found.idUnidadEnlazada) {
                        newIdUnidades = found.idUnidadEnlazada
                        break
                      }
                    }
                  }
                  if (newIdUnidades) break
                }
              }

              const targetIdUnidades = newIdUnidades ?? parentItem.idUnidades

              const readFreshParent = (): LinkedItem | null => {
                const item = get().items.get(parentID as AnyID)
                return item && isLinkedItem(item) ? item : null
              }

              let freshParent = readFreshParent()
              const needsLinkedOptions =
                targetIdUnidades && freshParent && !freshParent.tiradorOptions

              if (needsLinkedOptions && targetIdUnidades) {
                try {
                  const optionsPayload = await fetchLinkedUnitOptions(targetIdUnidades)
                  const patch = mapLinkedUnitOptionsResponse(optionsPayload)
                  set((s) => {
                    const item = s.items.get(parentID as AnyID)
                    if (!item || !isLinkedItem(item)) return
                    applyLinkedUnitOptionsPatch(item, patch, { preserveExisting: true })
                  })

                  freshParent = readFreshParent()
                } catch (error) {
                  // console.warn(
                    // 'Error obteniendo opciones enlazadas para unidad:',
                    // targetIdUnidades,
                    // error,
                  // )
                }
              }

              freshParent = readFreshParent()
              if (freshParent?.tiradorOptions) {
                const drawerNode = getTiradorDrawerNode(childItem)

                if (drawerNode !== undefined) {
                  targetIdElementosEnlazadosAnt = childItem.linkedID

                  const resolved = resolveTiradorIdElementosEnlazados(
                    freshParent.tiradorOptions,
                    childItem,
                    idElementosEnlazados,
                  )

                  if (resolved === null) {
                    // console.warn(
                      // `No se encontró opción en tiradorOptions para cajón ${drawerNode}`,
                    // )
                    continue
                  }
                  targetIdElementosEnlazados = resolved
                  optionIdUnidades = resolveTiradorIdUnidadEnlazada(
                    freshParent.tiradorOptions,
                    resolved,
                  )
                } else {
                  // console.warn(`Tirador ${childID} no tiene cajón (option/nodo) definido`)
                  continue
                }
              }
            }

            if (childDescription === 'Placa') {
              const placaParentItem = Array.from(state.items.values()).find((item) => {
                if (item.type === 'linkedItem' && item.childs) {
                  return Array.from(item.childs).includes(childID as AnyID)
                }
                return false
              })

              if (
                placaParentItem &&
                isLinkedItem(placaParentItem) &&
                placaParentItem.placaOptions
              ) {
                const currentNode = childItem.nodo

                if (currentNode !== undefined) {
                  targetIdElementosEnlazadosAnt = childItem.linkedID

                  const newPlacaOption = (placaParentItem.placaOptions as any[]).find(
                    (option: any) => option.idElementosEnlazados === idElementosEnlazados,
                  )

                  if (newPlacaOption) {
                    const newIdUnidadEnlazada = newPlacaOption.idUnidadEnlazada

                    const matchingOption = (placaParentItem.placaOptions as any[]).find(
                      (option: any) => {
                        return (
                          option.nodo === currentNode &&
                          option.idUnidadEnlazada === newIdUnidadEnlazada
                        )
                      },
                    )

                    if (matchingOption) {
                      targetIdElementosEnlazados = matchingOption.idElementosEnlazados

                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = matchingOption.idElementosEnlazados
                          child.idUnidades = matchingOption.idUnidadEnlazada
                        }
                      })
                    } else {
                      continue
                    }
                  } else {
                    continue
                  }
                } else {
                  continue
                }
              }
            }

            if (childDescription === 'Grifo' || childDescription?.toLowerCase().includes('grifo')) {
              const grifoParentItem = Array.from(state.items.values()).find((item) => {
                if (item.type === 'linkedItem' && item.childs) {
                  return Array.from(item.childs).includes(childID as AnyID)
                }
                return false
              })

              if (
                grifoParentItem &&
                isLinkedItem(grifoParentItem) &&
                grifoParentItem.grifoOptions
              ) {
                // Intentar obtener el nodo del grifo actual
                // El nodo puede estar en childItem.nodo, o puede ser childItem.option
                let currentNode = childItem.nodo

                // Si no está definido, intentar usar option (que suele corresponder al nodo)
                if (currentNode === undefined) {
                  const childOption = (childItem as any).option
                  if (childOption !== undefined) {
                    currentNode = childOption
                  }
                }

                // Si aún no está definido, buscar el nodo en las opciones del padre usando el linkedID actual del grifo
                if (currentNode === undefined) {
                  const currentGrifoOption = (grifoParentItem.grifoOptions as any[]).find(
                    (option: any) => option.idElementosEnlazados === childItem.linkedID,
                  )
                  if (currentGrifoOption && currentGrifoOption.nodo !== undefined) {
                    currentNode = currentGrifoOption.nodo
                  }
                }

                if (currentNode !== undefined) {
                  targetIdElementosEnlazadosAnt = childItem.linkedID

                  const newGrifoOption = (grifoParentItem.grifoOptions as any[]).find(
                    (option: any) => option.idElementosEnlazados === idElementosEnlazados,
                  )

                  if (newGrifoOption) {
                    const newIdUnidadEnlazada = newGrifoOption.idUnidadEnlazada

                    const matchingOption = (grifoParentItem.grifoOptions as any[]).find(
                      (option: any) => {
                        return (
                          option.nodo === currentNode &&
                          option.idUnidadEnlazada === newIdUnidadEnlazada
                        )
                      },
                    )

                    if (matchingOption) {
                      targetIdElementosEnlazados = matchingOption.idElementosEnlazados

                      set((s) => {
                        const child = s.items.get(childID as AnyID)
                        if (child && isLinkedSon(child)) {
                          child.linkedID = matchingOption.idElementosEnlazados
                          child.idUnidades = matchingOption.idUnidadEnlazada
                        }
                      })
                    } else {
                      // console.warn(
                        // `⚠️ [UPDATE GLB FOR GROUP] No se encontró opción en grifoOptions para nodo ${currentNode} con idUnidadEnlazada ${newIdUnidadEnlazada}, usando idElementosEnlazados pasado como fallback`,
                        // {
                          // childID,
                          // currentNode,
                          // newIdUnidadEnlazada,
                          // idElementosEnlazadosPasado: idElementosEnlazados,
                        // },
                      // )
                      // Usar el idElementosEnlazados pasado como fallback en lugar de hacer continue
                      targetIdElementosEnlazados = idElementosEnlazados
                    }
                  } else {
                    // console.warn(
                      // `⚠️ [UPDATE GLB FOR GROUP] No se encontró el nuevo grifo con idElementosEnlazados ${idElementosEnlazados}, usando como fallback`,
                      // {
                        // childID,
                        // idElementosEnlazados,
                      // },
                    // )
                    // Usar el idElementosEnlazados pasado como fallback en lugar de hacer continue
                    targetIdElementosEnlazados = idElementosEnlazados
                  }
                } else {
                  // console.warn(
                    // `⚠️ [UPDATE GLB FOR GROUP] Grifo ${childID} no tiene nodo definido, usando idElementosEnlazados pasado como fallback`,
                    // {
                      // childID,
                      // idElementosEnlazados,
                    // },
                  // )
                  // Usar el idElementosEnlazados pasado como fallback en lugar de hacer continue
                  targetIdElementosEnlazados = idElementosEnlazados
                }
              } else {
                // console.warn(
                  // `⚠️ [UPDATE GLB FOR GROUP] No se encontró la unidad padre para el grifo ${childID}, usando idElementosEnlazados pasado como fallback`,
                  // {
                    // childID,
                    // idElementosEnlazados,
                  // },
                // )
                // Usar el idElementosEnlazados pasado como fallback en lugar de hacer continue
                targetIdElementosEnlazados = idElementosEnlazados
              }
            }

            let response
            try {
              let finalIdAnt = targetIdElementosEnlazadosAnt
              if (
                (childDescription === 'Micro' ||
                  childDescription === 'Horno' ||
                  childDescription === 'HornoMicro') &&
                (targetIdElementosEnlazadosAnt === undefined ||
                  targetIdElementosEnlazadosAnt === null)
              ) {
                finalIdAnt = targetIdElementosEnlazados
              } else if (
                targetIdElementosEnlazadosAnt === undefined ||
                targetIdElementosEnlazadosAnt === null
              ) {
                // Para otros tipos, usar el linkedID del hijo si existe, o calcular como antes
                const idAntiguo =
                  targetIdElementosEnlazadosAnt !== undefined
                    ? targetIdElementosEnlazadosAnt
                    : parentItem.idUnidades || targetIdElementosEnlazados

                const idNuevo = targetIdElementosEnlazados

                finalIdAnt =
                  idAntiguo === idNuevo || targetIdElementosEnlazadosAnt === undefined
                    ? idNuevo
                    : idAntiguo
              }

              response = await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
                finalIdAnt,
                targetIdElementosEnlazados,
              )
            } catch (error) {
              console.error('[updateLinkedGLBForGroup] Error en llamada API:', error)
              continue
            }

            if (!response || !response.data || !response.data.unidad) {
              continue
            }
            const newLinkedSonInfo = response.data
            if (!newLinkedSonInfo.unidad) {
              continue
            }
            const glbRef = newLinkedSonInfo.unidad.referenciaBlender
            if (!glbRef) {
              continue
            }
            const glb = await loadGlbCached(glbRef)
            if (!newLinkedSonInfo.unidad.idUnidades) {
              continue
            }
            const extraInfo = await API().api.acabadosGetUnidadAcabadosInfo(
              newLinkedSonInfo.unidad.idUnidades,
            )

            const backGLBInfo = {
              idUnidades: newLinkedSonInfo.unidad?.idUnidades ?? 0,
              linkedID: newLinkedSonInfo.unidad?.idUnidades ?? 0,
              active: true,
              codigo: newLinkedSonInfo.unidad?.codigo ?? '',
              referenciaBlender: newLinkedSonInfo.unidad?.referenciaBlender ?? '',
              elemento: newLinkedSonInfo.unidad?.elemento,
              descripcion: newLinkedSonInfo.unidad?.descripcion,
              descripcionLarga: newLinkedSonInfo.unidad?.descripcionLarga,
              ancho: newLinkedSonInfo.unidad?.ancho,
              fondo: newLinkedSonInfo.unidad?.fondo,
              alto: newLinkedSonInfo.unidad?.alto,
              mano: newLinkedSonInfo.unidad?.mano,
              altura: newLinkedSonInfo.unidad?.altura,
              alturaHaciaAbajo: newLinkedSonInfo.unidad?.alturaHaciaAbajo,
              colision: newLinkedSonInfo.unidad?.colision,
              cota: newLinkedSonInfo.unidad?.cota,
              tipoNormalizado: newLinkedSonInfo.unidad?.tipoNormalizado ?? 'MBJ',
              tipoUnidNorm: newLinkedSonInfo.unidad?.tipoUnidNorm ?? '',
              modificarAncho: newLinkedSonInfo.unidad?.modificarAncho,
              modificarFondo: newLinkedSonInfo.unidad?.modificarFondo,
              modificarAlto: newLinkedSonInfo.unidad?.modificarAlto,
              morphs: newLinkedSonInfo.unidad?.morphs ?? [],
              precio: newLinkedSonInfo.unidad?.precio,
              acabados: newLinkedSonInfo.unidad?.acabados ?? [],
              unidadesLuces: newLinkedSonInfo.unidad?.unidadesLuces ?? [],
              elementosEnlazados: newLinkedSonInfo.unidad?.elementosEnlazados ?? [],
              referenciaJpgpng: newLinkedSonInfo.unidad?.referenciaJpgpng,
              universal: newLinkedSonInfo.unidad?.universal,
            } as unknown as BackGLBInfo

            const processedInfo = newGetDataFromGLB({
              glbData: glb,
              backGLBInfo,
              id: childID,
              type: 'linkedSon',
              catalogID: newLinkedSonInfo.unidad.codigo ?? '',
              extraInfo: {
                zonas: Array.isArray(extraInfo.data?.zonas)
                  ? extraInfo.data.zonas.map((z) => ({
                      idAcabadoUnidad: z.idAcabadoUnidad,
                      zona: z.zona ?? '',
                    }))
                  : [],
              },
              itemInfo: {},
            })
            set((s) => {
              const currElement = s.items.get(childID as AnyID) as LinkedItemSon
              if (!isLinkedSon(currElement)) {
                return
              }

              const prevPosition = { x: currElement.x, y: currElement.y, z: currElement.z }
              const prevSize = {
                width: currElement.width,
                height: currElement.height,
                thickness: currElement.thickness,
              }
              const prevMorphs = currElement.morphs

              let newX = prevPosition.x
              let newY = prevPosition.y
              let newZ = prevPosition.z

              if (parentItem) {
                const relativeX = prevPosition.x - parentItem.x
                const relativeY = prevPosition.y - parentItem.y
                const relativeZ = prevPosition.z - parentItem.z

                newX = parentItem.x + relativeX
                newY = parentItem.y + relativeY
                newZ = parentItem.z + relativeZ
              }
              if (!newLinkedSonInfo.unidad) {
                return
              }

              let finalX = newX
              let finalY = newY
              let finalZ = newZ

              // Para Grifo, TPCGrifo, Fregadero, Placa y tipos genéricos (Micro, Horno, HornoMicro, etc.),
              // usar posiciones del backend si están disponibles
              // Cada grifo/fregadero tiene su propia posición específica del backend según la unidad padre
              if (
                (currElement.typeOfSon === 'Grifo' ||
                  currElement.typeOfSon === 'TPCGrifo' ||
                  currElement.typeOfSon === 'Fregadero' ||
                  currElement.typeOfSon === 'Placa' ||
                  currElement.typeOfSon === 'Micro' ||
                  currElement.typeOfSon === 'Horno' ||
                  currElement.typeOfSon === 'HornoMicro') &&
                newLinkedSonInfo
              ) {
                const posicionBackendX = newLinkedSonInfo.posicionX ?? 0
                const posicionBackendY = newLinkedSonInfo.posicionY ?? 0
                const posicionBackendZ = newLinkedSonInfo.posicionZ ?? 0
                const coordsValid =
                  !isNaN(posicionBackendX) &&
                  !isNaN(posicionBackendY) &&
                  !isNaN(posicionBackendZ)

                if (coordsValid) {
                  finalX = posicionBackendX
                  finalY = posicionBackendY
                  finalZ = posicionBackendZ
                  ;(currElement as { posicionX?: number }).posicionX = posicionBackendX
                  ;(currElement as { posicionY?: number }).posicionY = posicionBackendY
                  ;(currElement as { posicionZ?: number }).posicionZ = posicionBackendZ

                  if (
                    currElement.typeOfSon === 'Grifo' ||
                    currElement.typeOfSon === 'TPCGrifo' ||
                    currElement.typeOfSon === 'Fregadero' ||
                    currElement.typeOfSon === 'Placa'
                  ) {
                    ;(currElement as any).baseY = posicionBackendY
                  }
                } else {
                  if (isNaN(finalX) || finalX === undefined) finalX = prevPosition.x || 0
                  if (isNaN(finalY) || finalY === undefined) finalY = prevPosition.y || 0
                  if (isNaN(finalZ) || finalZ === undefined) finalZ = prevPosition.z || 0
                }
              } else {
                // Para otros tipos, asegurarse de que las posiciones no sean NaN
                if (isNaN(finalX) || finalX === undefined) finalX = prevPosition.x || 0
                if (isNaN(finalY) || finalY === undefined) finalY = prevPosition.y || 0
                if (isNaN(finalZ) || finalZ === undefined) finalZ = prevPosition.z || 0
              }

              Object.assign(currElement, {
                linkedID: targetIdElementosEnlazados,
                idElementosEnlazados: targetIdElementosEnlazados,
                catalogID: newLinkedSonInfo.unidad.codigo,
                code: newLinkedSonInfo.unidad.codigo,
                idUnidades:
                  optionIdUnidades ?? newLinkedSonInfo.unidad.idUnidades ?? 0,
                textures: processedInfo.textures,
                zones: processedInfo.zones,
                glbPath: String(newLinkedSonInfo.unidad.referenciaBlender ?? ''),
                referenciaBlender: String(newLinkedSonInfo.unidad.referenciaBlender ?? ''),
                x: finalX,
                y: finalY,
                z: finalZ,
                posicionX: (currElement as { posicionX?: number }).posicionX,
                posicionY: (currElement as { posicionY?: number }).posicionY,
                posicionZ: (currElement as { posicionZ?: number }).posicionZ,
                long:
                  newLinkedSonInfo.unidad.descripcionLarga ||
                  newLinkedSonInfo.unidad.descripcion ||
                  newLinkedSonInfo.unidad.codigo ||
                  currElement.long,
                width: prevSize.width,
                height: prevSize.height,
                thickness: prevSize.thickness,
                morphs: prevMorphs,
              })
              if (isPlacaLinkedSon(currElement) && newLinkedSonInfo.unidad) {
                const ancho = Number(newLinkedSonInfo.unidad.ancho)
                const alto = Number(
                  newLinkedSonInfo.unidad.alto ??
                    (newLinkedSonInfo.unidad as { altura?: number }).altura,
                )
                const fondo = Number(newLinkedSonInfo.unidad.fondo)
                if (Number.isFinite(ancho) && ancho > 0) currElement.width = ancho
                if (Number.isFinite(alto) && alto > 0) currElement.height = alto
                if (Number.isFinite(fondo) && fondo > 0) currElement.thickness = fondo
                if (processedInfo.glbSize) currElement.glbSize = processedInfo.glbSize
              }
              if (
                newLinkedSonInfo.unidad.codigo !== undefined &&
                newLinkedSonInfo.unidad.codigo !== null
              ) {
                setSceneGlbForCatalog(
                  s.sceneGLB as never,
                  newLinkedSonInfo.unidad.codigo,
                  { glb, backGLBInfo: newLinkedSonInfo.unidad },
                  newLinkedSonInfo.unidad.idUnidades,
                )
              }
              currElement.typeOfSon =
                currElement.typeOfSon ||
                childItem.typeOfSon ||
                childItem.description ||
                'Desconocido'

              if (isApplianceLinkedSonType(currElement) && isLinkedItem(parentItem)) {
                applyLinkedApplianceLocalTransformFromApi(currElement, parentItem)
              }

              if (currElement.description !== 'Tirador') {
                const x = currElement.x ?? 0
                const y = currElement.y ?? 0
                const z = currElement.z ?? 0
                currElement.visible = !(x === 0 && y === 0 && z === 0)
              }

              if (currElement.description === 'Tirador' && newLinkedSonInfo) {
                const posicionesAlternativas = [
                  'biPosicionX',
                  'biPosicionY',
                  'biPosicionZ',
                  'bcPosicionX',
                  'bcPosicionY',
                  'bcPosicionZ',
                  'bdPosicionX',
                  'bdPosicionY',
                  'bdPosicionZ',
                  'aiPosicionX',
                  'aiPosicionY',
                  'aiPosicionZ',
                  'acPosicionX',
                  'acPosicionY',
                  'acPosicionZ',
                  'adPosicionX',
                  'adPosicionY',
                  'adPosicionZ',
                  'biRotacionX',
                  'biRotacionY',
                  'biRotacionZ',
                  'bcRotacionX',
                  'bcRotacionY',
                  'bcRotacionZ',
                  'bdRotacionX',
                  'bdRotacionY',
                  'bdRotacionZ',
                  'aiRotacionX',
                  'aiRotacionY',
                  'aiRotacionZ',
                  'acRotacionX',
                  'acRotacionY',
                  'acRotacionZ',
                  'adRotacionX',
                  'adRotacionY',
                  'adRotacionZ',
                  'b2idRotacionX',
                  'b2idRotacionY',
                  'b2idRotacionZ',
                  'a2idRotacionX',
                  'a2idRotacionY',
                  'a2idRotacionZ',
                ]
                posicionesAlternativas.forEach((key) => {
                  if (Object.prototype.hasOwnProperty.call(newLinkedSonInfo, key)) {
                    // @ts-expect-error: Puede que no esté tipado en el DTO, pero lo copiamos si existe
                    currElement[key] = newLinkedSonInfo[key]
                  }
                })
                if (newLinkedSonInfo.unidad?.morphs)
                  currElement.morphs = mapMorphsFromDTO(newLinkedSonInfo.unidad.morphs)

                const drawerNode = getTiradorDrawerNode(childItem)
                if (drawerNode !== undefined) {
                  currElement.nodo = drawerNode
                  currElement.option = drawerNode
                }
                currElement.parentOption = childItem.parentOption

                const parentForPosition = s.items.get(parentID as AnyID)
                const nuevaPos = calculateTiradorPosition(
                  currElement,
                  parentForPosition && isLinkedItem(parentForPosition)
                    ? parentForPosition
                    : parentItem,
                )
                currElement.x = nuevaPos.x
                currElement.y = nuevaPos.y
                currElement.z = nuevaPos.z

                currElement.visible = !(
                  currElement.x === 0 &&
                  currElement.y === 0 &&
                  currElement.z === 0
                )
                get().setLinkedHandPosition(
                  currElement.id as LinkedItemID,
                  currElement.handPosition,
                )
              }
            })

            if (isFregaderoMatch || childItem.typeOfSon === 'Fregadero') {
              setTimeout(() => {
                const positionFregaderoGrifoPlaca =
                  get().positionFregaderoGrifoPlacaAccordingToEncimera
                if (positionFregaderoGrifoPlaca) {
                  positionFregaderoGrifoPlaca(childID)
                }
              }, 0)
            }
            if (isPlacaLinkedSon(childItem) || childDescription === 'Placa') {
              schedulePlacaAccordingToEncimera(get, childID)
            }

            processedCount++
            if (processedCount >= BATCH_SIZE) {
              processedCount = 0

              await new Promise((resolve) => setTimeout(resolve, 0))
            }
          } catch (error) {
            console.error('[updateLinkedGLBForGroup] Error actualizando GLB para', childID, error)
          }
        }
      }
    } catch (error) {
      if (!batchActive) {
        set((s) => {
          s.frameloop = originalFrameloop
        })
      }
      throw error
    } finally {
      if (!batchActive) {
        setTimeout(() => {
          set((s) => {
            s.frameloop = originalFrameloop
          })
        }, 100)
      }
    }
  },

  setLinkedVisible: (linkedID: string, visible: boolean) => {
    // This will probably never be used but it also exists for items
  },
  editLinkedZone: (linkedID: string, zone: string) => {
    // This will be a mirror of item, so we either use the original (unless textures changes significantly)
  },
  editLinkedMatrix: (linkedID: string, matrix: number[]) => {
    // This will probably never be used but internally
  },
  // GENERAL PARENT MANIPULATION:
  editSelectedOption: (parentID: string, option: string) => {
    // This will generally be used to swap between options when selecting a new option. Usually will come at the same time than updateLinkedGLB
  },
  selectLinkedParent: (linkedID: string) => {
    // This is kinda trivial since every single item has a reference to their father, so:
    set((store) => {
      store.layers[store.currentLayer].selected.clear()
      const item = store.items.get(linkedID as AnyID)
      if (item && isLinkedSon(item)) {
        store.layers[store.currentLayer].selected.add((item as LinkedItemSon).firstParentID)
      }
    })
  },
  applyAllZonesAndChildrenToAllLinkedItems: (id: string, typeOfSon?: string, linkedID?: number) => {
    set((state) => {
      const originalItem = state.items.get(id as AnyID)
      if (!originalItem) return
      if (!isLinkedItem(originalItem)) return
      if (!originalItem.zones) return
      state.items.forEach((targetItem, key) => {
        if (!isLinkedItem(targetItem)) return
        targetItem.childrenOrder.forEach((el) => {
          const childId = el.split('!')[1]
          const child = state.items.get(childId as AnyID)
          if (!child || !isLinkedSon(child)) return
          if (
            (typeOfSon && child.typeOfSon !== typeOfSon) ||
            (linkedID && child.linkedID !== linkedID)
          )
            return
        })
      })
    })
  },
  applyToAllItemsAndChildrens: (id: string) => {
    set((state) => {
      const item = state.items.get(id as AnyID)
      // const parentID =
      //   id.startsWith('linkedSon_') && isLinkedSon(item)
      //     ? ((item as LinkedItemSon).firstParentID ?? '')
      //     : id
      // const originalItem = state.items.get(parentID as AnyID)
      const parenItem = state.items.get(id as AnyID)
      let finalParenItem = parenItem
      if (item?.type === 'linkedSon' && isLinkedSon(item)) {
        finalParenItem = state.items.get((item as LinkedItemSon).firstParentID as AnyID)
      }

      if (!isLinkedItem(finalParenItem)) {
        return
      }
      const childrensID = finalParenItem.childrenOrder.map((childId) => {
        return state.items.get(childId.split('!')[1] as AnyID)
      })
      const diccionaryChildren: Record<string, any> = {}
      childrensID.forEach((e) => {
        if (!isLinkedSon(e)) return
        if (!e.description) return
        if (!(e.description in diccionaryChildren)) {
          diccionaryChildren[e?.description] = e
        }
      })
      state.items.forEach((e) => {
        if (!isLinkedSon(e)) return
        if (
          e.description === 'Puerta' ||
          (e.description && e.description.toLowerCase().includes('encimera'))
        ) {
          if (diccionaryChildren[e.description]) {
            const propertys = diccionaryChildren[e.description]

            const isFregadero =
              e.description?.toLowerCase().includes('fregadero') ||
              (e as any).typeOfSon === 'Fregadero'
            const isEncimera = propertys.description?.toLowerCase().includes('encimera')

            if (isFregadero && isEncimera) {
              e.textures = [...propertys.textures]
              e.zones = { ...propertys.zones }
            } else {
              e.textures = [...propertys.textures]
              e.zones = { ...propertys.zones }
            }
          }
        }
      })
    })
  },
  applyAllZonesAndChildrenOfLinkedToAnotherLinkedWithTheSameName: (id: string) => {
    set((state) => {
      let parentID = id
      if (id.startsWith('linkedSon_')) {
        const item = state.items.get(id as AnyID)
        if (item && isLinkedSon(item)) {
          parentID = (item as LinkedItemSon).firstParentID ?? ''
        }
      }
      const originalItem = state.items.get(parentID as AnyID)
      if (!originalItem) {
        return
      }
      if (!isLinkedItem(originalItem)) {
        return
      }
      if (!originalItem.zones) {
        return
      }
      const zonesToCopy = Object.values(originalItem.zones)
      const childrenToCopy = originalItem.childrenOrder.map((el) =>
        state.items.get(el.split('!')[1] as AnyID),
      ) as LinkedItemSon[]
      if (!zonesToCopy.length && !childrenToCopy.length) {
        return
      }
      state.items.forEach((targetItem, key) => {
        if (key === originalItem.id) {
          return
        }
        if (!isLinkedItem(targetItem)) {
          return
        }
        if (targetItem.catalogID !== originalItem.catalogID) {
          return
        }
        if (targetItem.textures) {
          targetItem.textures = [...originalItem.textures]
        }
        zonesToCopy.forEach((zone) => {
          const zoneKey = Object.keys(targetItem.zones).find(
            (k) => targetItem.zones[k].name === zone.name,
          )
          if (zoneKey) {
            const newMaterial = { ...zone.material }
            targetItem.zones[zoneKey].material = newMaterial
            if (zone.material) {
              targetItem.zones[zoneKey].material.color = zone.material.color
              targetItem.zones[zoneKey].material.groupID = zone.material.groupID
              targetItem.zones[zoneKey].material.searchID = zone.material.searchID
              targetItem.zones[zoneKey].material.id = zone.material.id
              targetItem.zones[zoneKey].material.description = zone.material.description
            }
          }
        })
        targetItem.childrenOrder.map((el) => state.items.delete(el.split('!')[1] as AnyID))
        const newChildrenIDArr = childrenToCopy.map((el) => {
          const id = getId('linkedSon')
          return [el.id, id]
        })
        newChildrenIDArr.forEach(([oldId, newId], i) => {
          const childs: string[] = []
          const thisPreviousObject = childrenToCopy[i]
          thisPreviousObject.childs.forEach((el) => {
            const indexOfChild = childrenToCopy.findIndex((ele) => ele?.id === el)
            if (indexOfChild === -1) return
            childs.push(newChildrenIDArr[indexOfChild][1])
          })
          state.items.set(newId, {
            ...childrenToCopy[i],
            firstParentID: targetItem.id,
            childs: new Set(childs),
            id: newId,
          })
        })
        const newChildrenOrder = originalItem.childrenOrder.map((el, i) => {
          return `${el.split('!')[0]}!${newChildrenIDArr[i][1]}`
        })
        state.items.get(key as AnyID).childrenOrder = newChildrenOrder
        targetItem.childrenOrder = newChildrenOrder
        const parentChilds: string[] = []
        originalItem.childs.forEach((el, i) => {
          const indexOfChild = childrenToCopy.findIndex((ele) => ele?.id === el)
          if (indexOfChild === -1) return
          parentChilds.push(newChildrenIDArr[indexOfChild][1])
        })
        const setto: Set<`linkedSon_${string}`> = new Set(parentChilds as `linkedSon_${string}`[])
        state.items.get(key as AnyID).childs = setto
        targetItem.childs = setto
        state.items.get(key as AnyID).orderMap = originalItem.orderMap
        targetItem.orderMap = originalItem.orderMap
      })
    })
  },
  applyTheSelectedoZoneToAllLinkedZonesWithSameName: (id: LinkedItemsID, zoneName: string) => {
    set((s) => {
      const item = s.items.get(id)
      if (!isLinked(item)) {
        return
      }
      if (!item?.textures) {
        return
      }
      const texture = item.textures.find((el) => {
        return el.zone === zoneName || el.zoneName === zoneName || el.zoneDisplayName === zoneName
      })
      if (!texture) {
        return
      }
      const zoneDisplayName = texture.zoneDisplayName
      const zoneKey = Object.keys(item.zones).find(
        (key) => item.zones[key].name === texture.zoneName,
      )
      if (!zoneKey) {
        return
      }
      const zone = item.zones[zoneKey] as Item['zones'][string]
      const material = zone?.material as Material
      if (!zone || !zone.material || !material) {
        return
      }
      item.textures.forEach((el) => {
        if (el.zoneDisplayName === zoneDisplayName && el.zoneName !== texture.zoneName) {
          const targetZoneKey = Object.keys(item.zones).find(
            (k) => item.zones[k].name === el.zoneName,
          )
          if (targetZoneKey) {
            item.zones[targetZoneKey].material = { ...material }
            el.acabado = texture.acabado
            el.linkedTextureID = texture.linkedTextureID
            el.name = texture.name
            el.searchID = texture.searchID
            el.url = texture.url
            el.groupID = texture.groupID
          }
        }
      })
      s.items.forEach((value, key) => {
        if (!key.startsWith('linkedItem_') && !key.startsWith('linkedSon_')) {
          return
        }
        if (!isLinked(value)) {
          return
        }

        if (!value.textures || !Array.isArray(value.textures)) {
          return
        }

        value.textures.forEach((el) => {
          if (!value.zones) {
            return
          }

          const targetZoneKey = Object.keys(value.zones).find(
            (k) => value.zones[k].name === el.zoneName,
          )

          if (value.id === id && el.zoneName === texture.zoneName) {
            return
          }

          const isDecoItem =
            (typeof item.catalogID === 'string' && item.catalogID.includes('DECO')) ||
            (typeof item.referencia === 'string' && item.referencia.includes('DECO'))
          const isTargetDecoItem =
            (typeof value.catalogID === 'string' && value.catalogID.includes('DECO')) ||
            (typeof value.referencia === 'string' && value.referencia.includes('DECO'))

          const isDecoArmazon =
            isDecoItem &&
            texture.zoneDisplayName === 'Armazon' &&
            texture.modelo === 'AcabadoModelo'
          const isTargetPuertaAcabadoModelo =
            el.zoneDisplayName === 'Puerta' && el.modelo === 'AcabadoModelo'

          const isPuertaAcabadoModelo =
            texture.zoneDisplayName === 'Puerta' && texture.modelo === 'AcabadoModelo'
          const isTargetDecoArmazon =
            isTargetDecoItem && el.zoneDisplayName === 'Armazon' && el.modelo === 'AcabadoModelo'

          const shouldApplyDecoMapping =
            (isDecoArmazon && isTargetPuertaAcabadoModelo) ||
            (isPuertaAcabadoModelo && isTargetDecoArmazon)

          const isSameZone = el.zoneDisplayName === zoneDisplayName
          const isDecoMapping = shouldApplyDecoMapping
          const isLateralCostadoFromPuerta =
            isPuertaAcabadoModelo &&
            isLateralOrCostadoLinkedCatalog(value) &&
            el.zoneDisplayName === 'Costado' &&
            el.modelo === 'AcabadoModelo'
          const isRegletaPuertaFromPuerta =
            isPuertaAcabadoModelo &&
            isRegletaPuertaLinkedCatalog(value) &&
            el.zoneDisplayName === 'Puerta' &&
            el.modelo === 'AcabadoModelo'

          if (
            (isSameZone || isDecoMapping || isLateralCostadoFromPuerta || isRegletaPuertaFromPuerta) &&
            targetZoneKey
          ) {
            const zoneMaterial = value.zones[targetZoneKey]?.material as Material
            if (!zoneMaterial) {
              return
            }

            const shouldApplyTexture =
              (el.zoneDisplayName === texture.zoneDisplayName && el.modelo === texture.modelo) ||
              isLateralCostadoFromPuerta ||
              isRegletaPuertaFromPuerta

            const isFregadero =
              value.description?.toLowerCase().includes('fregadero') ||
              (value as any).typeOfSon === 'Fregadero'
            const isEncimera = texture.zoneDisplayName?.toLowerCase().includes('encimera')

            if (isFregadero && isEncimera) {
              el.acabado = texture.acabado
              el.linkedTextureID = texture.linkedTextureID
              el.name = texture.name
              el.searchID = texture.searchID
              el.url = texture.url
              el.groupID = texture.groupID
            } else if (shouldApplyDecoMapping) {
              replaceTextureEntryFromInheritedSource(
                el as Record<string, unknown>,
                material as Record<string, unknown>,
              )
              value.zones[targetZoneKey].material = replaceZoneMaterialFromInheritedSource(
                el as Record<string, unknown>,
                zoneMaterial as Record<string, unknown>,
                material as Record<string, unknown>,
              ) as Material
              el.acabado = texture.acabado
              el.name = texture.name
              el.searchID = texture.searchID
              el.url = texture.url
              el.groupID = texture.groupID
            } else if (shouldApplyTexture) {
              replaceTextureEntryFromInheritedSource(
                el as Record<string, unknown>,
                material as Record<string, unknown>,
              )
              value.zones[targetZoneKey].material = (
                isLateralCostadoFromPuerta || isRegletaPuertaFromPuerta
                  ? replaceZoneMaterialFromInheritedSource(
                      el as Record<string, unknown>,
                      zoneMaterial as Record<string, unknown>,
                      material as Record<string, unknown>,
                    )
                  : applyMaterialToZoneMaterial(
                      zoneMaterial as Record<string, unknown>,
                      material as Record<string, unknown>,
                    )
              ) as Material
              if (!isLateralCostadoFromPuerta && !isRegletaPuertaFromPuerta) {
                applyMaterialToTextureEntry(
                  el as Record<string, unknown>,
                  pickTextureEntryFields(material as Record<string, unknown>),
                )
              }
              el.acabado = texture.acabado
              el.linkedTextureID = texture.linkedTextureID
              el.name = texture.name
              el.searchID = texture.searchID
              el.url = texture.url
              el.groupID = texture.groupID
            }
          }
        })
      })
    })
  },
  applyTheSelectedZoneToSpecificLinkedZones: (
    id: LinkedItemsID,
    zoneName: string,
    selectedIds: Set<LinkedItemsID>,
  ) => {
    set((s) => {
      const item = s.items.get(id)
      if (!isLinked(item)) return
      if (!item?.textures) return

      const texture = item.textures.find((el) => {
        return el.zone === zoneName || el.zoneName === zoneName || el.zoneDisplayName === zoneName
      })

      if (!texture) return

      const zoneDisplayName = texture.zoneDisplayName
      const zoneKey = Object.keys(item.zones).find(
        (key) => item.zones[key].name === texture.zoneName,
      )

      if (!zoneKey) return

      const zone = item.zones[zoneKey] as Item['zones'][string]
      const material = zone?.material as Material

      if (!zone || !zone.material || !material) return

      selectedIds.forEach((selectedId) => {
        const selectedItem = s.items.get(selectedId)
        if (!selectedItem || !isLinked(selectedItem)) return

        if (selectedItem.tipoNormalizado === 'MAL') {
          const puertaTexture = selectedItem.textures.find((t) => t.zoneDisplayName === 'Puerta')
          const itemModelo = puertaTexture?.modelo || ''

          let targetModelo = ''
          if (s.currentObjectID) {
            const currentItem = s.items.get(s.currentObjectID as LinkedItemsID)
            if (currentItem && isLinked(currentItem) && currentItem.tipoNormalizado === 'MAL') {
              const currentTexture = currentItem.textures.find(
                (t) => t.zoneDisplayName === 'Puerta',
              )
              targetModelo = currentTexture?.modelo || ''
            }
          }

          if (!targetModelo) {
            const refItem = s.items.get(id)
            if (refItem && isLinked(refItem)) {
              const refTexture = refItem.textures.find((t) => t.zoneDisplayName === 'Puerta')
              targetModelo = refTexture?.modelo || ''
            }
          }

          if (itemModelo !== targetModelo) {
            return
          }
        }

        selectedItem.textures.forEach((el) => {
          const targetZoneKey = Object.keys(selectedItem.zones).find(
            (k) => selectedItem.zones[k].name === el.zoneName,
          )

          if (
            selectedItem.id === id &&
            !selectedIds.has(selectedItem.id) &&
            el.zoneName === texture.zoneName
          ) {
            return
          }

          if (el.zoneDisplayName === zoneDisplayName && targetZoneKey) {
            const zoneMaterial = selectedItem.zones[targetZoneKey]?.material as Material
            if (!zoneMaterial) return
            const originalModelo = el.modelo
            applyMaterialToTextureEntry(
              el as Record<string, unknown>,
              material as Record<string, unknown>,
            )
            el.modelo = originalModelo
            selectedItem.zones[targetZoneKey].material = applyMaterialToZoneMaterial(
              zoneMaterial as Record<string, unknown>,
              material as Record<string, unknown>,
            ) as Material
          }
        })
        bumpLinkedItemMaterialEpoch(selectedItem)
      })
      s.shouldUpdateCamera = true
      s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
    })
  },
  editLinkedElementTexture: (
    linkedID: LinkedItemsID,
    material: Material,
    modeEditor: 'Single' | 'All' | 'Selected' | 'MBJ' | 'MAL' | 'COL',
    selectedIds?: Set<LinkedItemsID>,
  ) => {
    let newTexture: Material = material
    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinked(item)) return

      if (!item.textures || item.textures.length === 0) {
        return
      }

      const texture = item.textures[0]
      const originalZoneName = texture.zoneName
      const originalZoneDisplayName = texture.zoneDisplayName
      const originalZone = texture.zone

      applyMaterialToTextureEntry(
        texture as Record<string, unknown>,
        material as Record<string, unknown>,
      )
      texture.zoneName = originalZoneName
      texture.zoneDisplayName = originalZoneDisplayName
      texture.zone = originalZone

      newTexture = { ...texture }
      bumpLinkedItemMaterialEpoch(item)

      if (isEncimeraLinkedSonItem(item)) {
        applyEncimeraThicknessMm(item)
        scheduleAppliancesAccordingToEncimera(get, linkedID)
      }

      if (
        item.description?.toLowerCase().includes('fregadero') ||
        (item as any).typeOfSon === 'Fregadero'
      ) {
        const positionFregaderoGrifoPlaca = get().positionFregaderoGrifoPlacaAccordingToEncimera
        if (positionFregaderoGrifoPlaca) {
          positionFregaderoGrifoPlaca(linkedID)
        } else {
          console.error('❌ positionFregaderoGrifoPlacaAccordingToEncimera no encontrada')
        }
      }

      if (item.zones && Object.keys(item.zones).length > 0) {
        const firstZoneKey = Object.keys(item.zones)[0]
        const firstZone = item.zones[firstZoneKey]
        if (firstZone && firstZone.material) {
          firstZone.material = { ...firstZone.material, ...material }
        }
      }

      if (modeEditor === 'All') {
        // Determinar si el cambio se está haciendo desde un padre (linkedItem) o desde un hijo (linkedSon)
        const isChangingFromParent = !linkedID.startsWith('linkedSon_')
        // Determinar si la fuente del cambio es una encimera (ya sea linkedItem o linkedSon)
        const sourceIsEncimera = isEncimeraLinkedSonItem(item)

        s.items.forEach((targetItem, key) => {
          if (!key.startsWith('linkedItem_') && !key.startsWith('linkedSon_')) return
          if (!isLinked(targetItem)) return
          if (targetItem.id === linkedID) return

          // NO aplicar cambios a linkedSon cuando se cambia desde el padre,
          // EXCEPTO cuando la fuente es una encimera (para que las encimeras se propaguen entre sí)
          if (isChangingFromParent && !sourceIsEncimera && targetItem.type === 'linkedSon') {
            return
          }

          const matchesAllModeTarget = sourceIsEncimera
            ? isEncimeraLinkedSonItem(targetItem) // Propagar a CUALQUIER tipo de encimera (linkedItem y linkedSon)
            : targetItem.description === item.description

          if (matchesAllModeTarget) {
            if (targetItem.textures && targetItem.textures.length > 0) {
              const targetTexture = targetItem.textures[0]

              const originalZoneName = targetTexture.zoneName
              const originalZoneDisplayName = targetTexture.zoneDisplayName
              const originalZone = targetTexture.zone
              const originalLinkedTextureID = targetTexture.linkedTextureID
              const originalIdAcabado = targetTexture.idAcabadoUnidadModeloAcabado

              Object.assign(targetTexture, newTexture)
              bumpLinkedItemMaterialEpoch(targetItem)

              targetTexture.zoneName = originalZoneName
              targetTexture.zoneDisplayName = originalZoneDisplayName
              targetTexture.zone = originalZone
              targetTexture.linkedTextureID = originalLinkedTextureID
              targetTexture.idAcabadoUnidadModeloAcabado = originalIdAcabado

              if (sourceIsEncimera && isEncimeraLinkedSonItem(targetItem)) {
                applyEncimeraThicknessMm(targetItem)
                scheduleAppliancesAccordingToEncimera(get, key)
              }

              if (
                targetItem.description?.toLowerCase().includes('fregadero') ||
                targetItem.typeOfSon === 'Fregadero'
              ) {
                // NO ajustar posiciones de linkedSon cuando se cambia desde un padre no-encimera
                if (!isChangingFromParent || sourceIsEncimera || targetItem.type !== 'linkedSon') {
                  const positionFregaderoGrifoPlaca =
                    get().positionFregaderoGrifoPlacaAccordingToEncimera
                  if (positionFregaderoGrifoPlaca) {
                    positionFregaderoGrifoPlaca(key)
                  } else {
                    console.error(
                      '❌ positionFregaderoGrifoPlacaAccordingToEncimera no encontrada en ALL mode',
                    )
                  }
                }
              }

              if (targetItem.zones && Object.keys(targetItem.zones).length > 0) {
                const targetZoneKey = Object.keys(targetItem.zones)[0]
                const targetZone = targetItem.zones[targetZoneKey]
                if (targetZone && targetZone.material) {
                  targetZone.material = { ...targetZone.material, ...material }
                }
              }
            }
          }
        })
      } else if (modeEditor === 'Selected' && selectedIds) {
        selectedIds.forEach((selectedId) => {
          if (selectedId === linkedID) return
          const selectedItem = s.items.get(selectedId)
          if (!selectedItem || !isLinked(selectedItem)) return

          if (selectedItem.textures && selectedItem.textures.length > 0) {
            const targetTexture = selectedItem.textures[0]
            Object.assign(targetTexture, newTexture)
            bumpLinkedItemMaterialEpoch(targetItem)

            if (selectedItem.zones && Object.keys(selectedItem.zones).length > 0) {
              const targetZoneKey = Object.keys(selectedItem.zones)[0]
              const targetZone = selectedItem.zones[targetZoneKey]
              if (targetZone && targetZone.material) {
                targetZone.material = { ...targetZone.material, ...material }
              }
            }
          }
        })
      } else if (modeEditor === 'MBJ') {
        s.items.forEach((targetItem, key) => {
          if (!key.startsWith('linkedItem_') && !key.startsWith('linkedSon_')) return
          if (!isLinked(targetItem)) return
          if (targetItem.id === linkedID) return

          if (
            targetItem.description === 'Puerta' &&
            String(targetItem.catalogID).toLowerCase().includes('encimera')
          ) {
            if (targetItem.textures && targetItem.textures.length > 0) {
              const targetTexture = targetItem.textures[0]
              Object.assign(targetTexture, newTexture)
              bumpLinkedItemMaterialEpoch(targetItem)

              if (targetItem.zones && Object.keys(targetItem.zones).length > 0) {
                const targetZoneKey = Object.keys(targetItem.zones)[0]
                const targetZone = targetItem.zones[targetZoneKey]
                if (targetZone && targetZone.material) {
                  targetZone.material = { ...targetZone.material, ...material }
                }
              }
            }
          }
        })
      } else if (modeEditor === 'COL') {
        s.items.forEach((targetItem, key) => {
          if (!key.startsWith('linkedItem_') && !key.startsWith('linkedSon_')) return
          if (!isLinked(targetItem)) return
          if (targetItem.id === linkedID) return

          if (
            targetItem.description === 'Puerta' &&
            String(targetItem.catalogID).toLowerCase().includes('encimera')
          ) {
            if (targetItem.textures && targetItem.textures.length > 0) {
              const targetTexture = targetItem.textures[0]
              Object.assign(targetTexture, newTexture)
              bumpLinkedItemMaterialEpoch(targetItem)

              if (targetItem.zones && Object.keys(targetItem.zones).length > 0) {
                const targetZoneKey = Object.keys(targetItem.zones)[0]
                const targetZone = targetItem.zones[targetZoneKey]
                if (targetZone && targetZone.material) {
                  targetZone.material = { ...targetZone.material, ...material }
                }
              }
            }
          }
        })
      }
      s.shouldUpdateCamera = true
      s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
    })
    return newTexture
  },

  updateTiradorPositionsOnly: (parentID: string) => {
    set((state) => {
      const parentItem = state.items.get(parentID as AnyID)
      if (!isLinkedItem(parentItem)) return

      if (parentItem.childs?.size > 0) {
        parentItem.childs.forEach((childID) => {
          const childItem = state.items.get(childID as AnyID)
          if (!childItem || !isLinkedSon(childItem)) return
          if (childItem.typeOfSon === 'Tirador' || childItem.description === 'Tirador') {
            const pos = calculateTiradorPosition(childItem, parentItem)
            childItem.x = pos.x
            childItem.y = pos.y
            childItem.z = pos.z
          }
        })
      }
    })
  },

  updateLinkedSonPositions: (parentID: string, morphChanged: 'Ancho' | 'Fondo' | 'Alto' | 'Regleta') => {
    set((state) => {
      const parentItem = state.items.get(parentID as AnyID)
      if (!isLinkedItem(parentItem)) return

      // Deshacer offset +100 antiguo que dejaba la encimera flotando.
      if (revertIndependentZocaloFloorOffsetIfNeeded(parentItem)) {
        const zocaloId = parentItem.independentZocaloSonId
        if (zocaloId) {
          const zocalo = state.items.get(zocaloId as AnyID)
          if (zocalo && isLinkedSon(zocalo)) {
            placeLinkedIndependentZocalo(zocalo as any, parentItem, { items: state.items })
          }
        }
      }

      if (usesFreeDimensionScaling(parentItem) && morphChanged === 'Ancho') {
        logFreeDimensionAncho('updateLinkedSonPositions before', parentItem, { morphChanged })
      }

      const parentHeight = parentItem.height
      const parentY = parentItem.y ?? 0
      const parentLayoutAlto = getFreeDimensionParentLayoutAltoMm(parentItem) ?? parentHeight
      const parentBodyTopY = usesFreeDimensionScaling(parentItem)
        ? getFreeDimensionVisualBodyTopYMm(parentItem)
        : parentHeight
      const parentEncimeraTopY = usesFreeDimensionScaling(parentItem)
        ? getFreeDimensionEncimeraPositionYMm(parentItem)
        : parentY + parentHeight
      const parentWidth = parentItem.width
      const parentThickness = parentItem.thickness

      const isRBorRegleta =
        /^rb/i.test(parentItem.catalogID ?? '') || /regleta/i.test(parentItem.description ?? '')

      let siblingEncimera: (typeof parentItem) | null = null
      if (parentItem.childs?.size) {
        for (const cid of parentItem.childs) {
          const son = state.items.get(cid as AnyID)
          if (son && isEncimeraLinkedSonItem(son)) {
            siblingEncimera = son as typeof parentItem
            break
          }
        }
      }
      const encimeraAabb = siblingEncimera
        ? getCatalogGlbAabbMm(state.sceneGLB as never, siblingEncimera.catalogID, [
            sceneGLBLookupKey(String(siblingEncimera.catalogID ?? '')),
            (siblingEncimera as { idUnidades?: number }).idUnidades,
          ])
        : null

      if (parentItem.childs?.size > 0) {
        parentItem.childs.forEach((childID) => {
          const childItem = state.items.get(childID as AnyID)
          if (!childItem || !isLinkedSon(childItem)) return

          if (childItem.typeOfSon === 'Tirador' || childItem.description === 'Tirador') {
            const pos = calculateTiradorPosition(childItem, parentItem)
            childItem.x = pos.x
            childItem.y = pos.y
            childItem.z = pos.z
          } else {
            const isEncimera =
              isEncimeraLinkedSonItem(childItem) ||
              childItem.description?.toLowerCase().includes('encimera')

            if (isEncimera) {
              const rbFreeDimEncimera =
                usesFreeDimensionScaling(parentItem) && isRbFreeDimensionRegleta(parentItem)

              // Free-dim (MBJ/COL/MAL, laterales, etc.):
              // siempre reposicionar encima del cuerpo y sincronizar ancho/fondo.
              if (usesFreeDimensionScaling(parentItem)) {
                placeLinkedEncimeraY(childItem, parentItem)
                if (morphChanged === 'Alto') {
                  applyEncimeraThicknessMm(childItem)
                } else if (morphChanged === 'Ancho') {
                  syncFreeDimensionEncimeraAnchoToParent(childItem, parentItem)
                } else if (morphChanged === 'Fondo') {
                  syncFreeDimensionEncimeraFondoToParent(childItem, parentItem)
                }
              } else if (morphChanged === 'Alto') {
                if (isRBorRegleta) {
                  const parentMorphAlto = parentItem.morphs?.find((m) => m.name === 'Alto')
                  const _def = (parentMorphAlto?._default as number) ?? parentHeight
                  const def = (parentMorphAlto?.default as number) ?? parentHeight
                  const delta = _def - def

                  const mark = '__rbOriginalY'
                  if ((childItem as any)[mark] == null) {
                    ;(childItem as any)[mark] = childItem.y ?? 0
                  }
                  const originalY = (childItem as any)[mark] as number
                  childItem.y = originalY - delta
                } else {
                  childItem.y = parentEncimeraTopY
                }
                applyEncimeraThicknessMm(childItem)
              } else if (rbFreeDimEncimera) {
                if (morphChanged === 'Ancho') {
                  syncFreeDimensionEncimeraAnchoToParent(childItem, parentItem)
                } else if (morphChanged === 'Fondo') {
                  syncFreeDimensionEncimeraFondoToParent(childItem, parentItem)
                }
              } else {
                const relativeY = (childItem.y ?? 0) - parentY
                childItem.y = parentY + relativeY
              }
            } else if (isIndependentZocaloLinkedSon(childItem)) {
              // Zócalo fijo abajo: solo Ancho (= padre); alto/espesor nativos. Nunca heredar Fondo/Alto.
              syncIndependentZocaloToParent(childItem, parentItem, { items: state.items })
            } else if (linkedSonKeepsApiLocalTransform(childItem)) {
              // Fregadero/placa/horno: X/Z × scale cuerpo; Y anclada a encimera o lift+scale.
              // Re-sellar Y API desde el catálogo del padre (evita posicionY contaminada
              // tras un Alto que clasificó mal el zócalo y dejó el fregadero +~100 mm).
              if (morphChanged === 'Alto' && usesFreeDimensionScaling(parentItem)) {
                const elementos =
                  state.sceneGLB.get(parentItem.catalogID)?.backGLBInfo?.elementosEnlazados ??
                  state.sceneGLB.get(String(parentItem.catalogID ?? ''))?.backGLBInfo
                    ?.elementosEnlazados
                if (isCountertopApplianceLinkedSon(childItem)) {
                  const linkedKey = Number(
                    (childItem as { idElementosEnlazados?: number }).idElementosEnlazados ??
                      childItem.linkedID ??
                      0,
                  )
                  const exact = Array.isArray(elementos)
                    ? elementos.find(
                        (el: { idElementosEnlazados?: number }) =>
                          Number(el.idElementosEnlazados) === linkedKey,
                      )
                    : undefined
                  // Solo resetear Y si el catálogo del padre tiene ESTE elemento.
                  // Tras un swap de fregadero el ID nuevo no está en el catálogo:
                  // hay que conservar posicionY de getelementosenlazadosunidadacabados/{ant}/{nuevo}.
                  if (exact) {
                    ;(childItem as any).posicionY = null
                    ;(childItem as any).baseY = null
                  }
                }
                sealLinkedApplianceApiFloorCoordsFromParentCatalog(
                  childItem as any,
                  parentItem as any,
                  elementos,
                )
              }
              applyLinkedApplianceLocalTransformFromApi(
                childItem,
                parentItem,
                siblingEncimera as never,
                { encimeraAabb },
              )
            } else {
              childItem.y =
                parentY + (usesFreeDimensionScaling(parentItem) ? parentBodyTopY : parentLayoutAlto)
            }
          }

          if (childItem.morphs?.length > 0 && !childItem.description.includes('Tirador')) {
            childItem.morphs.forEach((morph) => {
              if (morph.name === morphChanged) {
                const isEncimera =
                  isEncimeraLinkedSonItem(childItem) ||
                  childItem.description?.toLowerCase().includes('encimera')

                if (isEncimera) {
                  if (isRBorRegleta && morph.name === 'Alto') {
                    return
                  }
                  // Grosor por acabado (10/12/20/30 mm); nunca copiar Alto/ancho del padre al morph Alto.
                  if (morph.name === 'Alto') {
                    applyEncimeraThicknessMm(childItem)
                    return
                  }
                  // LB8060/CB9060: ancho = lateral; fondo = catálogo encimera (620), no fondo lateral (600).
                  if (freeDimensionUsesLb8060StyleAltoOffset(parentItem)) {
                    if (morph.name === 'Ancho') {
                      morph.default = parentWidth
                      childItem.width = parentWidth
                      return
                    }
                    if (morph.name === 'Fondo') {
                      syncFreeDimensionEncimeraFondoToParent(childItem, parentItem)
                      return
                    }
                  }

                  // FREE_DIMENSION: encimera con morph GLB (50 mm), no estirar al ancho del padre (100 mm).
                  if (usesFreeDimensionScaling(parentItem)) {
                    if (morph.name === 'Ancho') {
                      syncFreeDimensionEncimeraAnchoToParent(childItem, parentItem)
                      return
                    }
                    if (morph.name === 'Fondo') {
                      syncFreeDimensionEncimeraFondoToParent(childItem, parentItem)
                      return
                    }
                  }

                  // Encimera BR*: copiar Regleta del padre (deformación GLB vía getRegletaMorphInfluence).
                  if (morph.name === 'Regleta') {
                    const v = Number(parentItem.regleta ?? 0)
                    morph.default = v
                    ;(morph as { reversed?: boolean }).reversed = false
                    childItem.regleta = v
                    return
                  }

                  const parentMorph = parentItem.morphs?.find((m) => m.name === morph.name)
                  if (parentMorph) {
                    const currentParentValue =
                      morph.name === 'Ancho'
                        ? parentWidth
                        : morph.name === 'Fondo'
                          ? parentThickness
                          : morph.name === 'Alto'
                            ? isEncimera && usesFreeDimensionScaling(parentItem)
                              ? parentEncimeraTopY
                              : parentLayoutAlto
                            : 0

                    let newValue: number
                    if (isRBorRegleta && morph.name === 'Ancho' && isEncimera) {
                      const cap =
                        morph.max != null && morph.min != null && morph.max > morph.min
                          ? morph.max
                          : currentParentValue
                      newValue = Math.min(currentParentValue, cap)
                    } else if (isRBorRegleta && morph.name === 'Ancho') {
                      newValue = currentParentValue
                    } else {
                      // Para otros casos, calcular la diferencia como antes
                      const difference = morph.max - parentMorph.max
                      newValue = currentParentValue + difference
                    }

                    morph.default = newValue
                    // NO actualizar _default - mantener el valor original del modelo GLB

                    if (morph.name === 'Ancho') {
                      childItem.width = newValue
                    } else if (morph.name === 'Fondo') {
                      childItem.thickness = newValue
                    } else if (morph.name === 'Alto') {
                      if (!(isRBorRegleta && isEncimera)) {
                        childItem.height = newValue
                      }
                    }
                  }
                } else {
                  if (isIndependentZocaloLinkedSon(childItem)) {
                    if (morph.name === 'Ancho') {
                      // Solo Ancho sigue al padre; place/sync aplica scaleX + thickness nativo.
                      syncIndependentZocaloToParent(childItem, parentItem, { items: state.items })
                    }
                    // Fondo/Alto: nunca heredar del padre (tira de espesor fijo).
                    return
                  }
                  // Fregadero/placa/horno/…: tamaño vía group scale del padre, no morph dims.
                  if (
                    linkedSonKeepsApiLocalTransform(childItem) ||
                    linkedSonScalesWithParentFurniture(childItem)
                  ) {
                    return
                  }
                  if (morph.name === 'Ancho') {
                    morph.default = parentWidth
                    morph._default = parentWidth
                    childItem.width = parentWidth
                  } else if (morph.name === 'Fondo') {
                    morph.default = parentThickness
                    morph._default = parentThickness
                    childItem.thickness = parentThickness
                  } else if (morph.name === 'Alto') {
                    morph.default = parentHeight
                    morph._default = parentHeight
                    childItem.height = parentHeight
                  } else if (morph.name === 'Regleta') {
                    morph.default = parentItem.regleta || 0
                    morph._default = parentItem.regleta || 0
                    childItem.regleta = parentItem.regleta || 0
                  }
                }
              }
            })
          }
        })
      }

      if (usesFreeDimensionScaling(parentItem) && morphChanged === 'Ancho') {
        const enc = parentItem.childs
          ? Array.from(parentItem.childs)
              .map((cid) => state.items.get(cid as AnyID))
              .find((c) => c?.description?.toLowerCase().includes('encimera'))
          : null
        logFreeDimensionAncho('updateLinkedSonPositions after', parentItem, {
          encimeraWidth: enc?.width,
          encimeraY: enc?.y,
        })
      }

      // Recalcular UV contiguas de encimeras (padre o vecinos MBJ cambiaron ancho/fondo/alto).
      if (
        parentItem.tipoNormalizado === 'MBJ' &&
        (morphChanged === 'Ancho' || morphChanged === 'Fondo' || morphChanged === 'Alto')
      ) {
        state.contadorModificaciones = (state.contadorModificaciones || 0) + 1
      }
    })
  },
  setLinkedHandPosition: (
    linkedID: LinkedItemID,
    hand: 'Izquierda' | 'Centro' | 'Derecha' | 'Normal',
  ) => {
    set((s) => {
      const item = s.items.get(linkedID)
      if (item && item.type === 'linkedSon' && item.description === 'Tirador') {
        const parent = s.items.get(item.firstParentID as LinkedItemID) as LinkedItem
        if (parent) {
          let prefix = 'b'
          if (parent.tipoNormalizado === 'MAL' || parent.tipoNormalizado === 'COL') prefix = 'a'

          const parentHand = parent.hand
          const isCentralHandNormal =
            parentHand === 'CN' && (hand === 'Izquierda' || hand === 'Derecha')

          const itemWithHand = { ...(item as TiradorWithPositions), handPosition: hand }
          const { x, y, z } = calculateTiradorPosition(itemWithHand, parent)

          let rotationX = 0,
            rotationY = 0,
            rotationZ = 0

          if (hand === 'Izquierda') {
            if (isCentralHandNormal) {
              rotationX = (item as TiradorWithPositions)[`${prefix}cRotacionX`] ?? 0
              rotationY = (item as TiradorWithPositions)[`${prefix}cRotacionY`] ?? 0
              rotationZ = (item as TiradorWithPositions)[`${prefix}cRotacionZ`] ?? 0
            } else {
              rotationX = (item as TiradorWithPositions)[`${prefix}iRotacionX`] ?? 0
              rotationY = (item as TiradorWithPositions)[`${prefix}iRotacionY`] ?? 0
              rotationZ = (item as TiradorWithPositions)[`${prefix}iRotacionZ`] ?? 0
            }
          } else if (hand === 'Centro') {
            rotationX = (item as TiradorWithPositions)[`${prefix}cRotacionX`] ?? 0
            rotationY = (item as TiradorWithPositions)[`${prefix}cRotacionY`] ?? 0
            rotationZ = (item as TiradorWithPositions)[`${prefix}cRotacionZ`] ?? 0
          } else if (hand === 'Derecha') {
            if (isCentralHandNormal) {
              rotationX = (item as TiradorWithPositions)[`${prefix}cRotacionX`] ?? 0
              rotationY = (item as TiradorWithPositions)[`${prefix}cRotacionY`] ?? 0
              rotationZ = (item as TiradorWithPositions)[`${prefix}cRotacionZ`] ?? 0
            } else {
              rotationX = (item as TiradorWithPositions)[`${prefix}dRotacionX`] ?? 0
              rotationZ = (item as TiradorWithPositions)[`${prefix}dRotacionZ`] ?? 0
              rotationY = (item as TiradorWithPositions)[`${prefix}dRotacionY`] ?? 0
            }
          }

          let newMatrix: Matrix4

          const hasRotationValues = rotationX !== 0 || rotationY !== 0 || rotationZ !== 0
          const rotationZRad = (rotationZ * Math.PI) / 180

          if (hasRotationValues) {
            const rotationXRad = (rotationX * Math.PI) / 180
            const rotationYRad = (rotationY * Math.PI) / 180

            // Matriz de rotación - Z es el eje principal para horizontal/vertical
            // const rotationMatrix = new Matrix4().makeRotationFromEuler(
            //   new Euler(rotationXRad, rotationYRad, rotationZRad, 'XYZ'),
            // )

            newMatrix = new Matrix4()
              .makeRotationFromEuler(new Euler(rotationXRad, rotationYRad, rotationZRad, 'XYZ'))
              .setPosition(x, y, z)
          } else {
            newMatrix = new Matrix4().setPosition(x, y, z)
          }

          const posKey = hand === 'Izquierda' ? 'i' : hand === 'Centro' ? 'c' : 'd'
          const rawX = (item as any)[`${prefix}${posKey}PosicionX`]
          const rawY = (item as any)[`${prefix}${posKey}PosicionY`]
          const rawZ = (item as any)[`${prefix}${posKey}PosicionZ`]
          const isNull = rawX == null && rawY == null && rawZ == null
          const isZero = rawX === 0 && rawY === 0 && rawZ === 0
          const shouldHide = isNull || isZero

          const updated = {
            ...item,
            handPosition: hand,
            x: x,
            y: y,
            z: z,
            matrix: newMatrix,
            rotation: hasRotationValues ? rotationZRad : item.rotation,
            // Visibilidad basada SOLO en la posición final calculada
            visible: !(x === 0 && y === 0 && z === 0),
          }
          s.items.set(linkedID, updated)
          s.shouldUpdateCamera = true
        } else {
          const updated = { ...item, handPosition: hand } as TiradorWithPositions
          const posKey = hand === 'Izquierda' ? 'i' : hand === 'Centro' ? 'c' : 'd'
          // Detectar prefijo disponible: usar 'b' por defecto, caer a 'a' si 'b' no tiene datos pero 'a' sí
          let prefix: 'a' | 'b' = 'b'
          const bx = (updated as any)[`b${posKey}PosicionX`]
          const by = (updated as any)[`b${posKey}PosicionY`]
          const bz = (updated as any)[`b${posKey}PosicionZ`]
          const bHas = bx != null || by != null || bz != null
          const ax = (updated as any)[`a${posKey}PosicionX`]
          const ay = (updated as any)[`a${posKey}PosicionY`]
          const az = (updated as any)[`a${posKey}PosicionZ`]
          const aHas = ax != null || ay != null || az != null
          if (!bHas && aHas) prefix = 'a'

          if (hand === 'Izquierda') {
            updated.x = (updated as any)[`${prefix}iPosicionX`] ?? updated.x
            updated.y = (updated as any)[`${prefix}iPosicionY`] ?? updated.y
            updated.z = (updated as any)[`${prefix}iPosicionZ`] ?? updated.z
          } else if (hand === 'Centro') {
            updated.x = (updated as any)[`${prefix}cPosicionX`] ?? updated.x
            updated.y = (updated as any)[`${prefix}cPosicionY`] ?? updated.y
            updated.z = (updated as any)[`${prefix}cPosicionZ`] ?? updated.z
          } else if (hand === 'Derecha') {
            updated.x = (updated as any)[`${prefix}dPosicionX`] ?? updated.x
            updated.y = (updated as any)[`${prefix}dPosicionY`] ?? updated.y
            updated.z = (updated as any)[`${prefix}dPosicionZ`] ?? updated.z
          }

          // Visibilidad basada SOLO en la posición final calculada
          ;(updated as any).visible = !(updated.x === 0 && updated.y === 0 && updated.z === 0)

          s.items.set(linkedID, updated)
        }
      }
    })
  },
  restoreDefaultTiradoresForLinkedItem: async (linkedID: LinkedItemsID) => {
    const item = get().items.get(linkedID)
    if (!isLinkedItem(item)) return

    const catalogId = String(item.catalogID || '')
    if (!catalogId) return

    logGola('restoreDefaultTiradoresForLinkedItem', { linkedID, catalogId })

    await get().updateLinkedSonsForNewGLB(linkedID, catalogId, catalogId, {
      forceRestoreDefaultTiradores: true,
      useCatalogDefaultTextures: true,
    })

    set((s) => {
      const parent = s.items.get(linkedID)
      if (!isLinkedItem(parent) || !parent.childs) return
      parent.childs.forEach((childID) => {
        const child = s.items.get(childID)
        if (child?.type === 'linkedSon' && child.description === 'Tirador') {
          child.visible = true
        }
      })
    })

    // Recién creados vienen con coords de catálogo; alinear a Ancho/Fondo/Alto actuales.
    get().updateTiradorPositionsOnly(linkedID)
  },

  applyLinkedItemGolaModo: async (linkedID: LinkedItemsID, golaActiva: boolean) => {
    logGola('applyLinkedItemGolaModo', { linkedID, golaActiva })

    if (golaActiva) {
      set((s) => {
        const item = s.items.get(linkedID)
        if (!isLinkedItem(item)) return
        item.golaActiva = true
        if (item.childs) {
          item.childs.forEach((childID) => {
            const child = s.items.get(childID)
            if (child?.type === 'linkedSon' && child.description === 'Tirador') {
              child.visible = false
            }
          })
        }
      })
    } else {
      set((s) => {
        const item = s.items.get(linkedID)
        if (isLinkedItem(item)) item.golaActiva = false
      })

      // Modo (mismo GLB): al entrar solo se ocultan. No borrar/recrear (eso
      // devolvía coords de catálogo e ignoraba Ancho/Fondo ya modificados).
      const current = get().items.get(linkedID)
      const hasTiradores =
        isLinkedItem(current) &&
        Array.from(current.childs ?? []).some((childID) => {
          const child = get().items.get(childID)
          return child?.type === 'linkedSon' && child.description === 'Tirador'
        })

      if (hasTiradores) {
        set((s) => {
          const parent = s.items.get(linkedID)
          if (!isLinkedItem(parent) || !parent.childs) return
          parent.childs.forEach((childID) => {
            const child = s.items.get(childID)
            if (child?.type === 'linkedSon' && child.description === 'Tirador') {
              child.visible = true
            }
          })
        })
        get().updateTiradorPositionsOnly(linkedID)
      } else {
        await get().restoreDefaultTiradoresForLinkedItem(linkedID)
      }
    }

    set((s) => {
      s.shouldUpdateCamera = true
    })
  },

  updateLinkedItemGola: async (
    linkedID: LinkedItemsID,
    newUnidadData: any,
    prefetch?: GolaUpdatePrefetch,
  ) => {
    const item = get().items.get(linkedID)
    if (!isLinkedItem(item)) {
      logGola('updateLinkedItemGola_ABORT', { linkedID, reason: 'invalid_item' })
      return
    }

    const currentCatalogID = item.catalogID?.toString() || ''
    const newCatalogIDPreview = newUnidadData.codigo?.toString() || ''
    const resolvedCatalogID = resolveCatalogIdAfterGolaChange(
      currentCatalogID,
      newCatalogIDPreview,
    )

    logGola('updateLinkedItemGola_START', {
      linkedID,
      from: currentCatalogID,
      to: newCatalogIDPreview,
      resolvedCatalogID,
      idUnidadesActual: item.idUnidades,
      idUnidadesNuevo: newUnidadData.idUnidades,
      idCambioArmazon: newUnidadData.idCambioArmazon,
      idCambioGolaBlanco: newUnidadData.idCambioGolaBlanco,
      idCambioGolaPetroleo: newUnidadData.idCambioGolaPetroleo,
      referenciaBlender: newUnidadData.referenciaBlender,
    })
    if (item.childs) {
      logGolaLinkedChildrenSnapshot(
        'updateLinkedItemGola_before',
        linkedID,
        (id) => get().items.get(id as AnyID),
        item.childs,
      )
      logGolaItemTexturesSnapshot(
        'updateLinkedItemGola_before',
        linkedID,
        item.textures as Array<Record<string, unknown>>,
        currentCatalogID,
      )
    }
    const itemDescription = String(item.description || '').toLowerCase()

    // NO procesar laterales - no deberían tener Gola automáticamente
    // KAB puede ser MAL (unidades altas) o lateral, pero si tiene " GO" es una unidad Gola válida
    // Solo detectar laterales reales: LA, LB, LC, CC, CB (sin " GO")
    const catalogUpper = currentCatalogID.toUpperCase()
    const isLateral =
      (itemDescription.includes('lateral') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('LA') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('LB') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('LC') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('CC') && !catalogUpper.includes(' GO')) ||
      (catalogUpper.startsWith('CB') && !catalogUpper.includes(' GO'))

    if (isLateral) {
      logGola('updateLinkedItemGola_SKIP', { linkedID, reason: 'is_lateral', currentCatalogID })
      return
    }

    const newCatalogID = resolvedCatalogID
    const parsedResolved = parseLinkedCatalogParts(resolvedCatalogID)
    const isCurrentlyGola =
      parseLinkedCatalogParts(currentCatalogID).hasGola || currentCatalogID.includes(' GO')
    const isChangingToGola = parsedResolved.hasGola

    logGola('GOLA_STATE', {
      linkedID,
      isCurrentlyGola,
      isChangingToGola,
      direction: isChangingToGola ? 'to_gola' : 'from_gola',
    })

    // Detectar si es una vitrina (KABV o KAPV)
    const isVitrina =
      currentCatalogID.toUpperCase().startsWith('KABV') ||
      currentCatalogID.toUpperCase().startsWith('KAPV') ||
      currentCatalogID.toUpperCase().startsWith('PE KABV') ||
      currentCatalogID.toUpperCase().startsWith('PE KAPV')

    // Detectar si estamos cambiando el estado de gola o entre diferentes tipos de gola
    // Si ambas unidades tienen " GO", también debemos restaurar tiradores
    const isChangingGolaStateValue = isCurrentlyGola !== isChangingToGola
    const isChangingBetweenGolaTypes =
      isCurrentlyGola && isChangingToGola && currentCatalogID !== newCatalogID
    const shouldChangeChildren = isChangingGolaStateValue || isChangingBetweenGolaTypes

    let newLinkedOptions: Record<string, unknown> | null = prefetch?.linkedOptions ?? null
    try {
      if (!newLinkedOptions && newUnidadData.idUnidades) {
        logGolaApi('getelementosenlazadosunidadacabados', {
          idUnidades: newUnidadData.idUnidades,
          linkedID,
        })
        newLinkedOptions = await fetchLinkedUnitOptionsRaw(newUnidadData.idUnidades)
      }
      if (newLinkedOptions) {
        logGolaApi(
          'getelementosenlazadosunidadacabados',
          { idUnidades: newUnidadData.idUnidades, linkedID },
          newLinkedOptions,
          {
            hasTirador: Boolean(
              newLinkedOptions?.['0']
                ? (newLinkedOptions['0'] as Record<string, unknown>).Tirador
                : newLinkedOptions.Tirador,
            ),
            hasGrifo: Boolean(
              newLinkedOptions?.['0']
                ? (newLinkedOptions['0'] as Record<string, unknown>).TPCGrifo
                : newLinkedOptions.TPCGrifo,
            ),
            hasFregadero: Boolean(
              newLinkedOptions?.['0']
                ? (newLinkedOptions['0'] as Record<string, unknown>).Fregadero
                : newLinkedOptions.Fregadero,
            ),
          },
        )
      } else if (!prefetch?.linkedOptions) {
        logGola('LINKED_OPTIONS_FAIL', {
          linkedID,
          idUnidades: newUnidadData.idUnidades,
        })
      }
    } catch (error) {
      logGola('LINKED_OPTIONS_ERROR', { linkedID, error: String(error) })
      console.error('GOLA UPDATE - Error obteniendo linkedOptions:', error)
    }

    if (newLinkedOptions) {
      set((s) => {
        const item = s.items.get(linkedID)
        if (!isLinkedItem(item)) {
          return
        }

        const patch = mapLinkedUnitOptionsResponse(newLinkedOptions)
        applyLinkedUnitOptionsPatch(item, patch, {
          preserveExisting: true,
          clearTirador: isChangingToGola,
        })
      })
    }

    // Crear mapas para buscar texturas por zoneDisplayName y por zoneName
    const userAppliedTexturesByDisplayName = new Map<string, any>()
    const userAppliedTexturesByZoneName = new Map<string, any>()

    item.textures.forEach((texture) => {
      if (texture.zoneDisplayName) {
        userAppliedTexturesByDisplayName.set(texture.zoneDisplayName, texture)
      }
      if (texture.zoneName) {
        userAppliedTexturesByZoneName.set(texture.zoneName, texture)
      }
    })

    const store = get()
    const currentChilds = new Map<string, any>()

    if (item.childs?.size > 0) {
      for (const childID of item.childs) {
        const childItem = store.items.get(childID)
        if (childItem && childItem.type === 'linkedSon') {
          currentChilds.set(childItem.description || 'unknown', childItem)
        }
      }
    }

    const isChangingFromGola = isCurrentlyGola

    // Capturar el catalogID anterior ANTES de actualizarlo
    const previousCatalogID = currentCatalogID

    // KBR ↔ KGBR (API sin morphs): dims/catálogo rebasados a la unidad nueva.
    let kbrRebase: KbrFamilyRebaseResult | null = null

    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinkedItem(item)) {
        return
      }

      if (newUnidadData.idUnidades) {
        item.idUnidades = newUnidadData.idUnidades
        invalidateLinkedTextureCaches()
      }
      if (newUnidadData.codigo) {
        item.catalogID = resolvedCatalogID
        item.golaActiva = parsedResolved.hasGola
      }
      if (newUnidadData.referencia) {
        item.referencia = newUnidadData.referencia
      }
      applyIdCambioFromApi(item as Record<string, unknown>, newUnidadData)

      if (typeof newUnidadData.referenciaBlender === 'string') {
        item.glbPath = newUnidadData.referenciaBlender
      }
      if (typeof newUnidadData.referenciaJpgpng === 'string') {
        item.image = newUnidadData.referenciaJpgpng
      }

      const prevRegleta = item.regleta
      const prevRegletaMinimo = item.regletaMinimo
      const prevRegletaMaximo = item.regletaMaximo

      // Actualizar regleta, regletaMinimo y regletaMaximo desde newUnidadData si existen
      if (
        newUnidadData.regleta !== undefined &&
        newUnidadData.regleta !== null &&
        newUnidadData.regletaMinimo !== undefined &&
        newUnidadData.regletaMinimo !== null &&
        newUnidadData.regletaMaximo !== undefined &&
        newUnidadData.regletaMaximo !== null
      ) {
        item.regleta = newUnidadData.regleta
        item.regletaMinimo = newUnidadData.regletaMinimo
        item.regletaMaximo = newUnidadData.regletaMaximo
      } else if (
        prevRegleta !== undefined &&
        prevRegleta !== null &&
        prevRegletaMinimo !== undefined &&
        prevRegletaMinimo !== null &&
        prevRegletaMaximo !== undefined &&
        prevRegletaMaximo !== null
      ) {
        item.regleta = prevRegleta
        item.regletaMinimo = prevRegletaMinimo
        item.regletaMaximo = prevRegletaMaximo
      }

      // morphs: [] (KBR/KGBR) cuenta como "sin morphs": si no, el array vacío
      // pisaba item.morphs y el alto no se rebasaba a la unidad nueva.
      if (Array.isArray(newUnidadData.morphs) && newUnidadData.morphs.length > 0) {
        const preservedMap = new Map<string, number>()
        ;(item.morphs || []).forEach((m) => {
          const current = typeof m.default === 'number' ? m.default : m._default
          if (typeof current === 'number') preservedMap.set(m.name, current)
        })

        item.morphs = newUnidadData.morphs.map((morph: any) => {
          const name = morph.nombre || morph.name
          const min = morph.valorMin || morph.min
          const max = morph.valorMax || morph.max
          // Base de catálogo para deltas de tirador; default = tamaño actual (resize).
          // Si _default === default tras Gola, calculateTiradorPosition ve Δ=0.
          const catalogBase = morph.valor || morph.default
          let next = catalogBase
          if (preservedMap.has(name)) {
            const preserved = preservedMap.get(name) as number
            const clamped = Math.max(min, Math.min(max, preserved))
            next = clamped
          }
          return {
            _default: catalogBase,
            default: next,
            max,
            min,
            modifyable: true,
            morphId: morph.idMorph || morph.id,
            name,
            order: morph.orden || morph.order,
            show: true,
            type: morph.tipo || morph.type,
          }
        })

        const hasRegletaProps =
          item.regleta !== undefined &&
          item.regleta !== null &&
          item.regletaMinimo !== undefined &&
          item.regletaMinimo !== null &&
          item.regletaMaximo !== undefined &&
          item.regletaMaximo !== null

        if (hasRegletaProps) {
          const existingRegletaIndex = item.morphs.findIndex((m) => m.name === 'Regleta')
          const preservedRegletaValue = preservedMap.get('Regleta')

          if (existingRegletaIndex >= 0) {
            const regletaValue =
              preservedRegletaValue !== undefined ? preservedRegletaValue : item.regleta
            const clamped = Math.max(item.regletaMinimo, Math.min(item.regletaMaximo, regletaValue))
            item.morphs[existingRegletaIndex] = {
              ...item.morphs[existingRegletaIndex],
              default: clamped,
              _default: clamped,
              min: item.regletaMinimo,
              max: item.regletaMaximo,
            }
          } else {
            const regletaValue =
              preservedRegletaValue !== undefined ? preservedRegletaValue : item.regleta
            const clamped = Math.max(item.regletaMinimo, Math.min(item.regletaMaximo, regletaValue))
            item.morphs.push({
              default: clamped,
              _default: clamped,
              morphId: -1, // ID especial para regleta
              order: 999, // Al final
              type: 'R',
              min: item.regletaMinimo,
              max: item.regletaMaximo,
              name: 'Regleta',
              show: true,
              modifyable: true,
            })
          }
        }

        item.morphs.forEach((m) => {
          if (m.name === 'Ancho') {
            item.width = m.default
          } else if (m.name === 'Alto') {
            item.height = m.default
          } else if (m.name === 'Fondo') {
            item.thickness = m.default
          } else if (m.name === 'Regleta') {
            item.regleta = m.default
          }
        })
      } else {
        // Rinconeros KBR ↔ KGBR: sin morphs de API, el alto cambia de contrato
        // (KGBR 800 = cuerpo; KBR 900 = mueble con hueco de zócalo).
        kbrRebase = rebaseKbrFamilyDimsAfterUnitChange(item, newUnidadData)
        if (kbrRebase) {
          logGola('KBR_REBASE_DIMS', {
            linkedID,
            catalogID: item.catalogID,
            width: item.width,
            height: item.height,
            thickness: item.thickness,
            freeDimensionCatalogMm: item.freeDimensionCatalogMm,
            ...kbrRebase,
          })
        }
      }

      if (newUnidadData.acabados && Array.isArray(newUnidadData.acabados)) {
        // Para vitrinas, usar lógica especial para preservar TODAS las texturas
        if (isVitrina) {
          // Crear mapas para buscar texturas por zoneDisplayName y por zoneName
          const currentTexturesByZoneDisplayName = new Map<string, any>()
          const currentTexturesByZoneName = new Map<string, any>()
          const currentTexturesMap = new Map<string, any>()

          item.textures.forEach((texture) => {
            if (texture.zoneDisplayName) {
              currentTexturesByZoneDisplayName.set(texture.zoneDisplayName, texture)
              currentTexturesMap.set(texture.zoneDisplayName, texture)
            }
            if (texture.zoneName) {
              currentTexturesByZoneName.set(texture.zoneName, texture)
              if (!currentTexturesMap.has(texture.zoneDisplayName || '')) {
                currentTexturesMap.set(texture.zoneName, texture)
              }
            }
          })

          const updatedTextures: any[] = []
          const processedZoneNames = new Set<string>()
          const processedZoneDisplayNames = new Set<string>()

          // Primero procesar los acabados del backend
          newUnidadData.acabados.forEach((acabado: any) => {
            const displayName = acabado.descripcion || acabado.zona
            const zoneName = acabado.zona || ''
            processedZoneDisplayNames.add(displayName)
            if (zoneName) processedZoneNames.add(zoneName)

            // Buscar textura existente por zoneDisplayName o zoneName
            let existingTexture =
              currentTexturesByZoneDisplayName.get(displayName) ||
              currentTexturesByZoneName.get(zoneName)

            // Si no se encuentra, buscar por cualquier match parcial
            if (!existingTexture) {
              // Buscar por nombre similar (para casos como "Puerta" vs "Marco")
              for (const [key, texture] of currentTexturesMap.entries()) {
                if (
                  (texture.zoneDisplayName &&
                    texture.zoneDisplayName.toLowerCase() === displayName.toLowerCase()) ||
                  (texture.zoneName && texture.zoneName === zoneName) ||
                  (zoneName &&
                    texture.zoneName &&
                    texture.zoneName.includes(zoneName.split('_')[0]))
                ) {
                  existingTexture = texture
                  break
                }
              }
            }

            if (existingTexture) {
              const preservedName = existingTexture.name || acabado.texturas?.nombre
              const preservedDescription =
                existingTexture.description || existingTexture.descripcion || preservedName
              updatedTextures.push({
                ...reconcileExistingTextureWithUnitAcabado(existingTexture, acabado),
                name: preservedName,
                description: preservedDescription,
                descripcion: preservedDescription,
                zoneName: zoneName || existingTexture.zoneName,
                zoneDisplayName: displayName || existingTexture.zoneDisplayName,
                zone: displayName || existingTexture.zone || existingTexture.zoneDisplayName,
              })
            } else {
              updatedTextures.push({
                id: acabado.texturas?.identificadorTextura || acabado.idAcabadoUnidad,
                name: acabado.texturas?.nombre || '',
                zoneName: zoneName,
                zoneDisplayName: displayName,
                zone: acabado.zona || '',
                url: acabado.texturas?.url || '',
                groupID: acabado.texturas?.idGruposDeTexturas || 0,
                searchID: acabado.texturas?.idTexturas || 0,
                linkedTextureID: acabado.texturas?.identificadorTextura || acabado.idAcabadoUnidad,
                color: acabado.texturas?.color || '#FFFFFF',
                repeatX: acabado.texturas?.factorRepeticionX || 1,
                repeatY: acabado.texturas?.factorRepeticionY || 1,
                opacity: acabado.texturas?.opacidad ?? 1,
                roughness: acabado.texturas?.brillo ?? 1,
                metalness: acabado.texturas?.metal ?? 0,
                envMapIntensity: acabado.texturas?.reflejo ?? 0,
                bumpScale: acabado.texturas?.rugosidad ?? 0,
                mirror: acabado.texturas?.espejo ?? false,
                modelo: acabado.modelo,
                acabado: acabado.acabado,
              })
            }
          })

          const validZonesFromAcabados = collectValidZonesFromUnitAcabados(newUnidadData.acabados)

          item.textures.forEach((existingTexture) => {
            const alreadyProcessed = textureZoneAlreadyProcessed(
              processedZoneNames,
              existingTexture,
              updatedTextures,
            )

            if (
              !alreadyProcessed &&
              isTextureValidForUnitAcabados(existingTexture, validZonesFromAcabados)
            ) {
              updatedTextures.push(existingTexture)
            }
          })

          item.textures = updatedTextures
        } else {
          const currentTexturesByZoneDisplayName = new Map<string, any>()
          const currentTexturesByZoneName = new Map<string, any>()
          item.textures.forEach((texture) => {
            if (texture.zoneDisplayName) {
              currentTexturesByZoneDisplayName.set(texture.zoneDisplayName, texture)
            }
            if (texture.zoneName) {
              currentTexturesByZoneName.set(texture.zoneName, texture)
            }
          })

          const updatedTextures: any[] = []
          const processedZones = new Set<string>()

          newUnidadData.acabados.forEach((acabado: any) => {
            const displayName = acabado.descripcion || acabado.zona
            const zoneName = acabado.zona || ''
            processedZones.add(displayName)

            const existingTexture =
              currentTexturesByZoneName.get(zoneName) ||
              currentTexturesByZoneDisplayName.get(displayName)

            const isGolaZone = displayName === 'Gola' || zoneName === 'Gola'

            if (existingTexture && !isGolaZone) {
              updatedTextures.push(reconcileExistingTextureWithUnitAcabado(existingTexture, acabado))
            } else {
              updatedTextures.push(
                reconcileExistingTextureWithUnitAcabado(existingTexture ?? {}, acabado),
              )
            }
          })

          const validZonesFromAcabados = collectValidZonesFromUnitAcabados(newUnidadData.acabados)

          item.textures.forEach((existingTexture) => {
            const alreadyProcessed = textureZoneAlreadyProcessed(
              new Set(updatedTextures.map((t) => t.zoneName).filter(Boolean)),
              existingTexture,
              updatedTextures,
            )

            if (
              !alreadyProcessed &&
              isTextureValidForUnitAcabados(existingTexture, validZonesFromAcabados)
            ) {
              updatedTextures.push(existingTexture)
            }
          })

          item.textures = updatedTextures

          userAppliedTexturesByDisplayName.clear()
          userAppliedTexturesByZoneName.clear()
          updatedTextures.forEach((texture) => {
            if (texture.zoneDisplayName) {
              userAppliedTexturesByDisplayName.set(texture.zoneDisplayName, texture)
            }
            if (texture.zoneName) {
              userAppliedTexturesByZoneName.set(texture.zoneName, texture)
            }
          })
        }
      }

      if (shouldChangeChildren && isChangingToGola) {
        const childrenToRemove: string[] = []
        item.childs.forEach((childID) => {
          const child = s.items.get(childID)
          if (child && child.type === 'linkedSon' && child.description === 'Tirador') {
            childrenToRemove.push(childID)
          }
        })

        childrenToRemove.forEach((childID) => {
          s.items.delete(childID)
          item.childs.delete(childID as any)
        })

        item.childs.forEach((childID) => {
          const child = s.items.get(childID)
          if (child && child.type === 'linkedSon') {
            s.editItem(childID as any, { visible: true })
          }
        })
      }
    })

    try {
      const newCatalogId = sceneGLBLookupKey(resolvedCatalogID)
      const variantBundle = prefetch?.variantBundle

      if (variantBundle) {
        logGola('GLB_LOAD', {
          linkedID,
          newCatalogId,
          referenciaBlender: newUnidadData.referenciaBlender,
          glbCached: true,
          prefetched: true,
        })

        set((s) => {
          if (!s.sceneGLB.has(variantBundle.codeKey)) {
            s.sceneGLB.set(variantBundle.codeKey, {
              glb: variantBundle.glb,
              backGLBInfo: variantBundle.unidadData,
            })
          }
        })

        const glbData = get().sceneGLB.get(variantBundle.codeKey) ?? {
          glb: variantBundle.glb,
        }

        set((s) => {
          const item = s.items.get(linkedID)
          if (item && item.type === 'linkedItem' && glbData?.glb) {
            rebuildLinkedItemZonesFromUnit({
              item,
              newUnidadData,
              glbSceneChildren: glbData.glb.scene?.children,
              acabadosZonasExtra: variantBundle.acabadosZonasExtra,
              userAppliedTexturesByZoneName,
              isVitrina,
              itemId: linkedID,
              source: 'updateLinkedItemGola',
            })
            if (kbrRebase) {
              refreshKbrFamilyGlbBases(item, {
                glb: (glbData as { glb: unknown }).glb,
                backGLBInfo: newUnidadData,
              })
            }
          }
        })
        logGola('GLB_LOAD_DONE', { linkedID, newCatalogId, glbCached: true, prefetched: true })
      } else {
        const glbCached = get().sceneGLB.has(newCatalogId)
        logGola('GLB_LOAD', {
          linkedID,
          newCatalogId,
          referenciaBlender: newUnidadData.referenciaBlender,
          glbCached,
        })
        if (!glbCached) {
          const glb = await loadGlbCached(newUnidadData.referenciaBlender)

          set((s) => {
            if (!s.sceneGLB.has(newCatalogId)) {
              s.sceneGLB.set(newCatalogId, {
                glb,
                backGLBInfo: newUnidadData,
              })
            }
          })
        }
        logGola('GLB_LOAD_DONE', { linkedID, newCatalogId, glbCached })

        logGolaApi('acabadosGetUnidadAcabadosInfo', {
          idUnidades: newUnidadData.idUnidades,
          linkedID,
        })
        const acabadosInfo = await API().api.acabadosGetUnidadAcabadosInfo(newUnidadData.idUnidades)
        logGolaApi(
          'acabadosGetUnidadAcabadosInfo',
          { idUnidades: newUnidadData.idUnidades, linkedID },
          acabadosInfo,
          { zonasCount: acabadosInfo?.data?.zonas?.length },
        )

        const glbData = get().sceneGLB.get(newCatalogId)
        if (glbData?.glb) {
          const extraInfo = acabadosInfo.data.zonas
            ? { zonas: acabadosInfo.data.zonas as { idAcabadoUnidad: number; zona: string }[] }
            : undefined

          set((s) => {
            const item = s.items.get(linkedID)
            if (item && item.type === 'linkedItem') {
              rebuildLinkedItemZonesFromUnit({
                item,
                newUnidadData,
                glbSceneChildren: glbData.glb.scene?.children,
                acabadosZonasExtra: extraInfo,
                userAppliedTexturesByZoneName,
                isVitrina,
                itemId: linkedID,
                source: 'updateLinkedItemGola',
              })
              if (kbrRebase) {
                refreshKbrFamilyGlbBases(item, {
                  glb: (glbData as { glb: unknown }).glb,
                  backGLBInfo: newUnidadData,
                })
              }
            }
          })
        }
      }
    } catch (error) {
      console.error('Error actualizando GLB del item principal:', error)
    }

    // Rebase KBR ↔ KGBR: encimera / tiras de zócalo / hijos al contrato nuevo.
    if (kbrRebase) {
      const rebased = kbrRebase as KbrFamilyRebaseResult
      const axes: Array<'Ancho' | 'Fondo' | 'Alto'> = []
      if (rebased.widthChanged) axes.push('Ancho')
      if (rebased.thicknessChanged) axes.push('Fondo')
      // Alto siempre: las bases free-dim (bbox/catálogo) cambian aunque el número no.
      axes.push('Alto')
      for (const axis of axes) get().updateLinkedSonPositions(linkedID, axis)
      get().setOBBScene()
    }

    if (newLinkedOptions) {
      const optionsData = extractLinkedOptionsBucket(newLinkedOptions)

      const finalItem = get().items.get(linkedID)
      if (finalItem && finalItem.type === 'linkedItem' && finalItem.childs) {
        finalItem.childs.forEach((childID) => {
          const child = get().items.get(childID)
          if (child && child.type === 'linkedSon') {
            let matchingOption = null

            if (child.description === 'TPCGrifo' && optionsData.TPCGrifo) {
              const currentDescription = child.catalogID || child.referencia
              matchingOption = optionsData.TPCGrifo.find(
                (opt: any) => opt.descripcion === currentDescription,
              )
            } else if (child.description === 'Fregadero' && optionsData.Fregadero) {
              const currentDescription = child.catalogID || child.referencia

              matchingOption = optionsData.Fregadero.find(
                (opt: any) => opt.descripcion === currentDescription,
              )
            } else if (child.description === 'Encimera' && optionsData.Encimera) {
              const currentDescription = child.catalogID || child.referencia

              matchingOption = optionsData.Encimera.find(
                (opt: any) => opt.descripcion === currentDescription,
              )
            }

            if (matchingOption) {
              get().editItem(childID as any, {
                linkedID: matchingOption.idElementosEnlazados,
                idUnidades: matchingOption.idUnidadEnlazada,
                catalogID: matchingOption.descripcion,
                referencia: matchingOption.descripcion,
              })
            }
          }
        })
      }
    }

    logGola('CHILDREN_DECISION', {
      linkedID,
      shouldChangeChildren,
      isChangingFromGola,
      isChangingToGola,
      willRestoreTiradores: shouldChangeChildren && isChangingFromGola,
      willRemoveTiradores: shouldChangeChildren && isChangingToGola,
      previousCatalogID,
      newCatalogID,
    })

    if (shouldChangeChildren && isChangingFromGola) {
      try {
        logGola('CALL_updateLinkedSonsForNewGLB', {
          linkedID,
          newCatalogId: newUnidadData.codigo || newUnidadData.referencia,
          previousCatalogID,
        })
        await store.updateLinkedSonsForNewGLB(
          linkedID,
          newUnidadData.codigo || newUnidadData.referencia,
          previousCatalogID,
        )
        // GLB de Gola recrea tiradores con coords API; alinear a dims actuales.
        get().updateTiradorPositionsOnly(linkedID)
        logGola('RESULT_updateLinkedSonsForNewGLB', { linkedID })
        logGolaLinkedChildrenSnapshot(
          'after_restore_tiradores',
          linkedID,
          (id) => get().items.get(id as AnyID),
          get().items.get(linkedID)?.childs ?? [],
        )
      } catch (error) {
        logGola('RESTORE_TIRADORES_ERROR', { linkedID, error: String(error) })
        console.error('GOLA UPDATE - Error restaurando tiradores:', error)
      }
    }

    // Solo establecer visibilidad si NO estamos cambiando el estado de gola
    // Cuando cambiamos gola, la visibilidad se maneja en updateLinkedSonsForNewGLB
    const isRestoringTiradores = shouldChangeChildren && isChangingFromGola
    if (!isRestoringTiradores) {
      const finalUpdatedItem = get().items.get(linkedID)
      if (finalUpdatedItem && finalUpdatedItem.type === 'linkedItem' && finalUpdatedItem.childs) {
        finalUpdatedItem.childs.forEach((childID) => {
          const child = get().items.get(childID)
          if (child && child.type === 'linkedSon') {
            get().editItem(childID as any, { visible: true })
          }
        })
      }
    }

    // NO ajustar posiciones de hijos cuando solo se cambia un acabado (ej: Gola)
    // Solo ajustar cuando cambia la altura del padre (morphs de Alto)
    // Esto evita que cambiar acabados de zonas modifique incorrectamente las posiciones de los linkedSon
    const updatedItem = get().items.get(linkedID)
    const previousHeight = item.height
    const newHeight = updatedItem?.height

    // Solo ajustar posiciones si cambió la altura del padre
    const heightChanged = previousHeight !== newHeight

    if (
      heightChanged &&
      updatedItem &&
      updatedItem.type === 'linkedItem' &&
      updatedItem.childs &&
      Array.isArray(updatedItem.childs) &&
      updatedItem.childs.length > 0
    ) {
      adjustChildrenPositionsAfterInheritance(
        updatedItem,
        (childId) => get().items.get(childId),
        (childId, updates) => {
          get().editItem(childId, updates)
        },
      )
    }

    if (newLinkedOptions) {
      const contextUpdateEvent = new CustomEvent('linkedOptionsUpdated', {
        detail: {
          linkedID,
          newLinkedOptions,
          targetIdUnidades: newUnidadData.idUnidades,
        },
      })
      window.dispatchEvent(contextUpdateEvent)

      set((s) => {
        const item = s.items.get(linkedID as AnyID)
        if (item && isLinkedItem(item)) {
          item.lastLinkedOptionsUpdate = Date.now()
        }
      })

      const contextUpdateEvent2 = new CustomEvent('linkedOptionsUpdated', {
        detail: {
          linkedID,
          newLinkedOptions,
          targetIdUnidades: newUnidadData.idUnidades,
          forceUpdate: true,
        },
      })
      window.dispatchEvent(contextUpdateEvent2)

      // Un solo evento de sincronización; el retraso de 200ms duplicaba trabajo en toggles Gola masivos.
    }

    const finalItem = get().items.get(linkedID)
    if (finalItem && isLinkedItem(finalItem)) {
      logGolaItemTexturesSnapshot(
        'updateLinkedItemGola_after',
        linkedID,
        finalItem.textures as Array<Record<string, unknown>>,
        String(finalItem.catalogID || ''),
      )
      if (finalItem.childs) {
        logGolaLinkedChildrenSnapshot(
          'updateLinkedItemGola_after',
          linkedID,
          (id) => get().items.get(id as AnyID),
          finalItem.childs,
        )
      }
    }
    logGola('updateLinkedItemGola_DONE', {
      linkedID,
      catalogID: finalItem?.catalogID,
      idUnidades: isLinkedItem(finalItem) ? finalItem.idUnidades : null,
      idcambiogolablanco: isLinkedItem(finalItem) ? finalItem.idcambiogolablanco : null,
      idcambiogolapetroleo: isLinkedItem(finalItem) ? finalItem.idcambiogolapetroleo : null,
      idcambioarmazon: isLinkedItem(finalItem) ? finalItem.idcambioarmazon : null,
    })
  },
  updateLinkedItemArmazonOptimized: async (linkedID: LinkedItemsID, newUnidadData: any) => {
    const parentItem = get().items.get(linkedID)
    if (!isLinkedItem(parentItem)) {
      return
    }

    // const parentInfo = {
    //   catalogID: parentItem.catalogID,
    //   tipoNormalizado: parentItem.tipoNormalizado,
    // }

    // const parentId = linkedID
    const newIdUnidades = newUnidadData.idUnidades || parentItem.idUnidades
    // Guardar el catalogID antes de actualizar para poder actualizar sceneGLB después
    const catalogIDAntesDeActualizar = String(parentItem.catalogID || '')

    // Obtener tiradorOptions sin cargar GLB
    let newTiradorOptions: any[] | null = null
    try {
      const tiradorOptionsResponse =
        await API().api.acabadosGetElementosEnlazadosUnidadAcabados(newIdUnidades)

      if (
        tiradorOptionsResponse?.data &&
        tiradorOptionsResponse.data['0'] &&
        Array.isArray(tiradorOptionsResponse.data['0'].Tirador)
      ) {
        newTiradorOptions = tiradorOptionsResponse.data['0'].Tirador as any[]
      }
    } catch (error) {
      console.error('Error obteniendo tiradorOptions:', error)
    }

    // Procesar tiradores (igual que antes)
    const tiradorElementsById = new Map<number, any>()
    const tiradorElementsByNode = new Map<number, any>()

    if (Array.isArray(newUnidadData.elementosEnlazados)) {
      newUnidadData.elementosEnlazados.forEach((element: any) => {
        if (element?.descripcion !== 'Tirador') return

        if (typeof element.idElementosEnlazados === 'number') {
          tiradorElementsById.set(element.idElementosEnlazados, element)
        }

        if (typeof element.nodo === 'number' && !tiradorElementsByNode.has(element.nodo)) {
          tiradorElementsByNode.set(element.nodo, element)
        }
      })
    }

    const tiradorUpdatesByChild = new Map<string, { option?: any; positions?: any }>()

    if (parentItem.childs?.size > 0) {
      for (const childID of parentItem.childs) {
        const childItem = get().items.get(childID)
        if (!childItem || childItem.type !== 'linkedSon' || childItem.description !== 'Tirador') {
          continue
        }

        const childNode = childItem.option ?? childItem.nodo
        const childDescription =
          childItem.catalogID || childItem.referencia || childItem.description

        let targetOption: any = null
        if (Array.isArray(newTiradorOptions)) {
          targetOption = newTiradorOptions.find(
            (option: any) => option.nodo === childNode && option.descripcion === childDescription,
          )

          if (!targetOption) {
            targetOption = newTiradorOptions.find(
              (option: any) => option.nodo === childNode && option.porDefecto,
            )
          }

          if (!targetOption) {
            targetOption = newTiradorOptions.find((option: any) => option.nodo === childNode)
          }
        }

        let positions =
          (targetOption && tiradorElementsById.get(targetOption.idElementosEnlazados)) ||
          (childItem.linkedID ? tiradorElementsById.get(childItem.linkedID) : null) ||
          (typeof childNode === 'number' ? tiradorElementsByNode.get(childNode) : null)

        if (targetOption) {
          try {
            const response = await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
              newIdUnidades,
              targetOption.idElementosEnlazados,
            )

            if (response?.data) {
              positions = response.data
            }
          } catch (error) {
            console.error('Error obteniendo posiciones del tirador:', error)
          }
        }

        tiradorUpdatesByChild.set(childID as string, {
          option: targetOption ?? undefined,
          positions: positions ?? undefined,
        })
      }
    }

    // Actualizar propiedades del item SIN cargar GLB
    set((s) => {
      const item = s.items.get(linkedID)
      if (!isLinkedItem(item)) {
        return
      }

      if (Array.isArray(newTiradorOptions)) {
        item.tiradorOptions = newTiradorOptions as any[]
      }

      if (newUnidadData.idUnidades) {
        item.idUnidades = newUnidadData.idUnidades
        invalidateLinkedTextureCaches()
      }

      const armazonBlancoFromBackend =
        newUnidadData.ArmazonBlanco !== undefined
          ? newUnidadData.ArmazonBlanco
          : (newUnidadData as any).armazonBlanco !== undefined
            ? (newUnidadData as any).armazonBlanco
            : undefined

      if (typeof armazonBlancoFromBackend === 'boolean') {
        item.ArmazonBlanco = armazonBlancoFromBackend
      } else {
        // Si no viene del backend, invertir el valor actual
        item.ArmazonBlanco = !item.ArmazonBlanco
      }

      // Actualizar catalogID para reflejar correctamente el armazón (blanco/petróleo)
      // El prefijo "PE " indica que es petróleo, sin prefijo es blanco
      // Priorizar newUnidadData.codigo si está disponible, sino sincronizar con ArmazonBlanco
      if (newUnidadData.codigo) {
        item.catalogID = newUnidadData.codigo
      } else {
        // Si no hay codigo en newUnidadData, sincronizar catalogID con ArmazonBlanco
        const currentCatalogID = String(item.catalogID || '')
        const baseCatalogID = currentCatalogID.startsWith('PE ')
          ? currentCatalogID.substring(3)
          : currentCatalogID

        if (item.ArmazonBlanco) {
          // Si es blanco, asegurar que no tenga prefijo "PE "
          if (currentCatalogID.startsWith('PE ')) {
            item.catalogID = baseCatalogID
          }
        } else {
          // Si es petróleo, asegurar que tenga prefijo "PE "
          if (!currentCatalogID.startsWith('PE ')) {
            item.catalogID = `PE ${baseCatalogID}`
          }
        }
      }

      // Forzar actualización del contexto incrementando un contador interno
      // Esto asegura que el objeto se actualice en LinkedItemZoneContext
      const itemAny = item as any
      if (!itemAny._armazonUpdateCounter) {
        itemAny._armazonUpdateCounter = 0
      }
      itemAny._armazonUpdateCounter++
      if (newUnidadData.idCambioArmazon) {
        item.idcambioarmazon = newUnidadData.idCambioArmazon
      }
      if (newUnidadData.idCambioGolaBlanco) {
        item.idcambiogolablanco = newUnidadData.idCambioGolaBlanco
      }
      if (newUnidadData.idCambioGolaPetroleo) {
        item.idcambiogolapetroleo = newUnidadData.idCambioGolaPetroleo
      }

      if (typeof newUnidadData.referencia === 'string') {
        item.referencia = newUnidadData.referencia
      }
      // NO actualizar glbPath - el GLB debe permanecer igual, solo cambia la textura del armazón
      // El cambio de armazón solo afecta el color/textura, no el modelo 3D
      // if (typeof newUnidadData.referenciaBlender === 'string' && newUnidadData.referenciaBlender) {
      //   item.glbPath = newUnidadData.referenciaBlender
      // }
      if (typeof newUnidadData.referenciaJpgpng === 'string') {
        item.image = newUnidadData.referenciaJpgpng
      }

      // Actualizar morphs (igual que antes)
      if (newUnidadData.morphs && Array.isArray(newUnidadData.morphs)) {
        item.morphs?.forEach((m) => {
          if (m.name === 'Ancho') {
            m.value = item.width
            m.default = item.width
            m._default = item.width
          }
          if (m.name === 'Alto') {
            m.value = item.height
            m.default = item.height
            m._default = item.height
          }
          if (m.name === 'Fondo') {
            m.value = item.thickness
            m.default = item.thickness
            m._default = item.thickness
          }
        })

        const morphsToPreserve =
          item.morphs?.map((m) => ({
            name: m.name,
            value: m.value ?? m.default ?? m._default,
          })) || []

        item.morphs = newUnidadData.morphs.map((morph: any) => ({
          _default: morph.valor || morph.default,
          default: morph.valor || morph.default,
          max: morph.valorMax || morph.max,
          min: morph.valorMin || morph.min,
          modifyable: true,
          morphId: morph.idMorph || morph.id,
          name: morph.nombre || morph.name,
          order: morph.orden || morph.order,
          show: true,
          type: morph.tipo || morph.type,
          value: morph.valor || morph.default,
        }))

        item.morphs.forEach((m) => {
          const preserved = morphsToPreserve.find((pm) => pm.name === m.name)
          if (preserved) {
            m.value = preserved.value
            m.default = preserved.value
          }
        })

        item.morphs.forEach((m) => {
          if (m.name === 'Ancho') {
            item.width = m.value
          } else if (m.name === 'Alto') {
            item.height = m.value
          } else if (m.name === 'Fondo') {
            item.thickness = m.value
          }
        })
      }

      // ACTUALIZAR SOLO TEXTURAS DE ZONA "Armazon" (sin procesar GLB)
      if (newUnidadData.acabados && Array.isArray(newUnidadData.acabados)) {
        // Actualizar texturas
        item.textures = item.textures.map((texture) => {
          if (texture.zoneDisplayName !== 'Armazon') {
            return texture
          }

          const matchingAcabado = newUnidadData.acabados.find((acabado: any) => {
            return (
              acabado.acabado?.toLowerCase().includes('armazon') ||
              acabado.acabado?.toLowerCase().includes('basico') ||
              acabado.acabado?.toLowerCase().includes('petroleo')
            )
          })

          if (matchingAcabado) {
            return {
              ...texture,
              linkedTextureID:
                matchingAcabado.texturas?.identificadorTextura || matchingAcabado.idAcabadoUnidad,
              id: matchingAcabado.texturas?.identificadorTextura || matchingAcabado.idAcabadoUnidad,
              name: matchingAcabado.texturas?.nombre || texture.name,
              url: matchingAcabado.texturas?.url || texture.url,
              searchID: matchingAcabado.texturas?.idTexturas || texture.searchID,
              groupID: matchingAcabado.texturas?.idGruposDeTexturas || texture.groupID,
              acabado: matchingAcabado.texturas?.descripcion || texture.acabado,
            }
          }
          return texture
        })

        // Actualizar zonas (materiales) — solo meshes con zoneDisplayName Armazon
        Object.keys(item.zones).forEach((zoneKey) => {
          const zone = item.zones[zoneKey]
          if (zone.material?.zoneDisplayName !== 'Armazon') return

          const matchingTexture = item.textures.find(
            (texture) =>
              texture.zoneDisplayName === 'Armazon' &&
              (texture.zoneName === zoneKey || texture.zoneName === zone.name),
          )

          if (!matchingTexture) return

          const matchingAcabado = newUnidadData.acabados.find((acabado: any) => {
            return (
              acabado.acabado?.toLowerCase().includes('armazon') ||
              acabado.acabado?.toLowerCase().includes('basico') ||
              acabado.acabado?.toLowerCase().includes('petroleo')
            )
          })

          if (matchingAcabado?.texturas) {
            zone.material = {
              ...zone.material,
              color: matchingAcabado.texturas.color || zone.material.color,
              url: matchingAcabado.texturas.url || zone.material.url,
              name: matchingAcabado.texturas.nombre || zone.material.name,
              groupID: matchingAcabado.texturas.idGruposDeTexturas || zone.material.groupID,
              searchID: matchingAcabado.texturas.idTexturas || zone.material.searchID,
              id: matchingAcabado.texturas.identificadorTextura || zone.material.id,
            }
          }
        })
      }

      syncLinkedItemZonesFromTextures(item, {
        itemId: linkedID,
        catalogID: String(item.catalogID ?? ''),
        source: 'updateLinkedItemArmazonOptimized',
      })

      // Actualizar hijos (tiradores, encimeras) - igual que antes
      if (item.childs?.size > 0) {
        item.childs.forEach((childID) => {
          const childItem = s.items.get(childID)
          if (childItem && childItem.type === 'linkedSon') {
            if (childItem.description === 'Tirador') {
              const tiradorUpdate = tiradorUpdatesByChild.get(childID as string)
              const tiradorNewPositions = tiradorUpdate?.positions

              if (tiradorUpdate?.option) {
                childItem.linkedID = tiradorUpdate.option.idElementosEnlazados
                childItem.idElementosEnlazados = tiradorUpdate.option.idElementosEnlazados
                childItem.idUnidades = tiradorUpdate.option.idUnidadEnlazada
                childItem.catalogID = tiradorUpdate.option.descripcion ?? childItem.catalogID
                childItem.code = tiradorUpdate.option.descripcion ?? childItem.code
                childItem.referencia = tiradorUpdate.option.descripcion ?? childItem.referencia
              }

              if (tiradorNewPositions) {
                const posicionesAlternativas = [
                  'biPosicionX',
                  'biPosicionY',
                  'biPosicionZ',
                  'bcPosicionX',
                  'bcPosicionY',
                  'bcPosicionZ',
                  'bdPosicionX',
                  'bdPosicionY',
                  'bdPosicionZ',
                  'aiPosicionX',
                  'aiPosicionY',
                  'aiPosicionZ',
                  'acPosicionX',
                  'acPosicionY',
                  'acPosicionZ',
                  'adPosicionX',
                  'adPosicionY',
                  'adPosicionZ',
                  'biRotacionX',
                  'biRotacionY',
                  'biRotacionZ',
                  'bcRotacionX',
                  'bcRotacionY',
                  'bcRotacionZ',
                  'bdRotacionX',
                  'bdRotacionY',
                  'bdRotacionZ',
                  'aiRotacionX',
                  'aiRotacionY',
                  'aiRotacionZ',
                  'acRotacionX',
                  'acRotacionY',
                  'acRotacionZ',
                  'adRotacionX',
                  'adRotacionY',
                  'adRotacionZ',
                  'b2idRotacionX',
                  'b2idRotacionY',
                  'b2idRotacionZ',
                  'a2idRotacionX',
                  'a2idRotacionY',
                  'a2idRotacionZ',
                ]

                posicionesAlternativas.forEach((key) => {
                  if (Object.prototype.hasOwnProperty.call(tiradorNewPositions, key)) {
                    ;(childItem as any)[key] = tiradorNewPositions[key]
                  }
                })

                const acabadosTirador = tiradorNewPositions?.unidad?.acabados
                if (Array.isArray(acabadosTirador)) {
                  const existingZoneKeys = Object.keys(childItem.zones || {})
                  const zoneKey = existingZoneKeys.length > 0 ? existingZoneKeys[0] : 'Tirador'

                  childItem.textures = acabadosTirador.map((acabado: any) => ({
                    id: acabado.texturas?.idTexturas || acabado.idAcabadoUnidad || 0,
                    name: acabado.texturas?.nombre || 'Tirador',
                    zoneName: zoneKey,
                    zoneDisplayName: acabado.descripcion || 'Tirador',
                    zone: zoneKey,
                    url: acabado.texturas?.url || '',
                    description: acabado.texturas?.descripcion || '',
                    groupID: acabado.texturas?.idGruposDeTexturas || 0,
                    searchID: acabado.texturas?.idTexturas || 0,
                    color: acabado.texturas?.color || '#FFFFFF',
                    modelo: acabado.modelo || null,
                    acabado: acabado.acabado || null,
                    linkedTextureID: acabado.idAcabadoUnidad,
                  }))

                  acabadosTirador.forEach((acabado: any) => {
                    if (!childItem.zones) {
                      childItem.zones = {}
                    }

                    if (childItem.zones[zoneKey]) {
                      childItem.zones[zoneKey].material = {
                        id: acabado.texturas?.idTexturas || acabado.idAcabadoUnidad || 0,
                        searchID: acabado.texturas?.idTexturas || 0,
                        name: acabado.texturas?.nombre || 'Tirador',
                        url: acabado.texturas?.url || '',
                        description: acabado.texturas?.descripcion || '',
                        acabado: acabado.acabado || null,
                        groupID: acabado.texturas?.idGruposDeTexturas || 0,
                        color: acabado.texturas?.color || '#FFFFFF',
                        roughness: acabado.texturas?.brillo ?? 1,
                        metalness: acabado.texturas?.metal ?? 0,
                        envMapIntensity: acabado.texturas?.reflejo ?? 0,
                        bumpScale: acabado.texturas?.rugosidad ?? 0,
                        opacity: acabado.texturas?.opacidad ?? 1,
                        repeatX: acabado.texturas?.factorRepeticionX || 1,
                        repeatY: acabado.texturas?.factorRepeticionY || 1,
                        textureRotation: 0,
                        mirror: acabado.texturas?.espejo ?? false,
                        displacement: 0,
                        displacementScale: 1,
                        bumpScale: 0,
                        bumpMap: null,
                      }
                    }
                  })
                }

                const recalculatedPosition = calculateTiradorPosition(childItem, item)
                childItem.x = recalculatedPosition.x
                childItem.y = recalculatedPosition.y
                childItem.z = recalculatedPosition.z
                childItem.visible = !(childItem.x === 0 && childItem.y === 0 && childItem.z === 0)
              }
            } else if (childItem.description === 'Encimera') {
              const relativeX = (childItem.x ?? 0) - (item.x ?? 0)
              const relativeZ = (childItem.z ?? 0) - (item.z ?? 0)

              childItem.x = (item.x ?? 0) + relativeX
              childItem.z = (item.z ?? 0) + relativeZ
              if (usesFreeDimensionScaling(item)) {
                placeLinkedEncimeraY(childItem, item)
              } else {
                childItem.y = (item.y ?? 0) + (item.height ?? 0)
              }
            }
          }
        })
      }

      // Actualizar sceneGLB para que el nuevo catalogID también apunte al mismo GLB
      // Esto es necesario porque useGLBInstance busca el GLB usando el catalogID
      const itemAfterUpdate = s.items.get(linkedID)
      if (isLinkedItem(itemAfterUpdate)) {
        const nuevoCatalogID = String(itemAfterUpdate.catalogID || '')

        // Si el catalogID cambió, actualizar sceneGLB
        if (nuevoCatalogID !== catalogIDAntesDeActualizar && catalogIDAntesDeActualizar) {
          const glbDataAntes = s.sceneGLB.get(catalogIDAntesDeActualizar)
          if (glbDataAntes && !s.sceneGLB.has(nuevoCatalogID)) {
            // Copiar el GLB al nuevo catalogID para que useGLBInstance lo encuentre
            s.sceneGLB.set(nuevoCatalogID, glbDataAntes)
          }
        }
      }
    })
  },
  setLinkedTiradorPosition: (linkedID: string, x: number, y: number, z: number) => {
    set((s) => {
      const item = s.items.get(linkedID as AnyID)
      if (item && item.type === 'linkedSon' && item.description === 'Tirador') {
        s.items.set(linkedID as AnyID, { ...item, x, y, z })
      }
    })
  },
  setLinkedHingePosition: (linkedID: LinkedItemID, hinge: 'Interior' | 'Exterior') => {
    set((s) => {
      const item = s.items.get(linkedID)
      if (item && item.type === 'linkedItem') {
        const hingeBoolean = hinge === 'Interior'
        s.items.set(linkedID, { ...item, hinge: hingeBoolean })
      }
    })
  },
  updateLinkedSonsForNewGLB: async (
    itemId: string,
    newCatalogId: string,
    previousCatalogId?: string,
    options?: {
      forceRestoreDefaultTiradores?: boolean
      useCatalogDefaultTextures?: boolean
      /** Evita fetch redundante cuando el batch ya precargó opciones enlazadas. */
      linkedOptionsPayload?: Record<string, unknown>
    },
  ) => {
    logAcabado('updateLinkedSonsForNewGLB_START', {
      itemId,
      newCatalogId,
      previousCatalogId,
    })
    logGola('updateLinkedSonsForNewGLB_START', {
      itemId,
      newCatalogId,
      previousCatalogId,
    })

    const store = get()
    const linkedItem = store.items.get(itemId)

    if (!linkedItem || linkedItem.type !== 'linkedItem' || !linkedItem.idUnidades) {
      logAcabado('updateLinkedSonsForNewGLB_ABORT', { itemId, reason: 'invalid_item' })
      return false
    }

    // Usar el catalogID anterior si se proporciona, sino usar el actual del linkedItem
    // Esto es importante porque el catalogID del linkedItem puede haber sido actualizado
    // antes de llamar a esta función
    const currentCatalogID = previousCatalogId || linkedItem.catalogID?.toString() || ''

    try {
      const linkedOptionsPayload =
        options?.linkedOptionsPayload ??
        (await fetchLinkedUnitOptions(linkedItem.idUnidades))
      const newLinkedOptions = { data: linkedOptionsPayload }

      if (!newLinkedOptions?.data) {
        return false
      }

      const codeKey = sceneGLBLookupKey(newCatalogId)
      const cachedUnidad = get().sceneGLB.get(codeKey)?.backGLBInfo as
        | Record<string, unknown>
        | undefined

      let newLinkedData: { data?: Record<string, unknown> } | null = null
      if (cachedUnidad) {
        newLinkedData = { data: cachedUnidad }
      } else {
        try {
          newLinkedData = await API().api.unidadesGetUnidadPost({
            codigo: newCatalogId,
            date: '2023-05-12',
            catalogoId: newCatalogId,
          })
        } catch (error) {
          logAcabado('updateLinkedSonsForNewGLB_UNIDAD_FAIL', {
            itemId,
            newCatalogId,
            error: String(error),
          })
          return false
        }
      }

      if (!newLinkedData?.data) {
        return false
      }

      const elementosEnlazados = newLinkedData.data.elementosEnlazados || []

      const newAvailableElements = new Map<string, any>()

      if (Array.isArray(elementosEnlazados)) {
        elementosEnlazados.forEach((element: any) => {
          if (
            element.nodoPadre !== undefined &&
            element.nodo !== undefined &&
            element.descripcion
          ) {
            const key = `${element.nodoPadre}-${element.nodo}-${element.descripcion}`
            newAvailableElements.set(key, element)
          }
        })
      }

      for (const [parentNode, childNodeObj] of Object.entries(newLinkedOptions.data)) {
        for (const [childNode, elements] of Object.entries(childNodeObj as any)) {
          elements.forEach((element: any) => {
            const key = `${parentNode}-${element.nodo}-${element.descripcion}`

            if (!newAvailableElements.has(key) && Array.isArray(elementosEnlazados)) {
              const fullElement = elementosEnlazados.find(
                (el: any) =>
                  el.nodoPadre === parseInt(parentNode) &&
                  el.nodo === element.nodo &&
                  el.descripcion === element.descripcion,
              )
              if (fullElement) {
                newAvailableElements.set(key, fullElement)
              }
            }
          })
        }
      }

      if (newAvailableElements.size === 0 && Array.isArray(elementosEnlazados)) {
        elementosEnlazados.forEach((element: any) => {
          if (
            element.nodoPadre !== undefined &&
            element.nodo !== undefined &&
            element.descripcion
          ) {
            const key = `${element.nodoPadre}-${element.nodo}-${element.descripcion}`
            newAvailableElements.set(key, element)
          }
        })
      }

      // Detectar si estamos cambiando de gola a no-gola, viceversa, o entre diferentes tipos de gola
      const isCurrentGola = currentCatalogID.includes(' GO')
      const isNewGola = newCatalogId.includes(' GO')
      const isChangingGolaState = isCurrentGola !== isNewGola

      // Si ambas unidades tienen " GO", también estamos cambiando el estado de gola
      // (cambiamos de un tipo de gola a otro, por lo que los tiradores pueden cambiar)
      const isChangingBetweenGolaTypes =
        isCurrentGola && isNewGola && currentCatalogID !== newCatalogId

      const forceRestoreDefaultTiradores = options?.forceRestoreDefaultTiradores === true
      // Solo procesar tiradores cuando cambiamos gola o forzamos restauración al catálogo
      const shouldOnlyProcessTiradores =
        forceRestoreDefaultTiradores || isChangingGolaState || isChangingBetweenGolaTypes

      const currentChildren = Array.from(linkedItem.childs || [])

      const childrenToRemove: string[] = []
      const childrenToKeep: string[] = []
      const existingChildKeys = new Set()

      for (const childId of currentChildren) {
        const child = store.items.get(childId as AnyID)
        if (child && child.type === 'linkedSon') {
          const linkedSon = child as LinkedItemSon
          const childKey = `${linkedSon.parentOption}-${linkedSon.option}-${linkedSon.description}`

          // Zócalo independiente (cliente): nunca borrar al cambiar acabado/GLB.
          if (isIndependentZocaloLinkedSon(linkedSon)) {
            childrenToKeep.push(childId)
            existingChildKeys.add(childKey)
            continue
          }

          if (forceRestoreDefaultTiradores && linkedSon.description === 'Tirador') {
            childrenToRemove.push(childId)
            continue
          }

          if (shouldOnlyProcessTiradores && linkedSon.description !== 'Tirador') {
            // Mantener todos los hijos que NO son tiradores sin cambios
            childrenToKeep.push(childId)
            existingChildKeys.add(childKey)
            continue
          }

          // Para tiradores o cuando NO estamos cambiando el estado de gola
          if (newAvailableElements.has(childKey)) {
            childrenToKeep.push(childId)
            existingChildKeys.add(childKey)
          } else {
            if (shouldOnlyProcessTiradores) {
              if (linkedSon.description === 'Tirador') {
                childrenToRemove.push(childId)
              } else {
                childrenToKeep.push(childId)
              }
            } else {
              childrenToRemove.push(childId)
            }
          }
        }
      }

      const childrenToAdd: string[] = []
      for (const [key, element] of newAvailableElements) {
        if (!existingChildKeys.has(key)) {
          // Si estamos cambiando el estado de gola o entre tipos de gola, solo agregar tiradores
          if (shouldOnlyProcessTiradores && element.descripcion !== 'Tirador') {
            continue
          }
          childrenToAdd.push(key)
        }
      }

      if (childrenToRemove.length > 0 || childrenToAdd.length > 0) {
        set((s) => {
          const newItems = new Map(s.items)
          const updatedLinkedItem = { ...newItems.get(itemId) }

          if (!updatedLinkedItem || updatedLinkedItem.type !== 'linkedItem') {
            return s
          }

          for (const childId of childrenToRemove) {
            if (shouldOnlyProcessTiradores) {
              const child = newItems.get(childId as AnyID)
              if (child && child.type === 'linkedSon') {
                const linkedSon = child as LinkedItemSon
                if (linkedSon.description === 'Tirador') {
                  newItems.delete(childId as AnyID)
                }
              }
            } else {
              newItems.delete(childId as AnyID)
            }
          }

          const finalChildrenToKeep = [...childrenToKeep]
          if (shouldOnlyProcessTiradores) {
            for (const childId of currentChildren) {
              if (!finalChildrenToKeep.includes(childId)) {
                const child = store.items.get(childId as AnyID)
                if (child && child.type === 'linkedSon') {
                  const linkedSon = child as LinkedItemSon
                  if (linkedSon.description !== 'Tirador') {
                    finalChildrenToKeep.push(childId)
                  }
                }
              }
            }
          }

          const newChildren = [...finalChildrenToKeep]
          const newChildrenOrder: string[] = []

          for (const addKey of childrenToAdd) {
            const [parentNodeStr, childNodeStr, description] = addKey.split('-')
            const parentNode = parseInt(parentNodeStr)
            const childNode = parseInt(childNodeStr)

            const elementData = newAvailableElements.get(addKey)
            if (elementData) {
              const linkedID = getId('linkedSon')

              let initialX = elementData.posicionX || 0
              let initialY = elementData.posicionY || 0
              let initialZ = elementData.posicionZ || 0

              let prefix = 'b'
              if (linkedItem.tipoNormalizado === 'MAL' || linkedItem.tipoNormalizado === 'COL') {
                prefix = 'a'
              }

              if (
                elementData[`${prefix}cPosicionX`] !== null &&
                elementData[`${prefix}cPosicionX`] !== undefined
              ) {
                initialX = elementData[`${prefix}cPosicionX`]
                initialY = elementData[`${prefix}cPosicionY`] || initialY
                initialZ = elementData[`${prefix}cPosicionZ`] || initialZ
              }

              const handPosition = 'Centro'

              const parentForGola = get().items.get(itemId)
              const isGolaUnit =
                isLinkedItem(parentForGola) && isGolaEffective(parentForGola as LinkedItem)
              const isTirador = elementData.descripcion === 'Tirador'
              const shouldBeVisible = resolveLinkedSonVisibility(
                elementData,
                prefix,
                { x: initialX, y: initialY, z: initialZ },
                { isGolaUnit, isTirador },
              )

              const linkedSon: LinkedItemSon = {
                id: linkedID,
                type: 'linkedSon',
                catalogID: elementData.unidad?.codigo || '',
                description: elementData.descripcion,
                parentOption: parentNode,
                option: childNode,
                nodo: childNode,
                idElementosEnlazados: elementData.idElementosEnlazados,
                x: initialX,
                y: initialY,
                z: initialZ,
                visible: shouldBeVisible,
                increment: `${s.increment}`,
                firstParentID: itemId,
                quantity: elementData.cantidad || 1,
                code: elementData.unidad?.codigo || '',
                isUnlinkable: !elementData.desenlazar,
                linkedID: elementData.idElementosEnlazados,
                visualOrder: 1,
                typeOfSon: elementData.descripcion || 'Tirador',
                referenciaBlender: elementData.unidad?.referenciaBlender,
                childs: new Set(),
                matrix: new Matrix4(),
                handPosition: handPosition,

                textures: [],
                zones: {},
                morphs: [],

                width: elementData.unidad?.ancho || 0,
                height: elementData.unidad?.alto || 0,
                thickness: elementData.unidad?.fondo || 0,
                rotation: 0,
                selected: false,
                glbPath: elementData.unidad?.referenciaBlender || '',
                glbSize: undefined,
                price: 0,
                image: '',
                long:
                  elementData.unidad?.descripcionLarga ||
                  elementData.unidad?.descripcion ||
                  elementData.unidad?.codigo ||
                  elementData.descripcion ||
                  '',
                url: '',
                universal: false,
                canCollide: true,
                boundingBox: undefined,
                hasMorphs: false,
                isBlocked: false,
                herenciaProcessed: false,
                hand: elementData.unidad?.mano || '',
                mano: elementData.unidad?.mano || '',
                depth: 1,
                snappedWall: null,
                paginaPdf: null,
                regleta: null,
                regletaMinimo: null,
                regletaMaximo: null,
                idcambioarmazon: null,
                idcambiogolablanco: null,
                idcambiogolapetroleo: null,
                hinge: false,
                bisagra: false,
                ArmazonBlanco: false,
                idUnidades: elementData.unidad?.idUnidades || 0,
                referencia: elementData.unidad?.referencia || '',
                tipoNormalizado: elementData.unidad?.tipoNormalizado || '',
                tipoUnidNorm: elementData.unidad?.tipoUnidNorm || '',
                // Propiedades adicionales que pueden ser null
                a2idPosicionX: null,
                a2idPosicionY: null,
                a2idPosicionZ: null,
                a2idRotacionX: null,
                a2idRotacionY: null,
                a2idRotacionZ: null,
                acPosicionX: null,
                acPosicionY: null,
                acPosicionZ: null,
                acRotacionX: null,
                acRotacionY: null,
                acRotacionZ: null,
                adPosicionX: null,
                adPosicionY: null,
                adPosicionZ: null,
                adRotacionX: null,
                adRotacionY: null,
                adRotacionZ: null,
                aiPosicionX: null,
                aiPosicionY: null,
                aiPosicionZ: null,
                aiRotacionX: null,
                aiRotacionY: null,
                aiRotacionZ: null,
                b2idPosicionX: null,
                b2idPosicionY: null,
                b2idPosicionZ: null,
                b2idRotacionX: null,
                b2idRotacionY: null,
                b2idRotacionZ: null,
                bcRotacionX: null,
                bcRotacionY: null,
                bcRotacionZ: null,
                bdRotacionX: null,
                bdRotacionY: null,
                bdRotacionZ: null,
                biRotacionX: null,
                biRotacionY: null,
                biRotacionZ: null,
                rotacionX: null,
                rotacionY: null,
                rotacionZ: null,
              } as LinkedItemSon

              void API()
                .api.acabadosGetElementosEnlazadosUnidadAcabados2(
                  elementData.idElementosEnlazados,
                  elementData.idElementosEnlazados,
                )
                .then((acabadosResp) => {
                  const unidadInfo = acabadosResp?.data?.unidad
                  if (!unidadInfo) return

                  const mappedTextures = Array.isArray(unidadInfo.acabados)
                    ? unidadInfo.acabados.map((acabado: any) => ({
                        id: acabado.texturas?.idTexturas || acabado.idAcabadoUnidad || 0,
                        name: acabado.zona || acabado.texturas?.nombre || 'Tirador',
                        zoneName: acabado.zona || 'Tirador',
                        zoneDisplayName: acabado.descripcion || acabado.zona || 'Tirador',
                        zone: acabado.zona || 'Tirador',
                        url: acabado.texturas?.url || '',
                        description: acabado.texturas?.descripcion || '',
                        groupID: acabado.texturas?.idGruposDeTexturas || 0,
                        searchID: acabado.texturas?.idTexturas || 0,
                        color: acabado.texturas?.color || '#FFFFFF',
                        repeatX: acabado.texturas?.factorRepeticionX || 1,
                        repeatY: acabado.texturas?.factorRepeticionY || 1,
                        opacity: acabado.texturas?.opacidad ?? 1,
                        roughness: acabado.texturas?.rugosidad ?? 0,
                        metalness: acabado.texturas?.metal ?? 0,
                        mirror: acabado.texturas?.espejo ?? false,
                        modelo: acabado.modelo,
                        acabado: acabado.acabado,
                        linkedTextureID: acabado.idAcabadoUnidad,
                      }))
                    : []

                  if (mappedTextures.length === 0) return

                  set((s) => {
                    const newItems = new Map(s.items)
                    const curr = newItems.get(linkedID as AnyID) as any
                    if (!curr || curr.type !== 'linkedSon') return s

                    curr.textures = mappedTextures as any

                    if (!curr.zones || Object.keys(curr.zones).length === 0) {
                      const t = mappedTextures[0]
                      const zoneKey = t.zoneName || t.zoneDisplayName || 'Tirador'
                      curr.zones = {
                        [zoneKey]: {
                          index: 0,
                          idAcabadoUnidadModeloAcabado: `${zoneKey}_${t.linkedTextureID ?? t.id}`,
                          material: {
                            id: t.id,
                            searchID: t.searchID,
                            name: t.name,
                            url: t.url,
                            description: t.description,
                            acabado: t.acabado,
                            groupID: t.groupID,
                            color: t.color || '#FFFFFF',
                            roughness: t.roughness ?? 1,
                            metalness: t.metalness ?? 0,
                            envMapIntensity: 0,
                            opacity: t.opacity ?? 1,
                            repeatX: t.repeatX ?? 1,
                            repeatY: t.repeatY ?? 1,
                            textureRotation: 0,
                            mirror: t.mirror ?? false,
                            displacement: 0,
                            displacementScale: 1,
                            bumpScale: 0,
                            bumpMap: null,
                          },
                        },
                      }
                    }

                    newItems.set(linkedID as AnyID, curr)
                    return { ...s, items: newItems }
                  })
                })
                .catch(() => {})

              const posicionesAlternativas = [
                'posicionX',
                'posicionY',
                'posicionZ',
                'biPosicionX',
                'biPosicionY',
                'biPosicionZ',
                'bcPosicionX',
                'bcPosicionY',
                'bcPosicionZ',
                'bdPosicionX',
                'bdPosicionY',
                'bdPosicionZ',
                'aiPosicionX',
                'aiPosicionY',
                'aiPosicionZ',
                'acPosicionX',
                'acPosicionY',
                'acPosicionZ',
                'adPosicionX',
                'adPosicionY',
                'adPosicionZ',
                'rotacionX',
                'rotacionY',
                'rotacionZ',
                'biRotacionX',
                'biRotacionY',
                'biRotacionZ',
                'bcRotacionX',
                'bcRotacionY',
                'bcRotacionZ',
                'bdRotacionX',
                'bdRotacionY',
                'bdRotacionZ',
                'aiRotacionX',
                'aiRotacionY',
                'aiRotacionZ',
                'acRotacionX',
                'acRotacionY',
                'acRotacionZ',
                'adRotacionX',
                'adRotacionY',
                'adRotacionZ',
                'b2idPosicionX',
                'b2idPosicionY',
                'b2idPosicionZ',
                'b2idRotacionX',
                'b2idRotacionY',
                'b2idRotacionZ',
                'a2idPosicionX',
                'a2idPosicionY',
                'a2idPosicionZ',
                'a2idRotacionX',
                'a2idRotacionY',
                'a2idRotacionZ',
              ]

              posicionesAlternativas.forEach((key) => {
                if (elementData[key] !== undefined && elementData[key] !== null) {
                  ;(linkedSon as any)[key] = elementData[key]
                }
              })

              if (isTirador && isLinkedItem(updatedLinkedItem as LinkedItem)) {
                const pos = calculateTiradorPosition(
                  linkedSon,
                  updatedLinkedItem as LinkedItem,
                )
                linkedSon.x = pos.x
                linkedSon.y = pos.y
                linkedSon.z = pos.z
                linkedSon.visible = resolveLinkedSonVisibility(elementData, prefix, pos, {
                  isGolaUnit,
                  isTirador,
                })
              }

              newItems.set(linkedID as AnyID, linkedSon)
              newChildren.push(linkedID)

              const orderKey = `${parentNode}-${childNode}-0!${linkedID}`
              newChildrenOrder.push(orderKey)
            }
          }

          const existingChildrenOrder = (updatedLinkedItem.childrenOrder || []).filter(
            (orderKey: string) => {
              const childIdFromOrder = orderKey.split('!')[1]
              const shouldKeep = !childrenToRemove.includes(childIdFromOrder)

              return shouldKeep
            },
          )

          const finalLinkedItem = {
            ...updatedLinkedItem,
            childs: new Set(newChildren),
            childrenOrder: [...existingChildrenOrder, ...newChildrenOrder] as any[],
          } as LinkedItem

          newItems.set(itemId as AnyID, finalLinkedItem)

          return {
            ...s,
            items: newItems,
            increment: s.increment + childrenToAdd.length,
          }
        })

        if (childrenToAdd.length > 0) {
          const info = await fetchLinkedChildrenInfo(linkedItem.idUnidades)

          await runWithConcurrency(
            childrenToAdd.map((addKey) => async () => {
              const elementData = newAvailableElements.get(addKey)
              if (!elementData?.unidad?.referenciaBlender) return
              await get().loadAndUpdateLinkedSonGLB(
                itemId,
                elementData,
                addKey,
                null,
                info,
                linkedItem,
                { useCatalogDefaultTextures: options?.useCatalogDefaultTextures === true },
              )
            }),
            4,
          )
        }
      }

      return true
    } catch (error) {
      console.error('❌ Error actualizando linkedSon:', error)
      return false
    }
  },

  loadAndUpdateLinkedSonGLB: async (
    parentId: string,
    elementData: any,
    addKey: string,
    loader: any,
    info: any,
    parentLinkedItem: LinkedItem,
    loadOptions?: { useCatalogDefaultTextures?: boolean },
  ) => {
    try {
      // const [childNodeStr, description] = addKey.split('-')

      const sonGlbData = await loadGlbCached(elementData.unidad.referenciaBlender)
      const backData = elementData.unidad
      const idElementosEnlazados = elementData.idElementosEnlazados

      let responseData = elementData
      try {
        const posicionesResp = await API().api.acabadosGetElementosEnlazadosUnidadAcabados2(
          idElementosEnlazados,
          idElementosEnlazados,
        )
        responseData = posicionesResp?.data || elementData
      } catch (error) {
        // console.warn('❌ Error obteniendo posiciones:', error)
      }

      let processedInfo: any = null

      try {
        const extraInfoResponse = await API().api.acabadosGetUnidadAcabadosInfo(backData.idUnidades)

        const extraInfo = {
          zonas: Array.isArray(extraInfoResponse.data?.zonas)
            ? extraInfoResponse.data.zonas.map((z: any) => ({
                idAcabadoUnidad: z.idAcabadoUnidad,
                zona: z.zona ?? '',
              }))
            : [],
        }

        const backGLBInfo: any = {
          ...backData,
          linkedID: backData.idUnidades,
          idUnidades: backData.idUnidades,
          codigo: backData.codigo ?? '',
          active: true,
        }

        const store = get()
        const parentItem = store.items.get(parentId)
        if (!parentItem || parentItem.type !== 'linkedItem') return

        const existingSonId = Array.from(parentItem.childs || []).find((childId) => {
          const child = store.items.get(childId as AnyID) as LinkedItemSon | undefined
          return child && child.type === 'linkedSon' && child.nodo === elementData.nodo
        })

        if (!existingSonId) return
        const existingSon = store.items.get(existingSonId as AnyID) as LinkedItemSon
        if (!existingSon) return

        processedInfo = newGetDataFromGLB({
          glbData: sonGlbData,
          backGLBInfo,
          id: existingSonId,
          type: 'linkedSon',
          catalogID: backData.codigo ?? '',
          extraInfo,
          itemInfo: {},
        })
      } catch (error) {
        return
      }

      let finalX = 0,
        finalY = 0,
        finalZ = 0

      let prefix = 'b'
      if (
        parentLinkedItem.tipoNormalizado === 'MAL' ||
        parentLinkedItem.tipoNormalizado === 'COL'
      ) {
        prefix = 'a'
      }

      const cx = responseData[`${prefix}cPosicionX`]
      const cy = responseData[`${prefix}cPosicionY`]
      const cz = responseData[`${prefix}cPosicionZ`]

      finalX = cx ?? responseData.posicionX ?? 0
      finalY = cy ?? responseData.posicionY ?? 0
      finalZ = cz ?? responseData.posicionZ ?? 0

      const store = get()
      const parentItem = store.items.get(parentId)
      if (!parentItem || parentItem.type !== 'linkedItem') return

      const existingSonId = Array.from(parentItem.childs || []).find((childId) => {
        const child = store.items.get(childId as AnyID) as LinkedItemSon | undefined
        return child && child.type === 'linkedSon' && child.nodo === elementData.nodo
      })

      if (!existingSonId) return
      const existingSon = store.items.get(existingSonId as AnyID) as LinkedItemSon
      if (!existingSon) return

      let finalTextures = processedInfo.textures || []
      let finalZones = processedInfo.zones || {}

      if (finalTextures.length > 0 && !loadOptions?.useCatalogDefaultTextures) {
        const hasVitrinaModel = finalTextures.some((texture: any) => texture.modelo === 'Vitrina')

        const shouldInheritTextures =
          !hasVitrinaModel && !existingSon.catalogID?.toString().toUpperCase().startsWith('KABV')

        if (shouldInheritTextures) {
          const existingTextures = getExistingTexturesFromScene(
            store.items as any,
            existingSon.catalogID,
            parentId,
          )

          if (existingTextures && existingTextures.length > 0) {
            finalTextures = mapExistingTexturesToNewItemByDisplayName(
              existingTextures,
              finalTextures as any,
              existingSon,
            )

            const syncResult = syncZonesWithMappedTextures(
              finalZones,
              finalTextures as any,
              store.items as any,
              'linkedSon',
              existingSon.description || 'Tirador',
            )
            finalZones = syncResult.zones
          }
        }
      }

      const isGolaUnit = isGolaEffective(parentLinkedItem)
      const isTirador = elementData.descripcion === 'Tirador'
      const shouldBeVisible = resolveLinkedSonVisibility(
        responseData,
        prefix,
        { x: finalX, y: finalY, z: finalZ },
        { isGolaUnit, isTirador },
      )

      const updatedSon = {
        ...existingSon,

        x: finalX,
        y: finalY,
        z: finalZ,

        textures: finalTextures,
        zones: finalZones,

        width: backData.ancho || existingSon.width,
        height: backData.alto || existingSon.height,
        thickness: backData.fondo || existingSon.thickness,
        long:
          backData.descripcionLarga || backData.descripcion || backData.codigo || existingSon.long,
        image: backData.referenciaJpgpng || existingSon.image,
        price: backData.precio || existingSon.price,

        catalogID: backData.codigo || existingSon.catalogID,
        code: backData.codigo || existingSon.code,
        glbPath: backData.referenciaBlender || existingSon.glbPath,
        referenciaBlender: backData.referenciaBlender || existingSon.referenciaBlender,

        idElementosEnlazados: idElementosEnlazados,
        nodo: elementData.nodo,
        handPosition: existingSon.handPosition || 'Centro',
        visible: shouldBeVisible,
      } as LinkedItemSon

      // Copiar coords API (bc/ac…) y recalcular vs Ancho/Fondo actuales del padre.
      const posicionKeys = [
        'posicionX',
        'posicionY',
        'posicionZ',
        'biPosicionX',
        'biPosicionY',
        'biPosicionZ',
        'bcPosicionX',
        'bcPosicionY',
        'bcPosicionZ',
        'bdPosicionX',
        'bdPosicionY',
        'bdPosicionZ',
        'aiPosicionX',
        'aiPosicionY',
        'aiPosicionZ',
        'acPosicionX',
        'acPosicionY',
        'acPosicionZ',
        'adPosicionX',
        'adPosicionY',
        'adPosicionZ',
      ] as const
      for (const key of posicionKeys) {
        if (responseData[key] !== undefined && responseData[key] !== null) {
          ;(updatedSon as any)[key] = responseData[key]
        }
      }
      if (isTirador) {
        const parentForPos = store.items.get(parentId)
        if (parentForPos && isLinkedItem(parentForPos)) {
          const pos = calculateTiradorPosition(updatedSon, parentForPos)
          updatedSon.x = pos.x
          updatedSon.y = pos.y
          updatedSon.z = pos.z
          updatedSon.visible = resolveLinkedSonVisibility(
            responseData,
            prefix,
            pos,
            { isGolaUnit, isTirador },
          )
        }
      }

      set((s) => {
        const newItems = new Map(s.items)
        newItems.set(existingSonId as AnyID, updatedSon)

        const newSceneGLB = new Map(s.sceneGLB)
        const catalogIDForGLB = updatedSon.catalogID
        newSceneGLB.set(catalogIDForGLB, {
          glb: sonGlbData,
          backGLBInfo: backData,
        })

        return {
          ...s,
          items: newItems,
          sceneGLB: newSceneGLB,
        }
      })
    } catch (error) {
      console.error('❌ Error en loadAndUpdateLinkedSonGLB:', error)
    }
  },
  batchUpdateLinkedItemsGLB: async (
    changes: Array<{ itemId: string; newCatalogId: string; previousCatalogId?: string }>,
    onProgress?: (current: number, total: number) => void,
  ) => {
    if (!changes.length) {
      return { succeeded: [] as string[], failed: [] as string[] }
    }

    logAcabado('batchUpdateLinkedItemsGLB_START', { count: changes.length })

    const succeeded: string[] = []
    const failed: string[] = []
    const sonsUpdates: Array<{
      itemId: string
      newCatalogId: string
      previousCatalogId: string
    }> = []

    const prefetched = await prefetchLinkedVariantCatalogs(
      changes.map((c) => c.newCatalogId),
      get().sceneGLB,
    )

    const idUnidadesSet = new Set<number>()
    for (const bundle of prefetched.values()) {
      const id = bundle.unidadData.idUnidades as number | undefined
      if (id) idUnidadesSet.add(id)
    }

    const linkedOptionsByIdUnidades = new Map<number, Record<string, unknown>>()
    await Promise.all(
      [...idUnidadesSet].map(async (idUnidades) => {
        try {
          const payload = await fetchLinkedUnitOptions(idUnidades)
          linkedOptionsByIdUnidades.set(idUnidades, payload)
        } catch (error) {
          logAcabado('batchUpdateLinkedItemsGLB_OPTIONS_FAIL', {
            idUnidades,
            error: String(error),
          })
        }
      }),
    )

    const stripSuffix = (catalogID: string) =>
      catalogID.replace(/ (GO|LA GO|RU GO|R2 GO| LI| LA| RU| R2)/, '')

    set((s) => {
      changes.forEach((change, index) => {
        onProgress?.(index + 1, changes.length)

        const codeKey = sceneGLBLookupKey(change.newCatalogId)
        const bundle = prefetched.get(codeKey)
        if (!bundle) {
          failed.push(change.itemId)
          return
        }

        const linkedItem = s.items.get(change.itemId)
        const previousCatalogId =
          change.previousCatalogId ?? String(linkedItem?.catalogID ?? '')

        const ok = applyLinkedItemGlbVariantInStore(
          s,
          change.itemId,
          change.newCatalogId,
          bundle,
          previousCatalogId,
          {
            linkedOptionsByIdUnidades,
            skipSonsUpdate: true,
            // LA/LI/Ru/R2: cambiar puerta/GLB/zonas sin resetear tamaño ni zócalo.
            preserveSceneLayout: true,
          },
        )

        if (!ok) {
          failed.push(change.itemId)
          return
        }

        succeeded.push(change.itemId)

        const normalizedNew = sceneGLBLookupKey(change.newCatalogId)
        if (
          previousCatalogId !== normalizedNew &&
          stripSuffix(previousCatalogId) !== stripSuffix(normalizedNew)
        ) {
          sonsUpdates.push({
            itemId: change.itemId,
            newCatalogId: change.newCatalogId,
            previousCatalogId,
          })
        }
      })

      s.shouldUpdateCamera = true
    })

    await runWithConcurrency(
      sonsUpdates.map((sons) => async () => {
        const linkedItem = get().items.get(sons.itemId)
        const idUnidades =
          linkedItem && isLinkedItem(linkedItem) ? linkedItem.idUnidades : undefined
        const linkedOptionsPayload =
          idUnidades != null ? linkedOptionsByIdUnidades.get(idUnidades) : undefined

        await get().updateLinkedSonsForNewGLB(
          sons.itemId,
          sons.newCatalogId,
          sons.previousCatalogId,
          { linkedOptionsPayload },
        )
      }),
      3,
    )

    const editItem = get().editItem
    await runWithConcurrency(
      succeeded.map((itemId) => async () => {
        const change = changes.find((c) => c.itemId === itemId)
        if (!change) return
        const previousCatalogId =
          change.previousCatalogId ?? String(get().items.get(itemId)?.catalogID ?? '')

        await finalizeLinkedItemGlbVariantSideEffects(
          get,
          (id, updates) => editItem(id as AnyID, updates),
          itemId,
          change.newCatalogId,
          previousCatalogId,
          { skipSonsUpdate: true },
        )
      }),
      8,
    )

    // Reposicionar hijos (zócalo/encimera/fregadero…) al tamaño preservado tras el swap de puerta.
    const updateLinkedSonPositions = get().updateLinkedSonPositions
    if (updateLinkedSonPositions) {
      for (const itemId of succeeded) {
        try {
          updateLinkedSonPositions(itemId, 'Ancho')
          updateLinkedSonPositions(itemId, 'Alto')
          updateLinkedSonPositions(itemId, 'Fondo')
        } catch (error) {
          logAcabado('batchUpdateLinkedItemsGLB_SYNC_SONS_FAIL', {
            itemId,
            error: String(error),
          })
        }
      }
    }

    logAcabado('batchUpdateLinkedItemsGLB_DONE', {
      succeeded: succeeded.length,
      failed: failed.length,
      failedIds: failed,
    })

    return { succeeded, failed }
  },

  updateLinkedItemGLB: async (itemId: string, newCatalogId: string) => {
    const storeBefore = get()
    const linkedItemBefore = storeBefore.items.get(itemId)
    logAcabado('updateLinkedItemGLB_START', {
      itemId,
      newCatalogId,
      fromCatalogID: linkedItemBefore?.catalogID,
      idUnidades: isLinkedItem(linkedItemBefore) ? linkedItemBefore.idUnidades : undefined,
    })
    if (linkedItemBefore && isLinkedItem(linkedItemBefore) && linkedItemBefore.childs) {
      logLinkedChildrenSnapshot(
        'updateLinkedItemGLB_before',
        itemId,
        (id) => storeBefore.items.get(id as AnyID),
        linkedItemBefore.childs,
      )
      logItemTexturesSnapshot(
        'updateLinkedItemGLB_before',
        itemId,
        linkedItemBefore.textures as Array<Record<string, unknown>>,
        String(linkedItemBefore.catalogID || ''),
      )
    }

    const previousCatalogId = String(linkedItemBefore?.catalogID ?? '')

    const batchActive = isLinkedVisualBatchActive()
    let originalFrameloop: Store['frameloop'] = 'demand'
    if (!batchActive) {
      set((store) => {
        originalFrameloop = store.frameloop
        store.frameloop = 'always'
      })
    }

    if (!linkedItemBefore || linkedItemBefore.type !== 'linkedItem') {
      logAcabado('updateLinkedItemGLB_ABORT', { itemId, reason: 'invalid_linkedItem' })
      if (!batchActive) {
        set((s) => {
          s.frameloop = originalFrameloop
        })
      }
      return false
    }

    try {
      const { succeeded } = await get().batchUpdateLinkedItemsGLB([
        { itemId, newCatalogId, previousCatalogId },
      ])

      const success = succeeded.includes(itemId)

      const storeAfter = get()
      const finalLinkedItem = storeAfter.items.get(itemId)
      if (finalLinkedItem && isLinkedItem(finalLinkedItem)) {
        logItemTexturesSnapshot(
          'updateLinkedItemGLB_after',
          itemId,
          finalLinkedItem.textures as Array<Record<string, unknown>>,
          String(finalLinkedItem.catalogID || ''),
        )
        if (finalLinkedItem.childs) {
          logLinkedChildrenSnapshot(
            'updateLinkedItemGLB_after',
            itemId,
            (id) => storeAfter.items.get(id as AnyID),
            finalLinkedItem.childs,
          )
        }
      }

      if (success) {
        logAcabado('updateLinkedItemGLB_SUCCESS', { itemId, newCatalogId })
      } else {
        logAcabado('updateLinkedItemGLB_FAIL', { itemId, reason: 'batch_failed' })
      }

      return success
    } catch (error) {
      console.error('Error al actualizar GLB:', error)
      logAcabado('updateLinkedItemGLB_ERROR', { itemId, newCatalogId, error: String(error) })
      return false
    } finally {
      if (!batchActive) {
        setTimeout(() => {
          set((s) => {
            s.frameloop = originalFrameloop
          })
        }, 100)
      }
    }
  },
  updateEncimeraHeight: async (encimeraID: string, newHeight?: number) => {
    set((state) => {
      const encimeraItem = state.items.get(encimeraID as AnyID)

      if (!encimeraItem || !isEncimeraLinkedSonItem(encimeraItem)) {
        return
      }

      const parentItem = encimeraItem.firstParentID
        ? state.items.get(encimeraItem.firstParentID as AnyID)
        : null
      const parentUsesFreeDim =
        parentItem?.type === 'linkedItem' && usesFreeDimensionScaling(parentItem)

      if (newHeight !== undefined) {
        encimeraItem.height = newHeight
        encimeraItem.morphs?.forEach((morph) => {
          if (morph.name !== 'Alto') return
          const native =
            morph.min != null && morph.min > 0 && morph.min <= 50 ? morph.min : 10
          morph.default = native
          morph.value = native
        })
      } else {
        applyEncimeraThicknessMm(encimeraItem)
      }

      const ajuste = resolveEncimeraAcabadoAjusteYMm(encimeraItem as never)

      if (parentItem && parentItem.type === 'linkedItem') {
        if (parentUsesFreeDim) {
          placeLinkedEncimeraY(encimeraItem, parentItem)
        }

        state.items.forEach((item) => {
          if (item.type !== 'linkedSon') return
          if (!isCountertopApplianceLinkedSon(item as any)) return
          const pid = (item as any).firstParentID
          if (pid !== encimeraItem.firstParentID && pid !== encimeraItem.id) return

          ;(item as any).encimeraAcabadoAjusteY = ajuste

          if (parentUsesFreeDim && linkedSonKeepsApiLocalTransform(item)) {
            const encimeraAabb = getCatalogGlbAabbMm(
              state.sceneGLB as never,
              encimeraItem.catalogID,
              [
                sceneGLBLookupKey(String(encimeraItem.catalogID ?? '')),
                (encimeraItem as { idUnidades?: number }).idUnidades,
              ],
            )
            applyLinkedApplianceLocalTransformFromApi(
              item as any,
              parentItem as any,
              encimeraItem as never,
              { encimeraAabb },
            )
            return
          }
          if (
            (item as any).posicionY != null &&
            ((item as any).baseY === undefined || (item as any).baseY === null)
          ) {
            ;(item as any).baseY = (item as any).posicionY
          }
          const baseY = Number((item as any).baseY ?? (item as any).posicionY ?? 0)
          if (Number.isFinite(baseY) && baseY > 0) {
            item.y = baseY + ajuste
          }
        })
      }

      state.shouldUpdateCamera = true
    })

    scheduleAppliancesAccordingToEncimera(get, encimeraID)
  },

  // Función vacía - las placas solo se actualizan cuando se cambia la encimera manualmente
  adjustPlacaPositionAfterEncimeraCreation: (encimeraID: string) => {
    // No hacer nada - las placas se posicionan manualmente al inicio al menos en principio mientras todas sean compactas
  },

  positionFregaderoGrifoPlacaAccordingToEncimera: (childID: string) => {
    set((state) => {
      const childItem = state.items.get(childID as AnyID)

      if (!childItem || childItem.type !== 'linkedSon') {
        return
      }

      if (childItem.firstParentID) {
        const parentItem = state.items.get(childItem.firstParentID as AnyID)

        if (parentItem && parentItem.type === 'linkedItem') {
          let encimeraItem = null
          state.items.forEach((item, key) => {
            if (
              item.type === 'linkedSon' &&
              (item.description?.toLowerCase().includes('encimera') ||
                (item as any).typeOfSon === 'Encimera') &&
              (item as any).firstParentID === childItem.firstParentID
            ) {
              encimeraItem = item
            }
          })

          if (encimeraItem) {
            const encimeraTextures = encimeraItem.textures || []
            const encimeraTexture = encimeraTextures[0]
            const acabado = encimeraTexture?.acabado?.toLowerCase() || ''
            const ajuste = resolveEncimeraAcabadoAjusteYMm(encimeraItem as never)

            // baseY / posicionY = Y catálogo API (nunca la Y local ya transformada).
            // Tras un swap, el catálogo del padre sigue teniendo el fregadero antiguo:
            // seal solo pisa si hay match por ID o si el hijo no tiene coords API propias.
            const parentCatalogKey = String(parentItem.catalogID ?? '')
            const elementos =
              state.sceneGLB.get(parentItem.catalogID)?.backGLBInfo?.elementosEnlazados ??
              state.sceneGLB.get(parentCatalogKey)?.backGLBInfo?.elementosEnlazados ??
              state.sceneGLB.get(sceneGLBLookupKey(parentCatalogKey))?.backGLBInfo
                ?.elementosEnlazados
            sealLinkedApplianceApiFloorCoordsFromParentCatalog(
              childItem as any,
              parentItem as any,
              elementos,
            )
            ;(childItem as any).encimeraAcabadoAjusteY = ajuste

            const encimeraAabb = getCatalogGlbAabbMm(
              state.sceneGLB as never,
              (encimeraItem as { catalogID?: string | number }).catalogID,
              [
                sceneGLBLookupKey(
                  String((encimeraItem as { catalogID?: string | number }).catalogID ?? ''),
                ),
                (encimeraItem as { idUnidades?: number }).idUnidades,
              ],
            )

            // Free-dim: reescalar al alto actual del padre y sumar ajuste acabado.
            // Legacy: y = baseY catálogo + ajuste.
            if (
              usesFreeDimensionScaling(parentItem) &&
              linkedSonKeepsApiLocalTransform(childItem)
            ) {
              applyLinkedApplianceLocalTransformFromApi(
                childItem,
                parentItem,
                encimeraItem as never,
                { encimeraAabb },
              )
            } else {
              const baseY = Number(
                (childItem as any).posicionY ?? (childItem as any).baseY ?? childItem.y ?? 0,
              )
              childItem.y = baseY + ajuste
            }

            state.items.set(childID as AnyID, childItem)
          } else {
            // console.warn('⚠️ No se encontró encimera para posicionar el elemento:', childID)
          }
        }
      }

      state.shouldUpdateCamera = true
    })
  },

  updateZocaloWithCompleteTexture: async (
    itemId: AnyID,
    complementoName: string,
    linkedTextureID: number,
    idUnidades?: number,
  ) => {
    try {
      const api = API()
      let resolvedLinkedId = Number(linkedTextureID) > 0 ? Number(linkedTextureID) : 0

      if (!resolvedLinkedId && idUnidades) {
        try {
          const concha = await api.api.acabadosGetUnidadAcabadosInfo(idUnidades)
          const zonas = (concha?.data as { zonas?: { idAcabadoUnidad?: number; zona?: string }[] })
            ?.zonas
          const zocaloZona =
            zonas?.find((z) => /z[oó]calo/i.test(String(z.zona ?? ''))) ?? zonas?.[0]
          resolvedLinkedId = Number(zocaloZona?.idAcabadoUnidad ?? 0)
        } catch (e) {
          // console.warn('[updateZocaloWithCompleteTexture] fallback idUnidades fail', e)
        }
      }

      if (!resolvedLinkedId) {
        // console.warn('[updateZocaloWithCompleteTexture] sin linkedTextureID', {
          // itemId,
          // complementoName,
          // idUnidades,
        // })
        return
      }

      const info = await api.api.acabadosGetAcabadoZonaModeloAcabadoInfo(resolvedLinkedId)
      const data = (info as any)?.data ?? info
      const acabados = Array.isArray(data)
        ? data.find((z: any) => (z?.zona || '').toLowerCase().includes('zocalo'))?.acabados ||
          data[0]?.acabados ||
          []
        : []

      const idAcabado =
        acabados[0]?.idAcabadoUnidadModeloAcabado || acabados[0]?.idAcabadoUnidad

      if (!idAcabado) {
        // console.warn('[updateZocaloWithCompleteTexture] sin idAcabado', {
          // itemId,
          // resolvedLinkedId,
        // })
        return
      }

      const texturasResp = await api.api.acabadosGetTexturas(idAcabado)
      const texturas = (texturasResp as any)?.data ?? texturasResp
      const list = Array.isArray(texturas) ? texturas : []

      const cmp = complementoName.toLowerCase().trim()
      const matchingTexture = list.find((t: any) => {
        const n = (t?.nombre || '').toLowerCase()
        const d = (t?.descripcion || '').toLowerCase()
        return n === cmp || d === cmp || n.includes(cmp) || d.includes(cmp)
      })

      if (!matchingTexture) {
        // console.warn('[updateZocaloWithCompleteTexture] complemento no encontrado en catálogo', {
          // itemId,
          // complementoName,
          // sample: list.slice(0, 8).map((t: any) => t?.nombre || t?.descripcion),
        // })
        return
      }

      const matchedZocaloMaterial = getZocaloMaterialFieldsFromOption(
        matchingTexture,
        complementoName,
      )

      set((s) => {
        const refreshedItem = s.items.get(itemId)
        if (!refreshedItem || !isLinked(refreshedItem)) return

        const zocaloTexture =
          refreshedItem.textures.find(
            (el) =>
              el.zoneDisplayName === 'Zocalo' ||
              String(el.zone ?? '')
                .toLowerCase()
                .includes('zocalo') ||
              String(el.zoneName ?? '')
                .toLowerCase()
                .includes('zocalo'),
          ) || refreshedItem.textures[0]

        if (!zocaloTexture) return

        applyMaterialToTextureEntry(
          zocaloTexture as Record<string, unknown>,
          matchedZocaloMaterial as Record<string, unknown>,
        )

        const targetZoneKey = resolveZoneKeyForTarget(
          refreshedItem.zones as Record<
            string,
            { name?: string; material?: { zoneDisplayName?: string; zone?: string } }
          >,
          zocaloTexture,
        )
        if (targetZoneKey && refreshedItem.zones[targetZoneKey]?.material) {
          const existing = refreshedItem.zones[targetZoneKey].material as Record<string, unknown>
          refreshedItem.zones[targetZoneKey].material = preserveZoneBrightness(
            existing,
            applyMaterialToZoneMaterial(existing, matchedZocaloMaterial as Record<string, unknown>),
          ) as Material
        } else {
          const zoneKeys = Object.keys(refreshedItem.zones || {})
          if (zoneKeys[0] && refreshedItem.zones[zoneKeys[0]]?.material) {
            const existing = refreshedItem.zones[zoneKeys[0]].material as Record<string, unknown>
            refreshedItem.zones[zoneKeys[0]].material = preserveZoneBrightness(
              existing,
              applyMaterialToZoneMaterial(
                existing,
                matchedZocaloMaterial as Record<string, unknown>,
              ),
            ) as Material
          }
        }

        const parentId = (refreshedItem as LinkedItemSon).firstParentID
        if (parentId && list.length > 0) {
          const parent = s.items.get(parentId as AnyID)
          if (parent && isLinkedItem(parent) && !parent.zocaloOptions?.length) {
            parent.zocaloOptions = list as ItemWithOptions[]
          }
        }

        s.shouldUpdateCamera = true
        s.contadorModificaciones = (s.contadorModificaciones || 0) + 1
        ;(s as any).editorNeedsUpdate = true
        ;(s as any).lastEditorUpdate = Date.now()
      })
    } catch (e) {
      // console.warn('[updateZocaloWithCompleteTexture] Error:', e)
    }
  },
})
